import React, { useState, useEffect, useMemo } from 'react';
import { formatWithMethodDetectionLimitFrontend, getMethodDetectionLimit, icpStoredPpmToDisplayValue, shouldUseTracePercentColumn } from '../../config/methodDetectionLimits';
import {
  buildReportFormEntry,
  getIcpConversionPair,
  icpStoredPpmToElementPercent,
  isReportFormConvertible,
  resolveReportFormColumn,
  shouldIncludeSupervisorEditInCert,
  SupervisorReportFormEntry,
  SupervisorReportFormMap,
} from '../../config/oxideConversion';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
  Alert,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  useTheme,
  alpha,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  TableContainer,
  Radio,
  RadioGroup,
  FormControlLabel as MuiFormControlLabel,
  Snackbar,
  Checkbox,
  TablePagination,
  InputAdornment,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Science,
  Analytics,
  CheckCircle,
  Assignment,
  Timer,
  PriorityHigh,
  TrendingUp,
  Assessment,
  FlashOn,
  Settings,
  Refresh,
  Save,
  PlayArrow,
  Stop,
  Warning,
  Info,
  ExpandMore,
  Tune,
  VerifiedUser,
  Group,
  Speed,
  Error,
  Cancel,
  Dashboard,
  People,
  ExpandLess,
  ExpandMore as ExpandMoreIcon,
  Visibility,
  Search,
  Clear,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import samplesService, { Sample, Priority, SampleStatus } from '../../services/samplesService';
import api from '../../services/api';

interface SampleWithAnalyst extends Sample {
  analyst_name?: string;
  isBatchValidation?: boolean;
  batchSamples?: SampleWithAnalyst[];
  current_laboratory?: string;
}

interface ValidationBatch {
  batch_id: string;
  analyst_name: string;
  sample_count: number;
  elements_analyzed: string[];
  analysis_date: string;
  avg_rsd: number;
  qc_status: 'PASSED' | 'WARNING' | 'FAILED';
  validation_priority: Priority;
  samples: Sample[];
}

interface MethodValidation {
  element: string;
  lod_ppm: number;
  loq_ppm: number;
  linear_range_min_ppm: number;
  linear_range_max_ppm: number;
  accuracy_percent: number;
  precision_rsd_percent: number;
  recovery_min_percent: number;
  recovery_max_percent: number;
  expanded_uncertainty_percent: number;
  validation_date: string;
}

interface AnalysisReading {
  element: string;
  intensity: number;
  concentration_ppm: number;
  concentration_final_percent: number;
  uncertainty: number;
  timestamp: string;
  instrument_settings: {
    wavelength: number;
    rf_power: number;
    nebulizer_flow: number;
    integration_time: number;
  };
}

/** PRE.FO/ICP/078 — Excel « Fichier de traitement TA ICP » : PAF = ((CaO%/56)+(MgO%/40))*44 (CO₂ lié aux carbonates). */
const ICP_PAF_CAO_MOLAR = 56;
const ICP_PAF_MGO_MOLAR = 40;
const ICP_PAF_CO2_MOLAR = 44;

const ICP_TREATMENT_MAJOR_ELEMENTS = new Set([
  // Raw element symbols (older imports / CSV files)
  'Si', 'Al', 'Fe', 'Ca', 'Mg', 'Na', 'K', 'Ti', 'Mn', 'P',
  // Oxide names (newer imports where DB migration stored oxide form in readings)
  'SiO2', 'Al2O3', 'Fe2O3', 'CaO', 'MgO', 'Na2O', 'K2O', 'TiO2', 'MnO', 'P2O5',
]);

/** Reverse map: oxide name → raw element symbol, for functions that need the raw form. */
const ICP_OXIDE_TO_RAW: Record<string, string> = {
  SiO2: 'Si', Al2O3: 'Al', Fe2O3: 'Fe', CaO: 'Ca',
  MgO: 'Mg', Na2O: 'Na', K2O: 'K', TiO2: 'Ti', MnO: 'Mn', P2O5: 'P',
};

/**
 * Forward map: raw element symbol → oxide name. Mirrors the analyst dashboard's
 * `rawToOxide` (apps/frontend/src/components/dashboards/ICPAnalystDashboard.tsx)
 * so that the supervisor's fusion-import path normalizes incoming raw symbols
 * ("Ca", "Mg", "Fe", …) to the same oxide keys ("CaO", "MgO", "Fe2O3", …)
 * the analyst's multi-file import already produces. Without this, fusion
 * values land under raw keys while metadonnees readings (and PAF helpers)
 * look them up under oxide keys, leading to `editedValues["CaO"] = 0`
 * shadowing `editedValues["Ca"] = 256800` via `??` and a PAF of 0.
 *
 * Major-oxide elements ONLY. BGM analytes (Au, Ag, Cu, Pb, Sn, Zn, Mo, As)
 * intentionally absent — BGM uses raw element symbols throughout and must
 * never be renamed.
 */
const ICP_RAW_TO_OXIDE: Record<string, string> = {
  Al: 'Al2O3', Ca: 'CaO', Fe: 'Fe2O3', K: 'K2O',
  Mg: 'MgO', Mn: 'MnO', Na: 'Na2O', P: 'P2O5', Si: 'SiO2', Ti: 'TiO2',
};

/**
 * 🆕 Role-based QA/QC (non-BGM / non-ZMSM): hardcoded but editable CRM values for
 * OREAS 30a. Only the six certified majors below are pre-filled; every other column
 * stays blank and remains editable by the supervisor. Keys are the oxide names used
 * throughout the non-BGM ICP pipeline (selected_elements + readings are oxide-keyed).
 */
// ── QA/QC Certified reference values (PRE.MO/ANA/037 – D4A ICP, non-BGM/non-ZMSM) ──────────
// Source: QAQC.xlsx (Downloads, 2026-06-25)

const OREAS_30A_VALUES: Record<string, string> = {
  // Majors (%)
  Al2O3: '15.45', CaO: '7.86',  Fe2O3: '11.15', K2O:  '2.10',  MgO:  '7.13', MnO:   '0.13',
  Na2O:  '3.32',  P2O5: '0.58', S:     '0.04',  TiO2: '1.95',
  // Traces (ppm)
  Ag: '<1',  As: '<8',    Ba:  '570.53', Be:  '1.54',  Bi:    '2.16',  Cd:   '<1',
  Co: '47.2', Cr: '156.94', Cu: '42.16', Ga:  '22.09', Ge:    '<10',   In:   '<5',
  Li: '10.83', Mo: '2.97', Nb: '46.17', Ni:  '146.4',  Pb:    '2.97',  Sb:   '<1',
  Se: '<2',   Sn: '2.17', Sr: '782.63', Ta:  '2',      Th:    '<5',    Tl:   '<1',
  V:  '199.94', W: '<2',  Y:  '18.81',  Zn:  '101.97', Zr:    '203.24',
};

const OREAS_20A_VALUES: Record<string, string> = {
  // Majors (%)
  Al2O3: '15.21', CaO: '3.86',  Fe2O3: '5.18',  K2O:  '4.06',  MgO:  '2.36', MnO:   '0.06',
  Na2O:  '2.71',  P2O5: '0.23', S:     '0.06',  TiO2: '0.84',
  // Traces (ppm)
  Ag: '<1',   As: '18.03', Ba:  '1017.99', Be: '2.99',  Bi:    '<10',   Cd:   '<1',
  Co: '12.07', Cr: '66.93', Cu: '41.31',  Ga:  '18.83', Ge:    '<10',   In:   '<5',
  Li: '37.65', Mo: '2',    Nb: '22.84',   Ni:  '37.17', Pb:    '20.96', Sb:   '<1',
  Se: '<2',   Sn: '3.5',  Sr: '274.4',   Ta:  '<1',    Th:    '23.74', Tl:   '1',
  V:  '113.28', W: '3.1', Y:  '23.76',   Zn:  '68.08', Zr:    '82.7',
};

const BLANK_MP_VALUES: Record<string, string> = {
  // Majors (%)
  Al2O3: '0.18', CaO: '0.4',  Fe2O3: '1.02', K2O:  '0.02', MgO:  '0.1', MnO:  '0.01',
  Na2O:  '0.67', P2O5: '0.01', S:    '0.01',  TiO2: '0.01',
  // Traces (ppm)
  Ag: '<1', As: '<8',   Ba:  '52.1',  Be: '<1',  Bi:  '<10', Cd:  '<1',
  Co: '<5', Cr: '<5',   Cu:  '2.41',  Ga: '<5',  Ge:  '<10', In:  '<5',
  Li: '<2', Mo: '<2',   Nb:  '<1',    Ni: '<2',  Pb:  '4.55', Sb: '<1',
  Se: '<2', Sn: '<2',   Sr:  '64.54', Ta: '<1',  Th:  '<5',  Tl:  '<1',
  V:  '<1', W:  '<2',   Y:   '<1',    Zn: '4.1', Zr:  '<1',
};

const BLANK_CP_VALUES: Record<string, string> = {
  // Majors (%)
  Al2O3: '<0.01', CaO: '<0.01', Fe2O3: '<0.01', K2O:  '<0.01', MgO: '<0.01', MnO:  '<0.01',
  Na2O:  '<0.01', P2O5: '<0.01', S:    '<0.01',  TiO2: '<0.01',
  // Traces (ppm)
  Ag: '<1', As: '<8',  Ba:  '<2', Be: '<1',  Bi:  '<10', Cd:  '<1',
  Co: '<5', Cr: '<5',  Cu:  '<2', Ga: '<5',  Ge:  '<10', In:  '<5',
  Li: '<2', Mo: '<2',  Nb:  '<1', Ni: '<2',  Pb:  '<2',  Sb:  '<1',
  Se: '<2', Sn: '<2',  Sr:  '<2', Ta: '<1',  Th:  '<5',  Tl:  '<1',
  V:  '<1', W:  '<2',  Y:   '<1', Zn: '<2',  Zr:  '<1',
};

/** Display label for each major element in oxide form (matrix view header). */
const ICP_ELEMENT_OXIDE_LABEL: Record<string, string> = {
  Si: 'SiO₂', Al: 'Al₂O₃', Fe: 'Fe₂O₃', Ca: 'CaO',
  Mg: 'MgO', Na: 'Na₂O', K: 'K₂O', Ti: 'TiO₂',
  Mn: 'MnO', P: 'P₂O₅', S: 'S', Cr: 'Cr₂O₃',
};

/**
 * Convert the stored ppm value of a major element to the oxide percentage that
 * the user expects to see in the matrix view.
 *
 * Mirrors the logic of `icpMajorOxidePercentFromReading` and the existing sums:
 * the parser already stores major elements as "oxide-percent × 10000" in the
 * `concentration_ppm` field for non-legacy data. Legacy data is already in %.
 */
function icpMajorPpmToOxidePercent(value: number | null | undefined): number {
  const v = Number(value);
  if (!isFinite(v) || v === 0) return 0;
  return Math.abs(v) >= 100 ? v / 10000 : v;
}

function icpDisplayMajorAsOxidePercent(rawPpm: number, isLegacyData: boolean): number {
  const v = Number(rawPpm) || 0;
  return isLegacyData ? v : v / 10000;
}

function icpPafCarbonateEstimatePercent(caO_pct: number, mgO_pct: number): number {
  const ca = Math.max(0, Number(caO_pct) || 0);
  const mg = Math.max(0, Number(mgO_pct) || 0);
  return ((ca / ICP_PAF_CAO_MOLAR) + (mg / ICP_PAF_MGO_MOLAR)) * ICP_PAF_CO2_MOLAR;
}

function icpGetMetadonneesReadings(sample: any): any[] {
  const sources = [
    sample?.metadonnees?.icp_analysis_form?.analysis_readings,
    sample?.metadonnees?.icp_analysis_data?.analysis_readings,
    sample?.metadonnees?.analysis_results,
  ];
  for (const src of sources) {
    if (Array.isArray(src) && src.length > 0) return src;
  }
  return [];
}

function icpDetectLegacyMajorSmallPpm(readings: any[]): boolean {
  // Matches both raw ('Ca') and oxide ('CaO') element names thanks to the extended set.
  const majorReadings = readings.filter((r: any) => ICP_TREATMENT_MAJOR_ELEMENTS.has(r.element));
  if (majorReadings.length === 0) return false;
  const avg =
    majorReadings.reduce((s: number, r: any) => s + Math.abs(Number(r.concentration_ppm) || 0), 0) /
    majorReadings.length;
  return avg < 100;
}

function icpMajorOxidePercentFromReading(
  reading: any,
  sampleId: string,
  editedValues: Record<string, Record<string, number> | undefined>,
  isLegacyData: boolean,
): number {
  const sampleEdits = editedValues[sampleId];
  // Check both the stored key ('CaO') and the raw alias ('Ca') so edits applied
  // under either naming convention are picked up correctly.
  const rawAlias = ICP_OXIDE_TO_RAW[reading.element];
  const edited =
    sampleEdits?.[reading.element] ??
    (rawAlias !== undefined ? sampleEdits?.[rawAlias] : undefined);
  const raw = Number(edited ?? reading.concentration_ppm ?? 0) || 0;
  return isLegacyData ? raw : raw / 10000;
}

function icpCaMgOxidePercent(
  readings: any[],
  sampleId: string,
  editedValues: Record<string, Record<string, number> | undefined>,
): { caO_pct: number; mgO_pct: number } {
  const isLegacy = icpDetectLegacyMajorSmallPpm(readings);
  let caO_pct = 0;
  let mgO_pct = 0;
  for (const r of readings) {
    // Accept both raw symbol ('Ca'/'Mg') and oxide form ('CaO'/'MgO')
    if (r.element === 'Ca' || r.element === 'CaO')
      caO_pct = icpMajorOxidePercentFromReading(r, sampleId, editedValues, isLegacy);
    if (r.element === 'Mg' || r.element === 'MgO')
      mgO_pct = icpMajorOxidePercentFromReading(r, sampleId, editedValues, isLegacy);
  }
  return { caO_pct, mgO_pct };
}

/**
 * Computes majors / traces / total sums for a single sample's readings.
 * `overrideValues` allows swapping individual element ppm values (e.g. fusion overlay).
 * Elements present in overrides but absent from `readings` are included (e.g. Si from fusion).
 */
function icpComputeSampleSums(
  readings: any[],
  overrideValues: Record<string, number> | undefined,
  isLegacyData: boolean,
): { sommeMajeurs: number; sommeTraces: number; sommeTotale: number } {
  let sommeMajeurs = 0;
  let sommeTraces = 0;
  const seen = new Set<string>();

  for (const r of readings) {
    if (!r.element) continue;
    seen.add(r.element);
    // Look up override by stored name ('CaO') first, then by raw alias ('Ca') for cross-form compatibility
    const rawAlias = ICP_OXIDE_TO_RAW[r.element];
    const overrideVal =
      overrideValues?.[r.element] ??
      (rawAlias !== undefined ? overrideValues?.[rawAlias] : undefined);
    const rawPpm = Number(overrideVal ?? r.concentration_ppm ?? 0) || 0;

    if (ICP_TREATMENT_MAJOR_ELEMENTS.has(r.element)) {
      sommeMajeurs += isLegacyData ? rawPpm : rawPpm / 10000;
    } else if (r.element === 'S') {
      const sPct = rawPpm >= 100 ? rawPpm / 10000 : isLegacyData ? rawPpm : rawPpm / 10000;
      sommeMajeurs += sPct;
    } else {
      sommeTraces += rawPpm / 10000;
    }
  }

  if (overrideValues) {
    for (const [el, val] of Object.entries(overrideValues)) {
      if (el.startsWith('__') || seen.has(el)) continue;
      const rawPpm = Number(val) || 0;
      if (ICP_TREATMENT_MAJOR_ELEMENTS.has(el)) {
        sommeMajeurs += isLegacyData ? rawPpm : rawPpm / 10000;
      } else if (el === 'S') {
        const sPct = rawPpm >= 100 ? rawPpm / 10000 : isLegacyData ? rawPpm : rawPpm / 10000;
        sommeMajeurs += sPct;
      } else {
        sommeTraces += rawPpm / 10000;
      }
    }
  }

  return { sommeMajeurs, sommeTraces, sommeTotale: sommeMajeurs + sommeTraces };
}

interface ICPMethodConfig {
  method_id: string;
  method_name: string;
  elements: string[];
  detection_limits: Record<string, number>;
  calibration_standards: string[];
  qc_requirements: {
    max_rsd: number;
    required_blanks: number;
    required_duplicates: number;
    spike_recovery_range: [number, number];
  };
  instrument_conditions: Record<string, string>;
  is_active: boolean;
}

interface ICPPerformanceMetrics {
  total_samples_processed: number;
  average_turnaround_time: number;
  quality_pass_rate: number;
  instrument_uptime: number;
  analyst_efficiency: Record<string, number>;
  method_usage: Record<string, number>;
}

// 🔧 BGM ELEMENT ORDER: Standard order for BGM multi-lab samples (ICP elements first, Au at end)
// Au is placed at the END because it comes from AU lab (for comparison purposes)
const BGM_ELEMENT_ORDER = ['Ag', 'As', 'Cu', 'Fe', 'Mo', 'Pb', 'Sn', 'Zn', 'Au'];

// 🔧 Helper to sort elements in BGM order (for consistent display)
// Au is always placed at the END since it's from a different lab
const sortElementsInBGMOrder = (elements: string[]): string[] => {
  return elements.sort((a, b) => {
    // Au always goes to the END
    if (a === 'Au') return 1;
    if (b === 'Au') return -1;
    
    const aIndex = BGM_ELEMENT_ORDER.indexOf(a);
    const bIndex = BGM_ELEMENT_ORDER.indexOf(b);
    // If both are in BGM order, use that order
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    // If only one is in BGM order, prioritize it
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    // Otherwise, alphabetical
    return a.localeCompare(b);
  });
};

/** Resolve per-element ICP method for detection limits (mirrors certificates.service). */
const resolveElementMethodCode = (sample: any, element: string): string => {
  const useBGMLimits =
    sample?.metadonnees?.bgm_method_selected === true ||
    sample?.client?.company_name === 'BGM' ||
    sample?.client?.company_name === 'ZMSM';
  if (useBGMLimits) return 'BGM-MULTI-LAB';

  const mapping = sample?.metadonnees?.element_method_mapping || {};
  if (mapping[element]) return mapping[element];

  const methods: string[] = sample?.metadonnees?.analyses_demandees || [];
  const icpMethod = methods.find((m: string) =>
    /ICP|D4A|FP|BGM|ANA\/007|ANA\/038|ANA\/037|ANA\/039|ANA\/040|ANA\/041/i.test(m || ''),
  );
  if (icpMethod) return icpMethod;

  return 'D4A-ICP- PRE.MO/ANA/038';
};

const _ANA38_OX = ['Al2O3','CaO','Fe2O3','K2O','MgO','MnO','Na2O','P2O5','S','SiO2','TiO2'];
/** Map raw element symbols to their oxide slot in _ANA38_OX (supervisor element-report columns stay with oxides). */
const _ANA38_ELEMENT_TO_OX_SLOT: Record<string, string> = {
  Al: 'Al2O3', Ca: 'CaO', Fe: 'Fe2O3', K: 'K2O', Mg: 'MgO', Mn: 'MnO',
  Na: 'Na2O', P: 'P2O5', Si: 'SiO2', Ti: 'TiO2', SO3: 'S',
};
const _ana38SortIndex = (symbol: string): number => {
  const slot = _ANA38_OX.includes(symbol) ? symbol : (_ANA38_ELEMENT_TO_OX_SLOT[symbol] || symbol);
  const idx = _ANA38_OX.indexOf(slot);
  return idx;
};
const _sortANA38Order = (elements: string[]): string[] => {
  return elements.slice().sort((a, b) => {
    if (a === 'Au') return 1;
    if (b === 'Au') return -1;
    const aIdx = _ana38SortIndex(a);
    const bIdx = _ana38SortIndex(b);
    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
    if (aIdx !== -1) return -1;
    if (bIdx !== -1) return 1;
    return a.localeCompare(b);
  });
};

// 🔧 FIX: Get ALL unique elements across an entire batch (union of all samples)
// This ensures table headers show all possible elements even if samples have different selections
// 🆕 includeAuForBGM: For validation modal, include AU for BGM samples to compare with ICP values
const getAllBatchElements = (batchSamples: Sample[], includeAuForBGM: boolean = false): string[] => {
  const allElements = new Set<string>();
  const isBGMBatch = batchSamples.some(sample => 
    sample.metadonnees?.bgm_method_selected === true || 
    (sample as any).client?.company_name === 'BGM' ||
    (sample as any).client?.company_name === 'ZMSM'
  );
  
  batchSamples.forEach(sample => {
    const sampleElements = getSampleElements(sample, includeAuForBGM);
    sampleElements.forEach(el => allElements.add(el));
  });
  
  // 🔧 FIX: For BGM batches, use BGM element order; otherwise use standard sorting
  if (isBGMBatch) {
    return sortElementsInBGMOrder(Array.from(allElements));
  }
  
  return _sortANA38Order(Array.from(allElements));
};

// Helper function to get selected elements for a sample
// 🔥 ENHANCED: For multi-lab samples, only show elements routed to ICP lab
// 🔧 FIX: Must filter by BOTH selected_elements AND element_lab_routing
// 🆕 includeAuForBGM: For validation modal, include AU for BGM samples to compare with AU lab values
const getSampleElements = (sample: Sample, includeAuForBGM: boolean = false): string[] => {
  // 🔧 CRITICAL FIX: Start with selected_elements, then filter by routing
  // This ensures elements removed per-sample are properly greyed out
  const selectedElements = sample.metadonnees?.selected_elements || [];
  const elementRouting = sample.metadonnees?.element_lab_routing || {};
  const isMultiLab = sample.metadonnees?.multi_lab_workflow === true || 
                     sample.metadonnees?.bgm_method_selected === true ||
                     Object.keys(elementRouting).length > 0;
  const isBGM = sample.metadonnees?.bgm_method_selected === true || 
                (sample as any).client?.company_name === 'BGM' ||
                (sample as any).client?.company_name === 'ZMSM';
  
  if (isMultiLab && selectedElements.length > 0 && Object.keys(elementRouting).length > 0) {
    // Filter selected_elements to only those routed to ICP lab (or no routing = default to ICP)
    let icpElements = selectedElements.filter((element: string) => {
      const routing = elementRouting[element];
      return !routing || routing === 'LABORATOIRE_ICP';
    });
    
    // 🆕 BGM FIX: For validation modal, include AU for comparison even though it's routed to AU lab
    if (includeAuForBGM && isBGM && selectedElements.includes('Au') && !icpElements.includes('Au')) {
      icpElements = [...icpElements, 'Au'];
      console.log(`🔍 ICP Supervisor: Including AU for BGM comparison in ${sample.sample_id}`);
    }
    
    if (icpElements.length > 0) {
      const sortedElements = isBGM ? sortElementsInBGMOrder(icpElements) : _sortANA38Order(icpElements);
      console.log(`🔍 ICP Supervisor: Elements for ${sample.sample_id} (${isBGM ? 'BGM' : 'ANA38'} order):`, sortedElements);
      return sortedElements;
    }
  }
  
  // 2. Direct selected_elements array (for non-multi-lab samples)
  if (selectedElements.length > 0) {
    return isBGM ? sortElementsInBGMOrder(selectedElements) : _sortANA38Order(selectedElements);
  }
  
  // 3. From analyses_demandees (analysis methods requested)
  if (sample.metadonnees?.analyses_demandees && Array.isArray(sample.metadonnees.analyses_demandees)) {
    // Map ICP methods to their complete element lists
    const methodToElements: {[key: string]: string[]} = {
      'D4A-ICP- PRE.MO/ANA/038': [
        'Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti', 
        'Ag', 'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu', 'Ga', 'Ge', 
        'In', 'Li', 'Mo', 'Nb', 'Ni', 'Pb', 'Rb', 'Sb', 'Sc', 'Sn', 'Sr', 
        'Ta', 'Te', 'Th', 'U', 'V', 'W', 'Y', 'Zr'
      ], // 55 Elements ICP
      'D4A-ICP- PRE.MO/ANA/037': [
        'Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti', 
        'Ag', 'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu', 'Ni', 'Pb', 'Zn'
      ], // 40 Elements ICP
      'FP-ICP PRE.MO/ANA/007': [
        'Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti', 
        'Ag', 'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu'
      ], // 32 Elements ICP Fusion
      'ICP_MS': ['Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti', 'Ag', 'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu', 'Ni', 'Pb', 'Zn'],
      'ICP_OES': ['Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti', 'Ag', 'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu', 'Ni', 'Pb', 'Zn'],
      'ICP': ['Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti', 'Ag', 'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu', 'Ni', 'Pb', 'Zn'],
      'MULTI_ELEMENT': ['Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti', 'Ag', 'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu', 'Ni', 'Pb', 'Zn'],
      'BGM-MULTI-LAB': ['Ag', 'As', 'Au', 'Cu', 'Fe', 'Mo', 'Pb', 'Sn', 'Zn'], // 🔧 FIX: Include Au for BGM (shown in validation modal)
    };
    
    const elements = new Set<string>();
    sample.metadonnees.analyses_demandees.forEach(method => {
      const methodElements = methodToElements[method] || ['Fe', 'Cu', 'Pb', 'Zn', 'Mn', 'Ni'];
      methodElements.forEach(el => elements.add(el));
    });
    
    if (elements.size > 0) {
      const elementArray = Array.from(elements);
      return isBGM ? sortElementsInBGMOrder(elementArray) : _sortANA38Order(elementArray);
    }
  }
  
  // 4. Default ICP elements if nothing else found (use common elements)
  return ['Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti', 'Ag', 'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu', 'Ni', 'Pb', 'Zn'];
};

const ICPSupervisorDashboard: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const [currentTab, setCurrentTab] = useState(0);
  const [samples, setSamples] = useState<SampleWithAnalyst[]>([]);
  const [loading, setLoading] = useState(true);
  const [validationDialog, setValidationDialog] = useState(false);
  const [selectedSample, setSelectedSample] = useState<SampleWithAnalyst | null>(null);
  const [validationDecision, setValidationDecision] = useState<'APPROVE' | 'REJECT' | 'REQUEST_CORRECTIONS'>('APPROVE');
  const [validationNotes, setValidationNotes] = useState('');
  const [certificateNotes, setCertificateNotes] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  const [methodValidations, setMethodValidations] = useState<MethodValidation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [performanceMetrics, setPerformanceMetrics] = useState({
    total_samples_processed: 0,
    average_turnaround_time: 0,
    quality_pass_rate: 98.5,
    instrument_uptime: 95,
    analyst_efficiency: {} as Record<string, number>,
    method_usage: {} as Record<string, number>,
  });
  const [validationBatches, setValidationBatches] = useState<ValidationBatch[]>([]);
  const [methodConfigDialog, setMethodConfigDialog] = useState(false);

  // 🚀 PAGINATION & SEARCH STATE
  const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalSamples, setTotalSamples] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [batchCount, setBatchCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // QA/QC Data State (for BGM samples only)
  interface QAQCStandard {
    customers_ref: string;  // Row label (e.g., "BMX25120250")
    lab_ref: string;        // Editable (e.g., "R-9348-25-25")
    // 10 element columns - ALL EDITABLE (even if gray in template)
    Ag: string;
    Cu: string;
    Fe: string;
    Pb: string;
    Zn: string;
    Ag_FA: string;
    Au_FA: string;
    Mo_ICP: string;
    As_ICP: string;
    Sn_ICP: string;
  }
  const [qaqcStandards, setQaqcStandards] = useState<QAQCStandard[]>([]);
  const [showQaqcForm, setShowQaqcForm] = useState(false);

  const [dynamicQaqcStandards, setDynamicQaqcStandards] = useState<Record<string, string>[]>([]);
  const [dynamicQaqcElements, setDynamicQaqcElements] = useState<string[]>([]);
  const [showDynamicQaqcForm, setShowDynamicQaqcForm] = useState(false);

  // 🆕 Role-based QA/QC (non-BGM / non-ZMSM only). Reuses dynamicQaqcStandards /
  // dynamicQaqcElements / showDynamicQaqcForm (so the existing save + cert path is
  // unchanged) but adds: a fixed 5-row layout (1er échantillon, son BIS, BLANK MP,
  // BLANK CP, OREAS 30a), per-role sample pickers that auto-fill values from the
  // main results table (honouring supervisor edits), a Lab_Weight Kg column, and a
  // saved per-element meta map (unit / method / detection limit) so the certificate
  // can render the full header exactly like the QA/QC sheet.
  const [isRoleBasedQaqc, setIsRoleBasedQaqc] = useState(false);
  const [qcRoleFirstId, setQcRoleFirstId] = useState<string | null>(null);
  const [qcRoleBisId, setQcRoleBisId] = useState<string | null>(null);
  const [qcRoleBlankMPId, setQcRoleBlankMPId] = useState<string | null>(null);
  const [qcRoleBlankCPId, setQcRoleBlankCPId] = useState<string | null>(null);
  const [roleQaqcElementMeta, setRoleQaqcElementMeta] = useState<Record<string, { unit: string; method: string; limit: string }>>({});

  // Lot-level certificate element selection (non-BGM / non-ZMSM only)
  const [lotInitialElements, setLotInitialElements] = useState<string[]>([]);
  const [lotFinalElements, setLotFinalElements] = useState<string[]>([]);
  const [lotElementQaqcStale, setLotElementQaqcStale] = useState(false);

  // Lot-level oxide vs element reporting (non-BGM / non-ZMSM only)
  const [lotReportForm, setLotReportForm] = useState<SupervisorReportFormMap>({});
  const [reportFormDialogOpen, setReportFormDialogOpen] = useState(false);
  const [reportFormDialogSymbol, setReportFormDialogSymbol] = useState<string | null>(null);
  const [reportFormDialogMode, setReportFormDialogMode] = useState<'oxide' | 'element'>('oxide');

  // BIS Sample Selection State (2 BIS samples only)
  const [firstBISSampleId, setFirstBISSampleId] = useState<string | null>(null);
  const [secondBISSampleId, setSecondBISSampleId] = useState<string | null>(null);

  // 🆕 SUPERVISOR EDIT STATE: Track edited values for audit trail
  interface EditedValue {
    sampleId: string;
    element: string;
    originalValue: number;
    editedValue: number;
    timestamp: string;
  }
  const [editedValues, setEditedValues] = useState<Record<string, Record<string, number>>>({});
  const [originalValues, setOriginalValues] = useState<Record<string, Record<string, number>>>({});
  const [editingCell, setEditingCell] = useState<string | null>(null);
  // Staged string value while a cell is being typed — avoids per-keystroke ppm conversion
  const [editingCellDraft, setEditingCellDraft] = useState<string>('');
  const [editHistory, setEditHistory] = useState<EditedValue[]>([]);

  // Supervisor ICP file import (fusion overlay or confirmation re-run)
  const [supervisorImportOpen, setSupervisorImportOpen] = useState(false);
  const [supervisorImportMode, setSupervisorImportMode] = useState<'fusion' | 'confirmation'>('fusion');
  const [supervisorImportFiles, setSupervisorImportFiles] = useState<Array<{ name: string; analysisType?: string; matchCount?: number; matchedSampleIds?: string[] }>>([]);
  const [supervisorImportSelectedFile, setSupervisorImportSelectedFile] = useState<string>('');
  const [supervisorImportLoading, setSupervisorImportLoading] = useState(false);
  const [supervisorImportPreview, setSupervisorImportPreview] = useState<any[] | null>(null);
  // Track fusion-imported readings separately so treatment can show 4-acides vs fusion sums
  const [fusionImportedReadings, setFusionImportedReadings] = useState<Record<string, Record<string, number>>>({});

  // 🆕 Per-element value source picker (supervisor chooses 4-acides | fusion | manual per element)
  // valueSourcePref[sampleId][element] = explicit choice ('acid' | 'fusion' | 'manual')
  // manualOverrides[sampleId][element] = the typed manual value (preserved across source toggles)
  // expandedSourcePicker = set of sampleIds whose per-element source picker is expanded
  type ValueSource = 'acid' | 'fusion' | 'manual';
  const [valueSourcePref, setValueSourcePref] = useState<Record<string, Record<string, ValueSource>>>({});
  const [manualOverrides, setManualOverrides] = useState<Record<string, Record<string, number>>>({});
  const [expandedSourcePicker, setExpandedSourcePicker] = useState<Set<string>>(new Set());

  // 🆕 PER-SAMPLE source choice (the high-level "shortcut" — applies to all elements at once):
  //   'acid'   = use 4-Acides values only (ignore fusion overlay)
  //   'fusion' = use 4-Acides + fusion overlay (fusion wins where both exist) — DEFAULT when fusion is present
  //   'mixed'  = use per-element picker (opens the drill-down table)
  // If a sample has no fusion data, it's locked to 'acid' regardless of pref.
  type SampleSource = 'acid' | 'fusion' | 'mixed';
  const [sampleSourcePref, setSampleSourcePref] = useState<Record<string, SampleSource>>({});

  // 🆕 LOT-LEVEL SOURCE RULE: a default source + a list of per-element exceptions
  // that the supervisor can apply to every sample in the open lot at once.
  // This is the "easier way" requested when the user wants e.g. "all 4-Acides
  // EXCEPT take SiO₂ from Fusion". Stored per-batch (key = first sample id of
  // the open batch) so reopening the same lot remembers the rule.
  type LotSourceRule = {
    defaultSource: 'acid' | 'fusion';
    // exceptions[element] = source to use INSTEAD of defaultSource for that element
    exceptions: Record<string, 'acid' | 'fusion'>;
  };
  const [lotSourceRule, setLotSourceRule] = useState<LotSourceRule>({
    defaultSource: 'acid',
    exceptions: {},
  });
  const [lotRuleApplied, setLotRuleApplied] = useState(false);

  // 🆕 COMPARATIVE VIEW MODE: switch between the per-sample sums table (current)
  // and the per-element matrix table (new — see image attached by user).
  //   'sommes'  = legacy per-sample summary (Σ Maj / Σ Tr / Σ Tot for 4A and Fusion)
  //   'matrice' = per-element wide table (Customers-Ref · Lab-Ref · Al₂O₃(4A|F) · CaO(4A|F) · …)
  type ICPViewMode = 'sommes' | 'matrice';
  const [icpComparativeView, setIcpComparativeView] = useState<ICPViewMode>('matrice');
  // Matrix view filters which element class is shown (default: majors only —
  // because the 4A↔Fusion comparison is most useful on majors).
  type MatrixFilter = 'majeurs' | 'traces' | 'tous';
  const [matrixFilter, setMatrixFilter] = useState<MatrixFilter>('majeurs');
  // Matrix inline editing: key = `${sampleId}__${element}__${'acid'|'fusion'}`
  const [matrixEditingCell, setMatrixEditingCell] = useState<string | null>(null);
  const [matrixEditDraft, setMatrixEditDraft] = useState<string>('');
  // PAF staged drafts — only committed on blur/Enter to avoid per-keystroke re-renders
  const [pafDrafts, setPafDrafts] = useState<Record<string, string>>({});
  const [generatingPartialCert, setGeneratingPartialCert] = useState(false);

  const [stats, setStats] = useState({
    pending_validation: 0,
    validated_today: 0,
    rejected_today: 0,
    total_in_lab: 0,
    instrument_uptime: 95,
    quality_pass_rate: 98.5,
    overdue_validation: 0,
    quality_issues: 0,
  });

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(() => {
      if (!validationDialog) {
        loadDashboardData();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [validationDialog]); // Re-create interval when modal state changes

  // Debounce search input (800ms delay - gives time to finish typing)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0); // Reset to first page on search
    }, 800);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load data when page, rowsPerPage, or search changes
  useEffect(() => {
    loadValidationSamples();
  }, [page, rowsPerPage, debouncedSearch]);

  // Real-time updates for validation list
  useEffect(() => {
    const validationInterval = setInterval(() => {
      if (currentTab === 2 && !validationDialog) {
        loadValidationSamples();
      }
    }, 45000);

    return () => clearInterval(validationInterval);
  }, [currentTab, validationDialog]); // Re-create interval when modal state changes

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadValidationSamples(),
        loadMethodValidations(),
        loadStats()
      ]);
    } catch (error) {
      // Silenced in production to avoid noisy logs
    } finally {
      setLoading(false);
    }
  };

  const loadValidationSamples = async () => {
    try {
      // 🚀 OPTIMIZED: Use new paginated API endpoint with BGM filtering on backend
      const response = await api.get<{
        samples: SampleWithAnalyst[];
        total: number;
        page: number;
        totalPages: number;
        batchCount: number;
      }>('/samples/queues/icp-validation', {
        params: {
          page: page + 1, // API is 1-indexed, MUI is 0-indexed
          limit: rowsPerPage,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
          sortBy: 'date',
          sortOrder: 'desc',
        }
      });
      
      const icpSamples = (response.samples || []) as SampleWithAnalyst[];
      
      setSamples(icpSamples);
      setTotalSamples(response.total || 0);
      setTotalPages(response.totalPages || 0);
      setBatchCount(response.batchCount || 0);
      
    } catch (error) {
      // Silenced in production
      setSamples([]);
    }
  };

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const loadMethodValidations = async () => {
    try {
      // ICP method validation data with real detection limits
      const validations: MethodValidation[] = [
        {
          element: 'Fe',
          lod_ppm: 0.010,
          loq_ppm: 0.030,
          linear_range_min_ppm: 0.05,
          linear_range_max_ppm: 1000,
          accuracy_percent: 98.2,
          precision_rsd_percent: 2.1,
          recovery_min_percent: 90,
          recovery_max_percent: 110,
          expanded_uncertainty_percent: 4.0,
          validation_date: new Date().toISOString()
        },
        {
          element: 'Cu',
          lod_ppm: 0.003,
          loq_ppm: 0.010,
          linear_range_min_ppm: 0.02,
          linear_range_max_ppm: 500,
          accuracy_percent: 99.1,
          precision_rsd_percent: 1.8,
          recovery_min_percent: 90,
          recovery_max_percent: 110,
          expanded_uncertainty_percent: 3.5,
          validation_date: new Date().toISOString()
        },
        {
          element: 'Pb',
          lod_ppm: 0.005,
          loq_ppm: 0.015,
          linear_range_min_ppm: 0.03,
          linear_range_max_ppm: 200,
          accuracy_percent: 97.8,
          precision_rsd_percent: 2.5,
          recovery_min_percent: 90,
          recovery_max_percent: 110,
          expanded_uncertainty_percent: 4.2,
          validation_date: new Date().toISOString()
        },
        {
          element: 'Zn',
          lod_ppm: 0.002,
          loq_ppm: 0.006,
          linear_range_min_ppm: 0.01,
          linear_range_max_ppm: 100,
          accuracy_percent: 98.7,
          precision_rsd_percent: 1.9,
          recovery_min_percent: 90,
          recovery_max_percent: 110,
          expanded_uncertainty_percent: 3.8,
          validation_date: new Date().toISOString()
        }
      ];
      setMethodValidations(validations);
    } catch (error) {
      // Silenced in production
      setMethodValidations([]);
    }
  };

  const loadStats = async () => {
    try {
      // Calculate stats from samples
      const pendingValidation = samples.filter(s => s.statut === 'VALIDATION_SERVICE').length;
      const validatedToday = samples.filter(s => {
        const completedAt = s.metadonnees?.validation_completed_at;
        return completedAt && new Date(completedAt as string).toDateString() === new Date().toDateString();
      }).length;
      
      setStats({
        pending_validation: pendingValidation,
        validated_today: validatedToday,
        rejected_today: 0,
        total_in_lab: samples.length,
        instrument_uptime: 95,
        quality_pass_rate: 98.5,
        overdue_validation: 0,
        quality_issues: 0,
      });
    } catch (error) {
      // Silenced in production
    }
  };

  const groupSamplesIntoBatches = (samples: Sample[]): ValidationBatch[] => {
    const batchMap = new Map<string, Sample[]>();
    
    samples.forEach(sample => {
      // Check both individual analysis data and batch analysis form
      const analysisData = sample.metadonnees?.icp_analysis_data || sample.metadonnees?.icp_analysis_form;
      const analystName = sample.metadonnees?.icp_analyst_name || analysisData?.analyst_name || 'Unknown';
      const completedAt = sample.metadonnees?.icp_analysis_completed_at || analysisData?.analysis_completed_at || sample.date_reception;
      
      if (analysisData || sample.metadonnees?.batch_analysis_summary) {
        const batchKey = `${analystName}-${new Date(completedAt).toDateString()}`;
        
        if (!batchMap.has(batchKey)) {
          batchMap.set(batchKey, []);
        }
        batchMap.get(batchKey)?.push(sample);
      }
    });
    
    return Array.from(batchMap.entries()).map(([key, batchSamples]) => {
      const [analystName, dateStr] = key.split('-');
      
      // 🔧 FIX: Use same data source priority as certificate service for consistency
      // Priority: analysis_results -> icp_analysis_form -> icp_analysis_data
      const allResults = batchSamples.flatMap(s => {
        const icpAnalysisSources = [
          s.metadonnees?.analysis_results,
          s.metadonnees?.icp_analysis_form?.analysis_readings,
          s.metadonnees?.icp_analysis_data?.analysis_readings
        ];
        
        for (const source of icpAnalysisSources) {
          if (source && Array.isArray(source) && source.length > 0) {
            return source;
          }
        }
        return [];
      });
      
      const elements = [...new Set(allResults.map((r: any) => r.element))];
      const avgRsd = allResults.length > 0 ? 
        allResults.reduce((sum: number, r: any) => sum + (r.uncertainty || r.rsd_percent || 0), 0) / allResults.length : 0;
      
      // Determine QC status based on available data
      const qcStatus: 'PASSED' | 'WARNING' | 'FAILED' = 
        allResults.every((r: any) => (r.quality_flag === 'PASS' || r.detection_status === 'VALID')) ? 'PASSED' :
        allResults.some((r: any) => (r.quality_flag === 'FAIL' || r.detection_status === 'BELOW_LOD')) ? 'FAILED' : 'WARNING';
      
      const priority = batchSamples.some(s => s.priorite === Priority.URGENTE) ? Priority.URGENTE :
                     batchSamples.some(s => s.priorite === Priority.ELEVEE) ? Priority.ELEVEE : Priority.NORMALE;
      
      return {
        batch_id: `ICP-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        analyst_name: analystName || 'Analyst Inconnu',
        sample_count: batchSamples.length,
        elements_analyzed: elements,
        analysis_date: dateStr,
        avg_rsd: avgRsd,
        qc_status: qcStatus,
        validation_priority: priority,
        samples: batchSamples,
      };
    }).sort((a, b) => {
      // Sort by priority (urgent first) then by date
      if (a.validation_priority !== b.validation_priority) {
        return a.validation_priority === Priority.URGENTE ? -1 : 1;
      }
      return new Date(b.analysis_date).getTime() - new Date(a.analysis_date).getTime();
    });
  };

  const calculatePerformanceMetrics = (samples: Sample[]) => {
    const totalProcessed = samples.length;
    
    // Calculate average turnaround time
    const turnaroundTimes = samples.map(sample => {
      const startTime = new Date(sample.date_reception).getTime();
      const analysisCompletedAt = sample.metadonnees?.icp_analysis_data?.analysis_completed_at;
      const endTime = analysisCompletedAt && typeof analysisCompletedAt === 'string' ? 
        new Date(analysisCompletedAt).getTime() : Date.now();
      return (endTime - startTime) / (1000 * 60 * 60); // hours
    });
    
    const avgTurnaround = turnaroundTimes.reduce((sum, time) => sum + time, 0) / turnaroundTimes.length;
    
    // Calculate quality pass rate
    const qualityResults = samples.flatMap(s => s.metadonnees?.icp_analysis_data?.analysis_results || []);
    const passRate = qualityResults.length > 0 ? 
      (qualityResults.filter(r => r.quality_flag === 'PASS').length / qualityResults.length) * 100 : 0;
    
    // Analyst efficiency
    const analystEfficiency: Record<string, number> = {};
    samples.forEach(sample => {
      const analyst = sample.metadonnees?.icp_analysis_data?.analyst_name;
      if (analyst) {
        analystEfficiency[analyst] = (analystEfficiency[analyst] || 0) + 1;
      }
    });
    
    setPerformanceMetrics({
      total_samples_processed: totalProcessed,
      average_turnaround_time: avgTurnaround,
      quality_pass_rate: passRate,
      instrument_uptime: 95, // Mock data - would come from instrument
      analyst_efficiency: analystEfficiency,
      method_usage: { 'ICP-55': 65, 'ICP-31': 35 }, // Mock data
    });
  };

  const handleValidateSample = (sample: SampleWithAnalyst) => {
    setSelectedSample(sample);
    setValidationDialog(true);
    setValidationDecision('APPROVE');
    setValidationNotes('');
    // Prevent QA/QC state leaking from a previous batch into this new one (cross-contamination fix)
    setQaqcStandards([]);
    setShowQaqcForm(false);
    setDynamicQaqcStandards([]);
    setDynamicQaqcElements([]);
    setShowDynamicQaqcForm(false);
  };

  // Load default QA/QC template - 6 rows, 12 columns (Customers-Ref, Lab-Ref, + 10 elements)
  const loadDefaultQAQCTemplate = () => {
    const defaultStandards: QAQCStandard[] = [
      {
        customers_ref: "BMX25120250",
        lab_ref: "",
        Ag: "",      // MO/ANA/002 | 1
        Cu: "",      // MO/ANA/002 | 0.01
        Fe: "",      // MO/ANA/002 | 0.01
        Pb: "",      // MO/ANA/002 | 0.01
        Zn: "",      // MO/ANA/002 | 0.01
        Ag_FA: "",   // MO/ANA/035 | 200 (grayed in template but MUST be editable)
        Au_FA: "",   // MO/ANA/001 | 0.05
        Mo_ICP: "",  // MO/ANA/038 | 1
        As_ICP: "",  // MO/ANA/038 | 1
        Sn_ICP: "",  // MO/ANA/038 | 1
      },
      {
        customers_ref: "BIS(BMX25120250)",
        lab_ref: "",
        Ag: "",
        Cu: "",
        Fe: "",
        Pb: "",
        Zn: "",
        Ag_FA: "",   // Grayed but EDITABLE
        Au_FA: "",
        Mo_ICP: "<1",
        As_ICP: "<1",
        Sn_ICP: "<1",
      },
      {
        customers_ref: "BLANK (mechanical preparation)",
        lab_ref: "",
        Ag: "<1",    // Below detection limit (LOD = 1 g/t)
        Cu: "<0.01", // Below detection limit (LOD = 0.01 %)
        Fe: "<0.01", // Below detection limit (LOD = 0.01 %)
        Pb: "<0.01", // Below detection limit (LOD = 0.01 %)
        Zn: "<0.01", // Below detection limit (LOD = 0.01 %)
        Ag_FA: "",
        Au_FA: "<0.05",  // Below detection limit (LOD = 0.05 g/t)
        Mo_ICP: "<1",
        As_ICP: "<1",
        Sn_ICP: "<1",
      },
      {
        customers_ref: "BLANK (chemical preparation)",
        lab_ref: "",
        Ag: "<1",    // Below detection limit (LOD = 1 g/t)
        Cu: "<0.01", // Below detection limit (LOD = 0.01 %)
        Fe: "<0.01", // Below detection limit (LOD = 0.01 %)
        Pb: "<0.01", // Below detection limit (LOD = 0.01 %)
        Zn: "<0.01", // Below detection limit (LOD = 0.01 %)
        Ag_FA: "",
        Au_FA: "<0.05",  // Below detection limit (LOD = 0.05 g/t)
        Mo_ICP: "",
        As_ICP: "",
        Sn_ICP: "",
      },
      {
        customers_ref: "OREAS 316",
        lab_ref: "",
        Ag: "102",   // Certified: 102 g/t
        Cu: "0.17",  // Certified: 0.17 %
        Fe: "5.86",  // Certified: 5.86 %
        Pb: "",
        Zn: "",
        Ag_FA: "",
        Au_FA: "",
        Mo_ICP: "18",    // Certified: 18 ppm
        As_ICP: "365",   // Certified: 365 ppm
        Sn_ICP: "",
      },
      {
        customers_ref: "OREAS 933",
        lab_ref: "",
        Ag: "",
        Cu: "",
        Fe: "",
        Pb: "0.02",  // Certified: 0.02 %
        Zn: "0.06",  // Certified: 0.06 %
        Ag_FA: "",
        Au_FA: "",
        Mo_ICP: "",
        As_ICP: "",
        Sn_ICP: "65",    // Certified: 65 ppm
      },
      {
        customers_ref: "OREAS 608b",
        lab_ref: "",
        Ag: "",
        Cu: "",
        Fe: "",
        Pb: "",
        Zn: "",
        Ag_FA: "",
        Au_FA: "1.29",   // Certified: 1.29 g/t
        Mo_ICP: "",
        As_ICP: "",
        Sn_ICP: "",
      },
    ];
    
    setQaqcStandards(defaultStandards);
    setShowQaqcForm(true);
  };

  const ELEMENT_UNITS: Record<string, string> = {
    'Ag': 'g/t', 'Au': 'g/t', 'Au_FA': 'g/t', 'Ag_FA': 'g/t',
    'Cu': '%', 'Fe': '%', 'Pb': '%', 'Zn': '%',
    'Mo_ICP': 'ppm', 'As_ICP': 'ppm', 'Sn_ICP': 'ppm',
    'Cd': 'ppm', 'Co': 'ppm', 'Mn': 'ppm', 'Ni': 'ppm', 'H2O': '%',
  };

  const ELEMENT_DETECTION_LIMITS: Record<string, string> = {
    'Ag': '1', 'Au': '0,05', 'Au_FA': '0,05', 'Ag_FA': '200',
    'Cu': '0,01', 'Fe': '0,01', 'Pb': '0,01', 'Zn': '0,01',
    'Mo_ICP': '1', 'As_ICP': '1', 'Sn_ICP': '1',
    'Cd': '1', 'Co': '1', 'Mn': '1', 'Ni': '1', 'H2O': '0,1',
  };

  const loadDynamicQAQCTemplate = () => {
    const batchSamples = (selectedSample as any)?.batchSamples || [];
    const elemSet = new Set<string>();
    batchSamples.forEach((s: any) => {
      const sel = s.metadonnees?.selected_elements || [];
      sel.forEach((el: string) => elemSet.add(el));
      // 🔧 FIX (ZMSM+Au): Some non-BGM lots (e.g. ZMSM with Au added post-reception) store Au
      // only in element_lab_routing, not in selected_elements. Merge routing keys so every
      // customer-requested element (including Au) appears as a QC column. BGM is unaffected:
      // BGM lots are routed through loadDefaultQAQCTemplate (hardcoded 10-column header with
      // Au_FA) and never reach this function. For normal 5-element ZMSM, routing keys are the
      // same 5 ICP elements so the union is identical → no behavior change.
      const routing = s.metadonnees?.element_lab_routing || {};
      Object.keys(routing).forEach((el: string) => elemSet.add(el));
      const customRouting = s.metadonnees?.custom_lab_routing || {};
      Object.keys(customRouting).forEach((el: string) => elemSet.add(el));
    });
    const elements = Array.from(elemSet);
    if (elements.length === 0) {
      setSnackbarMessage('❌ Aucun élément trouvé pour ce lot');
      setSnackbarOpen(true);
      return;
    }
    setDynamicQaqcElements(elements);
    const makeRow = (label: string): Record<string, string> => {
      const row: Record<string, string> = { customers_ref: label, lab_ref: '' };
      elements.forEach(el => { row[el] = ''; });
      return row;
    };
    setDynamicQaqcStandards([
      makeRow('Standard 1'),
      makeRow('Standard 2'),
      makeRow('BLANK'),
    ]);
    setShowDynamicQaqcForm(true);
  };

  // 🆕 ============================================================================
  // 🆕 ROLE-BASED QA/QC (non-BGM / non-ZMSM clients)
  // 🆕 ----------------------------------------------------------------------------
  // 🆕 Builds the QA/QC sheet shown in the certificate: five rows — the first
  // 🆕 sample, its BIS (duplicate), a blank MP, a blank CP, and an editable
  // 🆕 hardcoded OREAS 30a CRM row. Values auto-fill from the main results table and
  // 🆕 honour any supervisor edit (editedValues), exactly like the displayed cell.
  // 🆕 ============================================================================

  /** Display string for (sample, element), mirroring the main matrix `fmtCellValue`. */
  const roleQcDisplayValue = (sample: any, el: string): string => {
    const readings = icpGetMetadonneesReadings(sample);
    const acidPpm = getAcidPpm(sample, el);
    const overridePpm = editedValues[sample.id]?.[el];
    const resolvedPpm = overridePpm !== undefined ? overridePpm : acidPpm;
    if (resolvedPpm === undefined || resolvedPpm === null || isNaN(resolvedPpm)) return '';
    const isMaj = ICP_TREATMENT_MAJOR_ELEMENTS.has(el) || el === 'S';
    if (isMaj) {
      const isLegacy = icpDetectLegacyMajorSmallPpm(readings);
      const oxidePct = icpDisplayMajorAsOxidePercent(resolvedPpm, isLegacy);
      if (oxidePct < 0.01) return '< 0.01';
      return oxidePct.toFixed(2);
    }
    // Traces stay in ppm; surface below-detection as "< limit" like the cert.
    const lim = getMethodDetectionLimit(resolveElementMethodCode(sample, el), el);
    const limVal = lim ? Number(lim.limit) : 0;
    if (resolvedPpm <= 0 || (limVal > 0 && resolvedPpm < limVal)) {
      return limVal > 0 ? `< ${lim?.limit}` : '< 1';
    }
    return resolvedPpm < 100 ? resolvedPpm.toFixed(2) : resolvedPpm.toFixed(1);
  };

  /** Lab weight (kg) for a sample, mirroring the certificate's poids_kg resolution. */
  const roleQcLabWeight = (sample: any): string => {
    const md = sample?.metadonnees || {};
    const grams = md?.scale_weights?.preparation_mecanique?.weight_grams;
    const raw =
      md?.poids_kg ??
      (grams ? Number(grams) / 1000 : undefined);
    const n = parseFloat(raw as any);
    if (!isFinite(n) || n <= 0) return '';
    return n.toFixed(3);
  };

  /** Build the unit/method/detection-limit meta for each QA/QC column. */
  const buildRoleElementMeta = (elements: string[], referenceSample?: any): Record<string, { unit: string; method: string; limit: string }> => {
    const meta: Record<string, { unit: string; method: string; limit: string }> = {};
    elements.forEach(el => {
      const isMaj = ICP_TREATMENT_MAJOR_ELEMENTS.has(el) || el === 'S';
      const isAu = el === 'Au' || el === 'Au_FA';
      const isAg = el === 'Ag' || el === 'Ag_FA';
      const unit = isMaj ? '%' : (isAu || isAg) ? 'g/t' : 'ppm';
      const method = isAu
        ? 'PRE.MO/ANA/001'
        : referenceSample
          ? resolveElementMethodCode(referenceSample, el)
          : (isMaj ? 'PRE.MO/ANA/037' : 'PRE.MO/ANA/038');
      const lim = getMethodDetectionLimit(method, el);
      const limit = lim ? `${lim.limit}` : (isMaj ? '0.01' : '1');
      meta[el] = { unit, method, limit };
    });
    return meta;
  };

  /** Load the role-based QA/QC template (5 rows, OREAS pre-filled, weight column). */
  const loadRoleBasedQAQCTemplate = () => {
    const batchSamples = (selectedSample as any)?.batchSamples || [];
    // 🔧 FIX 2026-06-19: QA/QC columns must mirror EXACTLY the elements shown in the
    // results matrix at the top (getAllBatchElements -> getSampleElements). The previous
    // union with element_lab_routing / custom_lab_routing pulled in stale/extra keys
    // (e.g. Au — routed to the AU lab, not ICP — or elements deselected after reception
    // such as SiO2/B) that never appear at the top. Mirroring the top list keeps QA/QC
    // consistent and drops those non-ICP / deselected columns. Scoped to the role-based
    // (non-BGM / non-ZMSM) template only.
    const elements = lotFinalElements.length > 0
      ? _sortANA38Order([...lotFinalElements])
      : getAllBatchElements(batchSamples, false);
    if (elements.length === 0) {
      setSnackbarMessage('❌ Aucun élément trouvé pour ce lot');
      setSnackbarOpen(true);
      return;
    }
    setLotElementQaqcStale(false);
    const makeRow = (label: string, preset?: Record<string, string>): Record<string, string> => {
      const row: Record<string, string> = { customers_ref: label, lab_ref: '', lab_weight: '' };
      elements.forEach(el => { row[el] = preset?.[el] ?? ''; });
      return row;
    };
    setDynamicQaqcElements(elements);
    setRoleQaqcElementMeta(buildRoleElementMeta(elements, batchSamples[0]));
    setDynamicQaqcStandards([
      makeRow('1er échantillon'),
      makeRow('BIS'),
      makeRow('BLANK MP', BLANK_MP_VALUES),
      makeRow('BLANK CP', BLANK_CP_VALUES),
      makeRow('OREAS 20a', OREAS_20A_VALUES),
      makeRow('OREAS 30a', OREAS_30A_VALUES),
    ]);
    // Reset role assignments
    setQcRoleFirstId(null);
    setQcRoleBisId(null);
    setQcRoleBlankMPId(null);
    setQcRoleBlankCPId(null);
    setIsRoleBasedQaqc(true);
    setShowDynamicQaqcForm(true);
  };

  // Row indices in the role-based layout
  const ROLE_ROW = { first: 0, bis: 1, blankMP: 2, blankCP: 3, oreas: 4 } as const;

  /** Fill one role row from a sample (values + lab_ref + lab_weight). */
  const fillRoleRow = (rowIndex: number, sampleId: string | null, opts?: { bisOf?: string | null }) => {
    setDynamicQaqcStandards(prev => {
      const updated = prev.map(r => ({ ...r }));
      if (!updated[rowIndex]) return prev;
      const batchSamples = (selectedSample as any)?.batchSamples || [];
      const sample = sampleId ? batchSamples.find((s: any) => s.id === sampleId) : null;
      const baseLabel = ['1er échantillon', 'BIS', 'BLANK MP', 'BLANK CP', 'OREAS 20a', 'OREAS 30a'][rowIndex] || '';
      if (!sample) {
        // Cleared: blank out values but keep the row label.
        const cleared: Record<string, string> = { customers_ref: baseLabel, lab_ref: '', lab_weight: '' };
        dynamicQaqcElements.forEach(el => { cleared[el] = ''; });
        updated[rowIndex] = cleared;
        return updated;
      }
      const clientRef = sample.external_id || sample.sample_id;
      let customersRef = clientRef;
      if (opts?.bisOf) {
        const orig = batchSamples.find((s: any) => s.id === opts.bisOf);
        const origRef = orig?.external_id || orig?.sample_id || clientRef;
        customersRef = `BIS(${origRef})`;
      } else if (rowIndex === ROLE_ROW.blankMP) {
        customersRef = `BLANK MP`;
      } else if (rowIndex === ROLE_ROW.blankCP) {
        customersRef = `BLANK CP`;
      }
      const row: Record<string, string> = {
        customers_ref: customersRef,
        lab_ref: sample.sample_id || '',
        lab_weight: roleQcLabWeight(sample),
      };
      dynamicQaqcElements.forEach(el => { row[el] = roleQcDisplayValue(sample, el); });
      updated[rowIndex] = row;
      return updated;
    });
  };

  const handleRoleFirstChange = (sampleId: string, checked: boolean) => {
    const id = checked ? sampleId : null;
    setQcRoleFirstId(id);
    fillRoleRow(ROLE_ROW.first, id);
    // If a BIS is already chosen, refresh its "BIS(...)" label against the new first sample.
    if (qcRoleBisId) fillRoleRow(ROLE_ROW.bis, qcRoleBisId, { bisOf: id });
  };
  const handleRoleBisChange = (sampleId: string, checked: boolean) => {
    const id = checked ? sampleId : null;
    setQcRoleBisId(id);
    fillRoleRow(ROLE_ROW.bis, id, { bisOf: qcRoleFirstId });
  };
  const handleRoleBlankMPChange = (sampleId: string, checked: boolean) => {
    const id = checked ? sampleId : null;
    setQcRoleBlankMPId(id);
    fillRoleRow(ROLE_ROW.blankMP, id);
  };
  const handleRoleBlankCPChange = (sampleId: string, checked: boolean) => {
    const id = checked ? sampleId : null;
    setQcRoleBlankCPId(id);
    fillRoleRow(ROLE_ROW.blankCP, id);
  };

  /** Remove one QA/QC row; clear role pickers because row indices shift after delete. */
  const removeRoleBasedQaqcRow = (idx: number) => {
    setDynamicQaqcStandards(prev => prev.filter((_, i) => i !== idx));
    setQcRoleFirstId(null);
    setQcRoleBisId(null);
    setQcRoleBlankMPId(null);
    setQcRoleBlankCPId(null);
  };

  // Auto-fill BIS sample data (Row 1 or Row 2)
  const autoFillBISRow = (sampleId: string, rowIndex: number, useBISFormat: boolean = false) => {
    if (!selectedSample) return;
    
    const batchSamples = (selectedSample as any)?.batchSamples || [];
    const sample = batchSamples.find((s: any) => s.id === sampleId);
    
    if (!sample) {
      setSnackbarMessage('❌ Échantillon non trouvé');
      setSnackbarOpen(true);
      return;
    }
    
    const analysisResults = sample.metadonnees?.analysis_results || [];
    const icpAnalysisReadings = sample.metadonnees?.icp_analysis_form?.analysis_readings || [];
    const auAnalysisReadings = sample.metadonnees?.au_analysis_form?.analysis_readings || [];
    
    if (qaqcStandards.length === 0) {
      setSnackbarMessage('❌ Chargez le template QA/QC avant de sélectionner un BIS');
      setSnackbarOpen(true);
      return;
    }

    if (analysisResults.length === 0 && icpAnalysisReadings.length === 0) {
      setSnackbarMessage(`❌ Pas de résultats d'analyse pour ${sample.sample_id}`);
      setSnackbarOpen(true);
      return;
    }
    
    // Get value with FIXED QA/QC detection limit handling
    const getValue = (element: string): string => {
      let result = analysisResults.find((r: any) => r.element === element);
      if (!result && icpAnalysisReadings.length > 0) {
        const icpHit = icpAnalysisReadings.find((r: any) => r.element === element);
        if (icpHit) {
          result = {
            element,
            concentration_ppm: icpHit.concentration_ppm ?? icpHit.ppm ?? 0,
            detection_status: icpHit.detection_status || 'VALID',
          };
        }
      }
      
      // Fixed QA/QC detection limits (from the table header)
      const qaqcDetectionLimits: Record<string, number> = {
        'Ag': 1,
        'Cu': 0.01,
        'Fe': 0.01,
        'Pb': 0.01,
        'Zn': 0.01,
        'Ag_FA': 200,
        'Au_FA': 0.05,
        'Mo_ICP': 1,
        'As_ICP': 1,
        'Sn_ICP': 1
      };
      
      // Map element names to QA/QC table column names
      const elementMapping: Record<string, string> = {
        'Ag': 'Ag',
        'Cu': 'Cu',
        'Fe': 'Fe',
        'Pb': 'Pb',
        'Zn': 'Zn',
        'Au': 'Au_FA',
        'Mo': 'Mo_ICP',
        'As': 'As_ICP',
        'Sn': 'Sn_ICP'
      };
      
      const qaqcColumnName = elementMapping[element] || element;
      const detectionLimit = qaqcDetectionLimits[qaqcColumnName] || 0.01;
      
      // Special handling for Au: check au_analysis_form if not found or = 0
      if (element === 'Au' && (!result || result.concentration_ppm === 0) && auAnalysisReadings.length > 0) {
        const auFormData = auAnalysisReadings.find((r: any) => r.element === 'Au');
        if (auFormData) {
          const auValue = auFormData.ppm || auFormData.grams_per_tonne || 0;
          // If Au value is below detection limit (0.05), show <0.05
          if (auValue < detectionLimit) {
            return `<${detectionLimit}`;
          }
          result = {
            element: 'Au',
            concentration_ppm: auValue,
            detection_status: 'VALID'
          };
        }
      }
      
      if (!result) return '';
      
      // If below detection limit, show "<detection_limit"
      if (result.detection_status === 'BELOW_LOD') {
        return `<${detectionLimit}`;
      }
      
      const ppm = result.concentration_ppm || 0;

      // BGM QA/QC: Cu/Fe/Pb/Zn display in % — use per-value ppm heuristic (matches certificate).
      const QA_PERCENT_ELEMENTS = new Set(['Cu', 'Fe', 'Pb', 'Zn']);
      const displayValue = QA_PERCENT_ELEMENTS.has(qaqcColumnName)
        ? icpMajorPpmToOxidePercent(ppm)
        : ppm;

      if (displayValue === 0 || displayValue < detectionLimit) {
        return `<${detectionLimit}`;
      }

      if (['Ag', 'Mo', 'As', 'Sn', 'Mo_ICP', 'As_ICP', 'Sn_ICP'].includes(element)) {
        return Math.round(displayValue).toString();
      }

      return displayValue.toFixed(2);
    };
    
    // Get client reference
    const clientRef = sample.external_id || sample.sample_id;
    
    // For 2nd BIS, use BIS(first BIS's external_id)
    let customersRefValue = clientRef;
    if (useBISFormat && firstBISSampleId) {
      const firstBISSample = batchSamples.find((s: any) => s.id === firstBISSampleId);
      const firstBISClientRef = firstBISSample?.external_id || firstBISSample?.sample_id || clientRef;
      customersRefValue = `BIS(${firstBISClientRef})`;
    }
    
    // Update the row
    const updatedStandards = [...qaqcStandards];
    updatedStandards[rowIndex] = {
      ...updatedStandards[rowIndex],
      customers_ref: customersRefValue,
      lab_ref: sample.sample_id,
      Ag: getValue('Ag'),
      Cu: getValue('Cu'),
      Fe: getValue('Fe'),
      Pb: getValue('Pb'),
      Zn: getValue('Zn'),
      Ag_FA: '',  // Keep empty (grayed column in QA/QC table)
      Au_FA: getValue('Au'),
      Mo_ICP: getValue('Mo'),
      As_ICP: getValue('As'),
      Sn_ICP: getValue('Sn')
    };
    
    setQaqcStandards(updatedStandards);
    setSnackbarMessage(`✅ Données importées depuis ${sample.sample_id} (${clientRef})`);
    setSnackbarOpen(true);
  };
  
  
  // Handle first BIS checkbox (fills Row 1 with sample's own external_id)
  const handleFirstBISChange = (sampleId: string, checked: boolean) => {
    if (checked) {
      // Uncheck second BIS if same sample
      if (sampleId === secondBISSampleId) {
        setSecondBISSampleId(null);
      }
      setFirstBISSampleId(sampleId);
      autoFillBISRow(sampleId, 0, false); // Row 0, no BIS format (use sample's own external_id)
    } else {
      setFirstBISSampleId(null);
      // Clear Row 1
      const updatedStandards = [...qaqcStandards];
      updatedStandards[0] = {
        customers_ref: "BMX25120250",
        lab_ref: "",
        Ag: "",
        Cu: "",
        Fe: "",
        Pb: "",
        Zn: "",
        Ag_FA: "",
        Au_FA: "",
        Mo_ICP: "",
        As_ICP: "",
        Sn_ICP: "",
      };
      setQaqcStandards(updatedStandards);
    }
  };
  
  // Handle second BIS checkbox (fills Row 2 with BIS(first BIS's external_id))
  const handleSecondBISChange = (sampleId: string, checked: boolean) => {
    if (checked) {
      // Uncheck first BIS if same sample
      if (sampleId === firstBISSampleId) {
        setFirstBISSampleId(null);
        // Clear Row 1
        const updatedStandards = [...qaqcStandards];
        updatedStandards[0] = {
          customers_ref: "BMX25120250",
          lab_ref: "",
          Ag: "",
          Cu: "",
          Fe: "",
          Pb: "",
          Zn: "",
          Ag_FA: "",
          Au_FA: "",
          Mo_ICP: "",
          As_ICP: "",
          Sn_ICP: "",
        };
        setQaqcStandards(updatedStandards);
      }
      setSecondBISSampleId(sampleId);
      autoFillBISRow(sampleId, 1, true); // Row 1, use BIS format with first BIS's external_id
    } else {
      setSecondBISSampleId(null);
      // Clear Row 2
      const updatedStandards = [...qaqcStandards];
      updatedStandards[1] = {
        customers_ref: "BIS(BMX25120250)",
        lab_ref: "",
        Ag: "",
        Cu: "",
        Fe: "",
        Pb: "",
        Zn: "",
        Ag_FA: "",
        Au_FA: "",
        Mo_ICP: "",
        As_ICP: "",
        Sn_ICP: "",
      };
      setQaqcStandards(updatedStandards);
    }
  };

  // 🆕 SUPERVISOR EDIT: Handle value edits with audit trail
  const handleValueEdit = (sampleId: string, element: string, newValue: number, originalPpm: number) => {
    // Validate input
    if (isNaN(newValue) || newValue < 0) {
      console.warn(`Invalid value for ${element}: ${newValue}`);
      return;
    }

    // Store original value if first edit
    if (!originalValues[sampleId]?.[element]) {
      setOriginalValues(prev => ({
        ...prev,
        [sampleId]: {
          ...prev[sampleId],
          [element]: originalPpm
        }
      }));
    }

    // Update edited values
    setEditedValues(prev => ({
      ...prev,
      [sampleId]: {
        ...prev[sampleId],
        [element]: newValue
      }
    }));

    // Track in edit history
    setEditHistory(prev => [...prev, {
      sampleId,
      element,
      originalValue: originalValues[sampleId]?.[element] || originalPpm,
      editedValue: newValue,
      timestamp: new Date().toISOString()
    }]);

    console.log(`✏️ Edit: ${element} for ${sampleId}: ${originalPpm} → ${newValue}`);
  };

  /** Efface la surcharge PAF pour réutiliser la formule Excel ((CaO/56)+(MgO/40))*44 */
  const clearPafOverride = (sampleId: string) => {
    setEditedValues((prev) => {
      const row = prev[sampleId];
      if (!row || row['__PAF__'] === undefined) return prev;
      const { __PAF__, ...rest } = row;
      const next = { ...prev };
      if (Object.keys(rest).length === 0) delete next[sampleId];
      else next[sampleId] = rest;
      return next;
    });
  };

  /** Revert a single element back to its original analyst value (removes it from editedValues). */
  const revertEditedValue = (sampleId: string, element: string) => {
    setEditedValues((prev) => {
      const row = prev[sampleId];
      if (!row || row[element] === undefined) return prev;
      const { [element]: _removed, ...rest } = row;
      const next = { ...prev };
      if (Object.keys(rest).length === 0) delete next[sampleId];
      else next[sampleId] = rest;
      return next;
    });
    // Also clear valueSourcePref so the matrix auto-defaults correctly
    setValueSourcePref((prev) => {
      if (!prev[sampleId]?.[element]) return prev;
      const { [element]: _r, ...rest } = prev[sampleId];
      return { ...prev, [sampleId]: rest };
    });
    setEditingCell(null);
  };

  /** Clear a manual override from the per-element picker (reverts to acid/fusion default). */
  const clearManualValue = (sample: any, element: string) => {
    const sampleId = sample.id;
    setManualOverrides((prev) => {
      const { [element]: _r, ...rest } = (prev[sampleId] || {});
      return { ...prev, [sampleId]: rest };
    });
    // Revert source pref to acid (or fusion if fusion data exists)
    const hasFusion = !!fusionImportedReadings[sampleId]?.[element];
    setValueSourcePref((prev) => {
      const { [element]: _r, ...rest } = (prev[sampleId] || {});
      const next = { ...prev, [sampleId]: rest };
      if (hasFusion) next[sampleId] = { ...rest, [element]: 'fusion' };
      return next;
    });
    // Remove from editedValues too
    setEditedValues((prev) => {
      const row = prev[sampleId];
      if (!row || row[element] === undefined) return prev;
      const { [element]: _rm, ...rest } = row;
      const next = { ...prev };
      if (Object.keys(rest).length === 0) delete next[sampleId];
      else next[sampleId] = rest;
      return next;
    });
  };

  // 🆕 SUPERVISOR EDIT: Clear all edits (on dialog close or cancel)
  const clearAllEdits = () => {
    setEditedValues({});
    setOriginalValues({});
    setEditHistory([]);
    setEditingCell(null);
    setEditingCellDraft('');
    setPafDrafts({});
    setMatrixEditingCell(null);
    setMatrixEditDraft('');
    setSupervisorImportOpen(false);
    setSupervisorImportPreview(null);
    setSupervisorImportSelectedFile('');
    setFusionImportedReadings({});
    setValueSourcePref({});
    setManualOverrides({});
    setExpandedSourcePicker(new Set());
    setSampleSourcePref({});
  };

  // 🆕 SOURCE PICKER: get the original 4-acides ppm value for a sample/element
  const getAcidPpm = (sample: any, element: string): number | undefined => {
    const readings = icpGetMetadonneesReadings(sample);
    const r = readings.find((x: any) => x.element === element);
    return r ? Number(r.concentration_ppm) || 0 : undefined;
  };

  // 🆕 SOURCE PICKER: determine which source is currently effective for an element
  // (explicit pref > fusion overlay > manual edit > 4-acides)
  const getEffectiveSource = (sampleId: string, element: string): ValueSource => {
    const pref = valueSourcePref[sampleId]?.[element];
    if (pref) return pref;
    if (fusionImportedReadings[sampleId]?.[element] !== undefined) return 'fusion';
    if (manualOverrides[sampleId]?.[element] !== undefined) return 'manual';
    return 'acid';
  };

  // 🆕 SOURCE PICKER: explicit choice — copies the chosen value into editedValues so the
  // existing validation/certificate flow uses it.
  const setSourceFor = (sample: any, element: string, source: ValueSource) => {
    const sampleId = sample.id;
    setValueSourcePref(prev => ({
      ...prev,
      [sampleId]: { ...(prev[sampleId] || {}), [element]: source }
    }));
    setEditedValues(prev => {
      const sampleEdits = { ...(prev[sampleId] || {}) };
      const acidPpm = getAcidPpm(sample, element) ?? 0;
      if (source === 'acid') {
        delete sampleEdits[element];
      } else if (source === 'fusion') {
        const fv = fusionImportedReadings[sampleId]?.[element];
        if (fv !== undefined) sampleEdits[element] = fv;
      } else if (source === 'manual') {
        const mv = manualOverrides[sampleId]?.[element];
        if (mv !== undefined) sampleEdits[element] = mv;
      }
      // Track original for audit if this is the first time this element is touched
      if (source !== 'acid' && !originalValues[sampleId]?.[element]) {
        setOriginalValues(prevOrig => ({
          ...prevOrig,
          [sampleId]: { ...(prevOrig[sampleId] || {}), [element]: acidPpm }
        }));
      }
      const next = { ...prev };
      if (Object.keys(sampleEdits).length === 0) delete next[sampleId];
      else next[sampleId] = sampleEdits;
      return next;
    });
  };

  // 🆕 SOURCE PICKER: type a manual value (auto-switches source to 'manual')
  const setManualValue = (sample: any, element: string, value: number) => {
    const sampleId = sample.id;
    if (isNaN(value) || value < 0) return;
    setManualOverrides(prev => ({
      ...prev,
      [sampleId]: { ...(prev[sampleId] || {}), [element]: value }
    }));
    setValueSourcePref(prev => ({
      ...prev,
      [sampleId]: { ...(prev[sampleId] || {}), [element]: 'manual' }
    }));
    const acidPpm = getAcidPpm(sample, element) ?? 0;
    handleValueEdit(sampleId, element, value, acidPpm);
  };

  const toggleSourcePicker = (sampleId: string) => {
    setExpandedSourcePicker(prev => {
      const next = new Set(prev);
      if (next.has(sampleId)) next.delete(sampleId);
      else next.add(sampleId);
      return next;
    });
  };

  // 🆕 PER-SAMPLE source: determine effective per-sample source
  // (returns 'acid' if no fusion data is available regardless of pref)
  const getEffectiveSampleSource = (sampleId: string): SampleSource => {
    const hasFusion = !!fusionImportedReadings[sampleId] && Object.keys(fusionImportedReadings[sampleId]).length > 0;
    const pref = sampleSourcePref[sampleId];
    if (!hasFusion) return 'acid';
    return pref || 'fusion'; // default: fusion wins when fusion data is present
  };

  // 🆕 PER-SAMPLE source change: bulk-apply choice to editedValues so calculations + validation
  // payload reflect the user's per-sample decision. Per-element prefs are cleared/reset on change.
  const setSampleSource = (sample: any, source: SampleSource) => {
    const sampleId = sample.id;
    setSampleSourcePref(prev => ({ ...prev, [sampleId]: source }));

    if (source === 'mixed') {
      // Open the per-element picker so user can fine-tune
      setExpandedSourcePicker(prev => {
        const next = new Set(prev);
        next.add(sampleId);
        return next;
      });
      return; // editedValues stays as-is so user can pick element-by-element
    }

    // Auto-apply: rebuild editedValues for this sample based on the chosen source
    const fusionMap = fusionImportedReadings[sampleId] || {};
    const readings = icpGetMetadonneesReadings(sample);
    setEditedValues(prev => {
      const next = { ...prev };
      // Preserve the PAF override
      const paf = next[sampleId]?.['__PAF__'];
      const newSampleEdits: Record<string, number> = {};
      if (paf !== undefined) newSampleEdits['__PAF__'] = paf;

      if (source === 'acid') {
        // No overrides — calculations fall back to original 4-acides readings
      } else if (source === 'fusion') {
        // Apply fusion values as overrides (matches existing fusion-overlay behavior)
        for (const [el, v] of Object.entries(fusionMap)) {
          newSampleEdits[el] = v;
        }
      }

      if (Object.keys(newSampleEdits).length === 0) delete next[sampleId];
      else next[sampleId] = newSampleEdits;
      return next;
    });

    // Track originals for audit when switching to fusion
    if (source === 'fusion') {
      setOriginalValues(prev => {
        const next = { ...prev };
        if (!next[sampleId]) next[sampleId] = {};
        for (const el of Object.keys(fusionMap)) {
          if (next[sampleId][el] === undefined) {
            const r = readings.find((x: any) => x.element === el);
            next[sampleId][el] = r ? Number(r.concentration_ppm) || 0 : 0;
          }
        }
        return next;
      });
    }

    // Clear per-element prefs for this sample when bulk-switching (to avoid stale picks)
    setValueSourcePref(prev => {
      const next = { ...prev };
      delete next[sampleId];
      return next;
    });
  };

  /**
   * 🆕 LOT-LEVEL APPLY: fan out a `LotSourceRule` (default + exceptions) to every
   * sample in the lot in one click. This is the shortcut for the common case
   * the user described: "keep all 4-Acides values, but for SiO₂ take the Fusion
   * value". Only samples that actually have BOTH 4-Acides and Fusion data are
   * touched — pure-4A or pure-Fusion samples are left alone (with a clear
   * sample-level pref).
   *
   * Effects on existing state:
   *   - sampleSourcePref[sampleId] = 'mixed' for samples with both methods
   *     (so the per-element picker rules), or = 'acid' / 'fusion' for samples
   *     that only have one method available.
   *   - valueSourcePref[sampleId][element] = exceptions[element] ?? defaultSource.
   *   - editedValues is rebuilt cell-by-cell using setSourceFor, so the
   *     calculations + validation payload are kept in sync without bypassing
   *     the existing audit trail.
   *
   * The rule remains visible in the editor after applying so the supervisor
   * can tweak and re-apply.
   */
  const applyLotSourceRule = (rule: LotSourceRule) => {
    const batchSamples: any[] = (selectedSample as any)?.batchSamples || [];
    if (batchSamples.length === 0) return;

    let applied = 0;
    for (const sample of batchSamples) {
      const sampleId = sample.id;
      const fusionMap = fusionImportedReadings[sampleId] || {};
      const hasFusion = Object.keys(fusionMap).length > 0;
      const readings = icpGetMetadonneesReadings(sample);
      const has4A = readings.length > 0;

      // Skip samples with no data at all (BGM / multi-lab samples never reach here)
      if (!has4A && !hasFusion) continue;

      // Single-method samples: just lock them to the only available source
      if (!hasFusion) {
        setSampleSource(sample, 'acid');
        continue;
      }
      if (!has4A) {
        setSampleSource(sample, 'fusion');
        continue;
      }

      // Mixed sample: apply rule cell-by-cell.
      setSampleSourcePref(prev => ({ ...prev, [sampleId]: 'mixed' }));

      // Build the union of elements we care about (= elements present in
      // either source for this sample, excluding internal markers like __PAF__).
      const elementSet = new Set<string>();
      readings.forEach((r: any) => { if (r.element && !r.element.startsWith('__')) elementSet.add(r.element); });
      Object.keys(fusionMap).forEach(el => { if (!el.startsWith('__')) elementSet.add(el); });

      for (const el of elementSet) {
        const target: 'acid' | 'fusion' = rule.exceptions[el] ?? rule.defaultSource;
        // Only switch to "fusion" if we actually have a fusion value for this element
        if (target === 'fusion' && fusionMap[el] === undefined) {
          setSourceFor(sample, el, 'acid');
        } else {
          setSourceFor(sample, el, target);
        }
      }
      applied += 1;
    }

    setLotRuleApplied(true);
    setSnackbarMessage(`✅ Règle appliquée à ${applied} échantillon(s) avec 4-Acides + Fusion`);
    setSnackbarOpen(true);
  };

  /** Reset the lot-level rule UI back to "all 4-Acides, no exceptions". Does NOT
   * clear sample-level / per-element preferences already in state — use the
   * existing per-sample chips for that. */
  const resetLotSourceRule = () => {
    setLotSourceRule({ defaultSource: 'acid', exceptions: {} });
    setLotRuleApplied(false);
  };

  // --- Supervisor ICP file import ---
  const handleSupervisorImportScan = async () => {
    try {
      setSupervisorImportLoading(true);

      // Pass the open batch's sample IDs so the backend can rank the file list
      // by how many of those samples each file contains. Without this, the user
      // sees all 50+ CSVs in the directory with no hint which one is for the
      // current lot — the typical complaint being "I clicked scan, I can't find
      // the file for R-XXXX". With it, the matching file lands at the top with
      // a "✅ N samples matched" badge.
      const batchSamples: any[] = (selectedSample as any)?.batchSamples || [];
      const batchSampleIds = batchSamples
        .map((s: any) => (s.sample_id || '').trim())
        .filter(Boolean);
      const params = batchSampleIds.length
        ? { batchSampleIds: batchSampleIds.join(',') }
        : undefined;

      // ⚠️ apiService.request() returns the parsed JSON body DIRECTLY (it's a
      // custom fetch wrapper, not axios — there is no { data, status } envelope).
      // The previous `res.data?.files` always evaluated to undefined, which is
      // why the dropdown was permanently empty even though the backend was
      // returning all 51 files correctly.
      const res = await api.get<{ files: any[] }>(
        '/icp/scan-directory',
        params ? { params } : undefined,
      );
      const files = (res?.files || []).map((f: any) => ({
        name: f.name || f.filename || f,
        analysisType: f.analysisType,
        matchCount: typeof f.matchCount === 'number' ? f.matchCount : undefined,
        matchedSampleIds: f.matchedSampleIds,
      }));
      setSupervisorImportFiles(files);
      setSupervisorImportOpen(true);
    } catch (err) {
      console.error('Scan error', err);
      setSnackbarMessage('Erreur lors du scan du dossier ICP');
      setSnackbarOpen(true);
    } finally {
      setSupervisorImportLoading(false);
    }
  };

  const handleSupervisorImportParse = async () => {
    if (!supervisorImportSelectedFile) return;
    const batchSamples: any[] = (selectedSample as any)?.batchSamples || [];
    if (batchSamples.length === 0) return;

    try {
      setSupervisorImportLoading(true);
      // Same response-shape note as Scan: apiService.post returns the parsed
      // JSON body directly. No `.data` envelope.
      const res = await api.post<{
        results?: any[];
        filteredResults?: any[];
      }>('/icp/parse-file', {
        filename: supervisorImportSelectedFile,
        batchSamples: batchSamples.map((s: any) => ({ id: s.id, sample_id: s.sample_id })),
      });
      const parsed = res?.results || res?.filteredResults || [];
      setSupervisorImportPreview(parsed);
    } catch (err) {
      console.error('Parse error', err);
      setSnackbarMessage('Erreur lors du parsing du fichier');
      setSnackbarOpen(true);
    } finally {
      setSupervisorImportLoading(false);
    }
  };

  const handleSupervisorImportApply = () => {
    if (!supervisorImportPreview || supervisorImportPreview.length === 0) return;
    const batchSamples: any[] = (selectedSample as any)?.batchSamples || [];
    const mode = supervisorImportMode;

    const newEdited = { ...editedValues };
    const newOriginals = { ...originalValues };
    const newFusionReadings = { ...fusionImportedReadings };
    let appliedCount = 0;

    for (const result of supervisorImportPreview) {
      // `processResultsWithDefaults` (backend) injects `{flags:"DEFAULT",ppm:0}` rows
      // for any expected element missing from the CSV. We must NEVER let those
      // zero-defaults overwrite real fusion values that landed under a renamed
      // oxide key earlier in this loop.
      if (result.flags === 'DEFAULT') continue;

      // Trim both sides — Agilent fusion CSVs sometimes pad labels with a trailing space
      const refClean = ((result.sample_reference as string | undefined) || '').trim();
      const sample = batchSamples.find(
        (s: any) => ((s.sample_id as string | undefined) || '').trim() === refClean,
      );
      if (!sample) continue;

      // BGM safety: BGM/ZMSM samples report majors as raw element symbols
      // (Fe %, Cu %, Pb %, …) and MUST NEVER be renamed to oxide form.
      // Only non-BGM fusion lots get the oxide-key normalization.
      const isBGMSample =
        sample.metadonnees?.bgm_method_selected === true ||
        sample.metadonnees?.multi_lab_workflow === true;

      const readings = icpGetMetadonneesReadings(sample);
      const existingMap: Record<string, number> = {};
      for (const r of readings) {
        existingMap[r.element] = Number(r.concentration_ppm) || 0;
      }

      // For non-BGM fusion-mode rows whose element is a major raw symbol
      // ("Ca","Mg","Fe","Si","Al","K","Mn","Na","P","Ti"), store under the
      // oxide key ("CaO","MgO","Fe2O3", …) so PAF helpers and the existing
      // metadonnees readings find it directly (no `??` collision against
      // the analyst's zero-valued oxide placeholders).
      const renameToOxide =
        mode === 'fusion' && !isBGMSample && ICP_RAW_TO_OXIDE[result.element] !== undefined;
      const el = renameToOxide ? ICP_RAW_TO_OXIDE[result.element] : result.element;

      const newPpm = Number(result.concentration_ppm) || 0;
      const existingPpm = existingMap[el] ?? 0;

      if (mode === 'fusion') {
        const isMajor = ICP_TREATMENT_MAJOR_ELEMENTS.has(el) || el === 'S';
        if (!isMajor && existingPpm > 0) continue;
        // Store the fusion reading separately for dual-table display
        if (!newFusionReadings[sample.id]) newFusionReadings[sample.id] = {};
        newFusionReadings[sample.id][el] = newPpm;
      }

      if (!newOriginals[sample.id]) newOriginals[sample.id] = {};
      if (!newOriginals[sample.id][el]) {
        newOriginals[sample.id][el] = existingPpm;
      }
      if (!newEdited[sample.id]) newEdited[sample.id] = {};
      newEdited[sample.id][el] = newPpm;
      appliedCount++;
    }

    setEditedValues(newEdited);
    setOriginalValues(newOriginals);
    if (mode === 'fusion') setFusionImportedReadings(newFusionReadings);
    setSupervisorImportPreview(null);
    setSupervisorImportOpen(false);
    setSupervisorImportSelectedFile('');

    setSnackbarMessage(
      mode === 'fusion'
        ? `Import fusion appliqué: ${appliedCount} valeurs (majeurs remplacés, traces conservées)`
        : `Import confirmation appliqué: ${appliedCount} valeurs remplacées pour les échantillons re-analysés`,
    );
    setSnackbarOpen(true);
  };


  const handleGeneratePartialCertificate = async () => {
    try {
      setGeneratingPartialCert(true);
      const batchSamples = (selectedSample as any)?.batchSamples || [selectedSample];
      const sampleIds = batchSamples.map((s: any) => s.id);
      const token = localStorage.getItem('access_token');

      const response = await fetch('/api/v1/certificates/generate/partial-batch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ sampleIds, laboratory: 'LABORATOIRE_ICP' }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Echec de la generation du certificat partiel');
      }

      const result = await response.json();
      const certificateId = result.data?.id;

      if (certificateId) {
        const downloadResponse = await fetch(`/api/v1/certificates/${certificateId}/download`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const html = await downloadResponse.text();
        const newWindow = window.open('', '_blank');
        if (newWindow) {
          newWindow.document.write(html);
          newWindow.document.close();
        }
      }

      setSnackbarMessage(`Certificat partiel ICP genere avec succes pour ${sampleIds.length} echantillons`);
      setSnackbarOpen(true);
    } catch (error: any) {
      setSnackbarMessage(`Erreur: ${error.message}`);
      setSnackbarOpen(true);
    } finally {
      setGeneratingPartialCert(false);
    }
  };

  /** Certificate column list for non-BGM lot modal (respects supervisor element selection). */

  /** True when the PAF / treatment block is shown (blocks CaO/MgO oxide↔element conversion). */
  const batchShowsPafTreatmentBlock = (batchSamples: Sample[]): boolean => {
    if (batchSamples.length === 0) return false;
    const ICP_TREATMENT_METHOD_KEYS = [
      'PRE.MO/ANA/038', 'PRE.MO/ANA/037', 'PRE.MO/ANA/041',
      'PRE.MO/ANA/007', 'PRE.MO/ANA/039',
    ];
    return batchSamples.some((s: any) => {
      const methods: string[] = s.metadonnees?.analyses_demandees || s.analyses_demandees || [];
      if (methods.some((m: string) => typeof m === 'string' && ICP_TREATMENT_METHOD_KEYS.some(k => m.includes(k)))) return true;
      const dig = s.metadonnees?.icp_analysis_form?.digestion_method;
      if (dig === 'FOUR_ACID' || dig === 'FUSION_Na2O2') return true;
      const at = s.metadonnees?.icp_analysis_form?.analysis_type;
      if (at === '4-acides' || at === 'fusion') return true;
      if (icpGetMetadonneesReadings(s).some((r: any) => r.analysisType === '4-acides' || r.analysisType === 'fusion')) return true;
      return false;
    });
  };

  const openReportFormDialog = (symbol: string) => {
    const batchSamples = (selectedSample as any)?.batchSamples || [];
    if (isBGMBatch(batchSamples) || isZMSMBatch(batchSamples)) return;
    if (!isReportFormConvertible(symbol)) return;
    const pair = getIcpConversionPair(symbol)!;
    if (batchShowsPafTreatmentBlock(batchSamples) && (pair.oxide === 'CaO' || pair.oxide === 'MgO')) {
      setSnackbarMessage('❌ Conversion CaO/MgO indisponible lorsque le bloc PAF est actif');
      setSnackbarOpen(true);
      return;
    }
    const existing = lotReportForm[pair.element];
    setReportFormDialogSymbol(symbol);
    setReportFormDialogMode(existing?.mode === 'element' ? 'element' : 'oxide');
    setReportFormDialogOpen(true);
  };

  const applyReportFormChoice = () => {
    if (!reportFormDialogSymbol) return;
    const pair = getIcpConversionPair(reportFormDialogSymbol);
    if (!pair) return;

    if (reportFormDialogMode === 'oxide') {
      setLotReportForm(prev => {
        const next = { ...prev };
        delete next[pair.element];
        delete next[pair.oxide];
        return next;
      });
      setLotFinalElements(prev => {
        const without = prev.filter(e => e !== pair.element && e !== pair.oxide);
        const next = _sortANA38Order([...without, pair.oxide]);
        if (JSON.stringify(next) !== JSON.stringify(prev)) invalidateLotQaqcOnElementChange();
        return next;
      });
      setReportFormDialogOpen(false);
      setSnackbarMessage(`✅ ${pair.oxide} sera reporté en oxyde sur le certificat`);
      setSnackbarOpen(true);
      return;
    }

    const hadBoth = lotFinalElements.includes(pair.element) && lotFinalElements.includes(pair.oxide);
    const entry = buildReportFormEntry(reportFormDialogSymbol, 'element', lotFinalElements);
    if (!entry) return;

    setLotReportForm(prev => {
      const next = { ...prev };
      delete next[pair.oxide];
      next[pair.element] = entry;
      return next;
    });
    setLotFinalElements(prev => {
      const without = prev.filter(e => e !== pair.oxide && e !== pair.element);
      const next = _sortANA38Order([...without, pair.element]);
      if (JSON.stringify(next) !== JSON.stringify(prev)) invalidateLotQaqcOnElementChange();
      return next;
    });
    setReportFormDialogOpen(false);
    if (hadBoth) {
      setSnackbarMessage(`⚠️ ${pair.element} et ${pair.oxide} étaient tous deux sélectionnés — conservé en ${pair.element} (élément)`);
    } else {
      setSnackbarMessage(`✅ ${pair.oxide} → ${pair.element} sur le certificat (÷ ${pair.factor})`);
    }
    setSnackbarOpen(true);
  };

  const getMatrixStoredPpmForColumn = (
    sampleId: string,
    columnSymbol: string,
    elementData: Record<string, any>,
    reportForm: SupervisorReportFormMap,
  ): { storedPpm: number; dataKey: string; entry: SupervisorReportFormEntry | null; finalPercent?: number | null } => {
    const { dataKey, entry } = resolveReportFormColumn(columnSymbol, reportForm);
    const reading = elementData[dataKey] || elementData[columnSymbol];
    const ppm = Number(reading?.concentration_ppm) || 0;
    const finalPercent = reading?.concentration_final_percent;
    const edited = editedValues[sampleId]?.[dataKey] ?? editedValues[sampleId]?.[columnSymbol];
    return { storedPpm: edited !== undefined ? edited : ppm, dataKey, entry, finalPercent };
  };

  const formatMatrixCellValue = (
    icpMethodCode: string,
    columnSymbol: string,
    storedPpm: number,
    entry: SupervisorReportFormEntry | null,
    columnForcePercent = false,
    finalPercent?: number | null,
  ): { formatted: string; reportAsPercent: boolean; editDisplayValue: number } => {
    const limit = getMethodDetectionLimit(icpMethodCode, columnSymbol);
    let reportAsPercent = limit?.unit === '%';
    if (columnForcePercent && limit?.unit === 'ppm') reportAsPercent = true;
    if (entry?.mode === 'element') {
      const elementPct = icpStoredPpmToElementPercent(storedPpm, entry.factor);
      const displayPpm = elementPct * 10000;
      const formatted = formatWithMethodDetectionLimitFrontend(icpMethodCode, columnSymbol, displayPpm);
      return { formatted, reportAsPercent: true, editDisplayValue: elementPct };
    }
    const fmtOpts = columnForcePercent
      ? { columnForcePercent: true, finalPercent }
      : finalPercent != null ? { finalPercent } : undefined;
    const formatted = formatWithMethodDetectionLimitFrontend(icpMethodCode, columnSymbol, storedPpm, fmtOpts);
    const editDisplayValue = limit ? icpStoredPpmToDisplayValue(limit, storedPpm, columnForcePercent, finalPercent) : storedPpm;
    return { formatted, reportAsPercent, editDisplayValue };
  };

  const getBatchCertificateElements = (batchSamples: Sample[]): string[] => {
    if (lotFinalElements.length > 0) return _sortANA38Order([...lotFinalElements]);
    return getAllBatchElements(batchSamples, false);
  };

  const batchHasHeterogeneousElements = (batchSamples: Sample[]): boolean => {
    const hashes = new Set<string>();
    batchSamples.forEach((s: Sample) => {
      const sel = s.metadonnees?.selected_elements || [];
      hashes.add(JSON.stringify([...sel].sort()));
    });
    return hashes.size > 1;
  };

  const invalidateLotQaqcOnElementChange = () => {
    if (showDynamicQaqcForm && isRoleBasedQaqc) {
      setShowDynamicQaqcForm(false);
      setIsRoleBasedQaqc(false);
      setDynamicQaqcStandards([]);
      setDynamicQaqcElements([]);
      setLotElementQaqcStale(true);
    }
  };

  const toggleLotCertificateElement = (element: string) => {
    setLotFinalElements(prev => {
      if (prev.includes(element)) {
        if (prev.length <= 1) {
          setSnackbarMessage('❌ Au moins 1 élément doit rester sélectionné pour le certificat final');
          setSnackbarOpen(true);
          return prev;
        }
        const next = _sortANA38Order(prev.filter(e => e !== element));
        invalidateLotQaqcOnElementChange();
        return next;
      }
      const next = _sortANA38Order([...prev, element]);
      invalidateLotQaqcOnElementChange();
      return next;
    });
  };

  const resetLotCertificateElements = () => {
    setLotFinalElements([...lotInitialElements]);
    invalidateLotQaqcOnElementChange();
    setLotElementQaqcStale(false);
  };

  const handleCompleteValidation = async () => {
    if (!selectedSample || !validationNotes.trim()) return;

    try {
      const token = localStorage.getItem('access_token');
      
      // Check if token exists
      if (!token) {
        setSnackbarMessage('❌ Session expirée - Veuillez vous reconnecter');
        setSnackbarOpen(true);
        return;
      }
      const isBatchValidation = (selectedSample as any)?.isBatchValidation;
      const batchSamples = (selectedSample as any)?.batchSamples || [selectedSample];

      if (
        isBatchValidation &&
        validationDecision === 'APPROVE' &&
        !isBGMBatch(batchSamples) &&
        !isZMSMBatch(batchSamples)
      ) {
        if (lotFinalElements.length < 1) {
          setSnackbarMessage('❌ Au moins 1 élément doit rester sélectionné pour le certificat final');
          setSnackbarOpen(true);
          return;
        }
        if (lotElementQaqcStale) {
          setSnackbarMessage('❌ Les éléments ont changé — rechargez le template QA/QC avant validation');
          setSnackbarOpen(true);
          return;
        }
      }
      
      if (isBatchValidation) {
        // Batch validation - validate all samples in the batch
        // Debug log removed
        
        let successCount = 0;
        let errorCount = 0;
        const errors: string[] = [];
        
        // 🚀 BATCH OPTIMIZATION: Concurrent validation calls instead of sequential
        console.log(`🔄 Batch validating ${batchSamples.length} ICP samples concurrently...`);
        
        const validationPromises = batchSamples.map(sample => {
          const sampleEdits = editedValues[sample.id];
          const activeCertElements = (!isBGMBatch(batchSamples) && !isZMSMBatch(batchSamples) && validationDecision === 'APPROVE')
            ? lotFinalElements
            : null;
          const supervisorEdits = sampleEdits ? Object.entries(sampleEdits)
            .filter(([element]) => shouldIncludeSupervisorEditInCert(element, activeCertElements, lotReportForm))
            .map(([element, editedValue]) => {
              const originalValue = originalValues[sample.id]?.[element] || 0;
              return {
                element,
                original_value: originalValue,
                edited_value: editedValue,
                edited_by: user?.id || '',
                edited_by_name: `${user?.firstName || ''} ${user?.lastName || ''}`,
                edited_at: new Date().toISOString(),
                edit_reason: validationNotes,
                change_magnitude: Math.abs(editedValue - originalValue),
                change_percentage: originalValue > 0 ? ((editedValue - originalValue) / originalValue) * 100 : 0
              };
            }) : [];
          
          const readings = icpGetMetadonneesReadings(sample);
          const { caO_pct, mgO_pct } = icpCaMgOxidePercent(readings, sample.id, editedValues);
          const pafFromFormula = icpPafCarbonateEstimatePercent(caO_pct, mgO_pct);
          const pafManual = sampleEdits?.['__PAF__'];
          const pafValue = pafManual !== undefined ? pafManual : pafFromFormula;

          return fetch(`/api/v1/samples/${sample.id}/validate`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              decision: validationDecision,
              notes: `${validationNotes} [Validation de lot ICP: ${getBatchNumber(selectedSample)}]`,
              validated_by: 'ICP Supervisor',
              batch_validation: true,
              batch_number: getBatchNumber(selectedSample),
              ...(showQaqcForm && {
                qaqc_data: {
                  reference_standards: qaqcStandards,
                  batch_info: {
                    analyst_name: batchSamples[0]?.metadonnees?.icp_analyst_name || 'N/A',
                    analysis_date: new Date().toISOString(),
                    batch_number: getBatchNumber(selectedSample) || 'N/A',
                    sample_count: batchSamples.length
                  }
                }
              }),
              ...(showDynamicQaqcForm && {
                qaqc_data: {
                  reference_standards: dynamicQaqcStandards,
                  ...(isRoleBasedQaqc && { role_based: true, element_meta: roleQaqcElementMeta }),
                  batch_info: {
                    analyst_name: batchSamples[0]?.metadonnees?.icp_analyst_name || 'N/A',
                    analysis_date: new Date().toISOString(),
                    batch_number: getBatchNumber(selectedSample) || 'N/A',
                    sample_count: batchSamples.length
                  }
                }
              }),
              ...(supervisorEdits.length > 0 && {
                supervisor_edits: {
                  icp: supervisorEdits,
                  validation_session_id: `validation_${Date.now()}`,
                  total_edits: supervisorEdits.length
                }
              }),
              treatment_data: {
                paf_percent: pafValue,
                paf_manual_override: pafManual !== undefined,
                paf_formula: '((CaO/56)+(MgO/40))*44',
                caO_pct_used: caO_pct,
                mgO_pct_used: mgO_pct,
                entered_by: user?.id || '',
                entered_at: new Date().toISOString(),
              },
              ...(!isBGMBatch(batchSamples) && !isZMSMBatch(batchSamples) && validationDecision === 'APPROVE' && lotFinalElements.length > 0 && {
                element_selection: {
                  selected_elements: lotFinalElements,
                  apply_to_batch: true,
                  adjustment_reason: validationNotes,
                },
                element_report_form: Object.keys(lotReportForm).length > 0 ? lotReportForm : undefined,
                supervisor_certificate_notes: certificateNotes.trim() || undefined,
              }),
            })
          }).then(response => ({ sample, response }))
          .catch(error => ({ sample, error }));
        });

        // Wait for all validation calls to complete
        const results = await Promise.all(validationPromises);
        
        // Process results
        for (const result of results) {
          const { sample, response, error } = result as any;
          
          if (error || !response) {
            errorCount++;
            errors.push(`${sample?.sample_id || '?'}: Erreur de connexion`);
            console.warn(`Validation error for ${sample?.sample_id}:`, error);
          } else if (response.ok) {
            successCount++;
            console.log(`✅ Sample ${sample.sample_id} validated successfully`);
          } else {
            errorCount++;
            let errorMessage = 'Erreur inconnue';
            
            if (response.status === 401) {
              errorMessage = 'Session expirée - Reconnectez-vous';
            } else if (response.status === 404) {
              errorMessage = 'Endpoint de validation non trouvé';
            } else {
              try {
                const errorData = await response.json();
                errorMessage = errorData.message || `Erreur ${response.status}`;
              } catch {
                errorMessage = `Erreur HTTP ${response.status}`;
              }
            }
            
            errors.push(`${sample.sample_id}: ${errorMessage}`);
            console.warn(`❌ Validation failed for ${sample.sample_id}: ${errorMessage}`);
          }
        }
        
        console.log(`✅ Batch validation completed: ${successCount} successful, ${errorCount} errors`);
        
        // Show results
        if (successCount === batchSamples.length) {
          setSnackbarMessage(`✅ Lot ICP ${getBatchNumber(selectedSample)} ${validationDecision === 'APPROVE' ? 'approuvé' : 'rejeté'} avec succès - ${successCount} échantillons ${validationDecision === 'APPROVE' ? 'envoyés en génération de certificat' : 'renvoyés en analyse'}`);
        } else if (successCount > 0) {
          setSnackbarMessage(`⚠️ Validation partielle: ${successCount}/${batchSamples.length} échantillons validés. ${errorCount} erreurs.`);
        } else {
          setSnackbarMessage(`❌ Échec de la validation du lot: ${errors.join(', ')}`);
        }
        
      } else {
        // 🆕 SUPERVISOR EDITS: Prepare edited values for individual sample
        const sampleEdits = editedValues[selectedSample.id];
        const supervisorEdits = sampleEdits ? Object.entries(sampleEdits)
          .filter(([element]) => element !== '__PAF__')
          .map(([element, editedValue]) => {
            const originalValue = originalValues[selectedSample.id]?.[element] || 0;
            return {
              element,
              original_value: originalValue,
              edited_value: editedValue,
              edited_by: user?.id || '',
              edited_by_name: `${user?.firstName || ''} ${user?.lastName || ''}`,
              edited_at: new Date().toISOString(),
              edit_reason: validationNotes,
              change_magnitude: Math.abs(editedValue - originalValue),
              change_percentage: originalValue > 0 ? ((editedValue - originalValue) / originalValue) * 100 : 0
            };
          }) : [];

        const readingsSingle = icpGetMetadonneesReadings(selectedSample);
        const { caO_pct: caO_single, mgO_pct: mgO_single } = icpCaMgOxidePercent(
          readingsSingle,
          selectedSample.id,
          editedValues,
        );
        const pafFormulaSingle = icpPafCarbonateEstimatePercent(caO_single, mgO_single);
        const pafManualSingle = sampleEdits?.['__PAF__'];
        const pafEffectiveSingle = pafManualSingle !== undefined ? pafManualSingle : pafFormulaSingle;

        // Individual sample validation
        const response = await fetch(`/api/v1/samples/${selectedSample.id}/validate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            decision: validationDecision,
            notes: validationNotes,
            validated_by: 'ICP Supervisor',
            laboratory: 'LABORATOIRE_ICP', // 🔥 CRITICAL FIX: Explicit laboratory
            treatment_data: {
              paf_percent: pafEffectiveSingle,
              paf_manual_override: pafManualSingle !== undefined,
              paf_formula: '((CaO/56)+(MgO/40))*44',
              caO_pct_used: caO_single,
              mgO_pct_used: mgO_single,
              entered_by: user?.id || '',
              entered_at: new Date().toISOString(),
            },
            ...(showQaqcForm && {
              qaqc_data: {
                reference_standards: qaqcStandards,
                batch_info: {
                  analyst_name: selectedSample.metadonnees?.icp_analyst_name || 'N/A',
                  analysis_date: new Date().toISOString(),
                  batch_number: getBatchNumber(selectedSample) || 'N/A',
                  sample_count: 1
                }
              }
            }),
            ...(showDynamicQaqcForm && {
              qaqc_data: {
                reference_standards: dynamicQaqcStandards,
                ...(isRoleBasedQaqc && { role_based: true, element_meta: roleQaqcElementMeta }),
                batch_info: {
                  analyst_name: selectedSample.metadonnees?.icp_analyst_name || 'N/A',
                  analysis_date: new Date().toISOString(),
                  batch_number: getBatchNumber(selectedSample) || 'N/A',
                  sample_count: 1
                }
              }
            }),
            // 🆕 SUPERVISOR EDITS: Include edited values with full audit trail
            ...(supervisorEdits.length > 0 && {
              supervisor_edits: {
                icp: supervisorEdits,
                validation_session_id: `validation_${Date.now()}`,
                total_edits: supervisorEdits.length
              }
            })
          })
        });

        if (response.ok) {
          const result = await response.json();
          setSnackbarMessage(`Échantillon ICP ${selectedSample.sample_id} ${validationDecision === 'APPROVE' ? 'approuvé et envoyé en génération de certificat' : 'rejeté et renvoyé en analyse'} avec succès`);
          // Debug log removed
        } else {
          let errorMessage = 'Erreur inconnue';
          
          if (response.status === 401) {
            errorMessage = 'Session expirée - Veuillez vous reconnecter';
          } else if (response.status === 404) {
            errorMessage = 'Endpoint de validation non trouvé';
          } else {
            try {
              const errorData = await response.json();
              errorMessage = errorData.message || `Erreur ${response.status}`;
            } catch {
              errorMessage = `Erreur HTTP ${response.status}`;
            }
          }
          
          // Silenced in production
          setSnackbarMessage(`Erreur lors de la validation: ${errorMessage}`);
          setSnackbarOpen(true);
          return;
        }
      }
      
      setSnackbarOpen(true);
      setValidationDialog(false);
      setSelectedSample(null);
      setValidationNotes('');
      setCertificateNotes('');
      setValidationDecision('APPROVE');
      
      // 🆕 SUPERVISOR EDITS: Clear all edits after successful validation
      clearAllEdits();
      
      // Reset BIS selections
      setFirstBISSampleId(null);
      setSecondBISSampleId(null);
      
      // Reload the validation samples list to remove the validated samples
      await loadValidationSamples();
      
    } catch (error: any) {
      // Silenced in production
      const errorMessage = error?.message || String(error) || 'Erreur de connexion';
      
      if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        setSnackbarMessage('❌ Session expirée - Veuillez vous déconnecter et vous reconnecter');
      } else {
        setSnackbarMessage(`❌ Erreur lors de la validation: ${errorMessage}`);
      }
      
      setSnackbarOpen(true);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'CRITIQUE': return 'error';
      case 'URGENTE': return 'warning';
      default: return 'primary';
    }
  };

  // Helper functions for batch processing
  const groupSamplesByBatch = (samples: SampleWithAnalyst[]) => {
    const batches: Record<string, SampleWithAnalyst[]> = {};
    const individualSamples: SampleWithAnalyst[] = [];

    samples.forEach(sample => {
      const batchInfo = sample.metadonnees?.batch_info || sample.metadonnees?.batch_analysis_summary;
      if (batchInfo && (batchInfo.is_batch_sample || batchInfo.batch_number)) {
        const batchNumber = batchInfo.batch_number;
        if (!batches[batchNumber]) {
          batches[batchNumber] = [];
        }
        batches[batchNumber].push(sample);
      } else {
        individualSamples.push(sample);
      }
    });

    return { batches, individualSamples };
  };

  const isBatchSample = (sample: SampleWithAnalyst): boolean => {
    return sample.metadonnees?.batch_info?.is_batch_sample === true || 
           sample.metadonnees?.batch_analysis_summary?.batch_number !== undefined;
  };

  const getBatchNumber = (sample: SampleWithAnalyst): string | null => {
    return sample.metadonnees?.batch_info?.batch_number || 
           sample.metadonnees?.batch_analysis_summary?.batch_number || null;
  };

  const getBatchPosition = (sample: SampleWithAnalyst): number => {
    return sample.metadonnees?.batch_info?.sample_position || 
           sample.metadonnees?.batch_analysis_summary?.sample_position || 1;
  };

  const getTotalSamplesInBatch = (sample: SampleWithAnalyst): number => {
    return sample.metadonnees?.batch_info?.total_samples || 
           sample.metadonnees?.batch_analysis_summary?.total_samples || 1;
  };

  // 🔍 MULTI-LAB VALIDATION STATUS HELPERS - SAFE VISUAL INDICATORS ONLY
  const isMultiLabBatch = (batchSamples: SampleWithAnalyst[]): boolean => {
    return batchSamples.some(sample => 
      sample.metadonnees?.multi_lab_workflow === true || 
      sample.metadonnees?.bgm_method_selected === true
    );
  };
  
  // Keep old function for backward compatibility
  const isBGMBatch = (batchSamples: SampleWithAnalyst[]): boolean => {
    return batchSamples.some(sample => 
      sample.metadonnees?.bgm_method_selected === true || 
      (sample as any).client?.company_name === 'BGM'
    );
  };

  // 🆕 Detect ZMSM lots. The role-based QA/QC feature targets non-BGM AND non-ZMSM
  // clients only; ZMSM keeps its existing dynamic QA/QC form + certificate behaviour
  // untouched. Mirrors the company_name check used elsewhere (limits / ordering).
  const isZMSMBatch = (batchSamples: SampleWithAnalyst[]): boolean => {
    return batchSamples.some(sample => (sample as any).client?.company_name === 'ZMSM');
  };

  /** Stored ppm for column-max scan (supervisor validation table only). */
  const getSampleStoredPpmForColumn = (sample: any, columnSymbol: string, reportForm: SupervisorReportFormMap): number => {
    const { dataKey } = resolveReportFormColumn(columnSymbol, reportForm);
    const edited = editedValues[sample.id]?.[dataKey] ?? editedValues[sample.id]?.[columnSymbol];
    if (edited !== undefined) return Number(edited) || 0;
    const sources = [
      sample.metadonnees?.icp_analysis_form?.analysis_readings,
      sample.metadonnees?.icp_analysis_data?.analysis_readings,
      sample.metadonnees?.analysis_results,
    ];
    for (const source of sources) {
      if (!source || !Array.isArray(source)) continue;
      const reading = source.find((r: any) => r.element === dataKey || r.element === columnSymbol);
      if (reading) return Number(reading.concentration_ppm) || 0;
    }
    return 0;
  };

  /** If any sample > 10000 ppm for a trace element, show whole column in %. BGM/ZMSM excluded. */
  const buildTraceColumnPercentMap = (
    batchSamples: any[],
    elements: string[],
    reportForm: SupervisorReportFormMap,
  ): Record<string, boolean> => {
    if (isBGMBatch(batchSamples) || isZMSMBatch(batchSamples) || batchSamples.length === 0) return {};
    const map: Record<string, boolean> = {};
    elements.forEach((element) => {
      const limit = getMethodDetectionLimit(resolveElementMethodCode(batchSamples[0], element), element);
      let maxStored = 0;
      batchSamples.forEach((sample) => {
        const ppm = getSampleStoredPpmForColumn(sample, element, reportForm);
        if (ppm > maxStored) maxStored = ppm;
      });
      if (shouldUseTracePercentColumn(limit, maxStored)) map[element] = true;
    });
    return map;
  };

  const validationTraceColumnPercentMap = useMemo(() => {
    if (!validationDialog) return {} as Record<string, boolean>;
    const batchSamples = (selectedSample as any)?.batchSamples || [];
    if (batchSamples.length === 0) return {};
    const isBGM = batchSamples[0]?.metadonnees?.bgm_method_selected === true || batchSamples[0]?.client?.company_name === 'BGM';
    const elements = isBGM ? getAllBatchElements(batchSamples, true) : getBatchCertificateElements(batchSamples);
    return buildTraceColumnPercentMap(batchSamples, elements, lotReportForm);
  }, [validationDialog, selectedSample, editedValues, lotReportForm, lotFinalElements]);

  const getBGMValidationStatus = (batchSamples: SampleWithAnalyst[]) => {
    // 🔥 FIX: Use isMultiLabBatch to include custom multi-lab samples, not just BGM
    if (!isMultiLabBatch(batchSamples)) {
      return { isBGM: false, icpCompleted: false, auCompleted: false, awaitingAU: false };
    }

    // Check validation completion status across all samples in batch
    const icpCompleted = batchSamples.some(sample => 
      sample.metadonnees?.icp_validation_completed === true
    );
    
    const auCompleted = batchSamples.some(sample => 
      sample.metadonnees?.au_validation_completed === true ||
      sample.metadonnees?.au_validation?.validation_decision === 'APPROVED'
    );

    return {
      isBGM: true,
      icpCompleted,
      auCompleted,
      awaitingAU: icpCompleted && !auCompleted
    };
  };

  const handleValidateBatch = (batchSamples: SampleWithAnalyst[]) => {
    // For batch validation, we'll set the first sample as selectedSample
    // but we'll also store the entire batch for display
    if (batchSamples.length > 0) {
      setSelectedSample({
        ...batchSamples[0],
        // Add batch context to the sample
        batchSamples: batchSamples,
        isBatchValidation: true
      } as any);
      setValidationDialog(true);
      setValidationDecision('APPROVE');
      setValidationNotes('');
      // Prevent QA/QC state leaking from a previous batch into this new one (cross-contamination fix)
      setQaqcStandards([]);
      setShowQaqcForm(false);
      setDynamicQaqcStandards([]);
      setDynamicQaqcElements([]);
      setShowDynamicQaqcForm(false);
      setIsRoleBasedQaqc(false);
      const initialLotElements = getAllBatchElements(batchSamples, false);
      setLotInitialElements(initialLotElements);
      setLotFinalElements(initialLotElements);
      setLotElementQaqcStale(false);
      const existingReportForm = batchSamples[0]?.metadonnees?.supervisor_report_form;
      setLotReportForm(
        existingReportForm && typeof existingReportForm === 'object' && !Array.isArray(existingReportForm)
          ? { ...existingReportForm }
          : {},
      );
    }
  };

  const toggleBatchExpansion = (batchNumber: string) => {
    const newExpandedBatches = new Set(expandedBatches);
    if (newExpandedBatches.has(batchNumber)) {
      newExpandedBatches.delete(batchNumber);
    } else {
      newExpandedBatches.add(batchNumber);
    }
    setExpandedBatches(newExpandedBatches);
  };

  const getQCStatusColor = (status: string) => {
    switch (status) {
      case 'PASSED':
        return 'success';
      case 'WARNING':
        return 'warning';
      case 'FAILED':
        return 'error';
      default:
        return 'default';
    }
  };

  const loadSupervisorData = async () => {
    await loadDashboardData();
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
          borderRadius: 3,
        }}
      >
        <Box textAlign="center">
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
            Chargement du tableau de bord superviseur ICP...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', bgcolor: 'background.default', minHeight: '100vh' }}>
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={(_, newValue) => setCurrentTab(newValue)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<Dashboard />} label="Vue d'Ensemble ICP" />
          <Tab icon={<People />} label="Performance Équipe" />
          <Tab icon={<Assessment />} label="Validation Échantillons" />
        </Tabs>
      </Paper>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 3 }} 
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Vue d'Ensemble ICP Tab */}
      {currentTab === 0 && (
        <Box sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
            🎛️ Supervision Laboratoire ICP
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Bonjour, {user?.firstName} - Superviseur ICP • Configuration méthodes • Validation lots • Contrôle qualité
          </Typography>

          {/* Enhanced Stats Dashboard with Premium Styling */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {[
              {
                icon: Assignment,
                value: stats.pending_validation,
                label: 'En attente validation',
                color: theme.palette.warning.main,
                emoji: '⏳',
                gradient: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)'
              },
              {
                icon: CheckCircle,
                value: stats.validated_today,
                label: 'Validés aujourd\'hui',
                color: theme.palette.success.main,
                emoji: '✅',
                gradient: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
              },
              {
                icon: Error,
                value: stats.rejected_today,
                label: 'Rejetés aujourd\'hui',
                color: theme.palette.error.main,
                emoji: '❌',
                gradient: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)'
              },
              {
                icon: Timer,
                value: stats.overdue_validation,
                label: 'Validations en retard',
                color: theme.palette.error.main,
                emoji: '🚨',
                gradient: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)'
              },
              {
                icon: Science,
                value: stats.total_in_lab,
                label: 'Total en laboratoire',
                color: theme.palette.info.main,
                emoji: '🔬',
                gradient: 'linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)'
              },
              {
                icon: Speed,
                value: `${stats.quality_pass_rate.toFixed(1)}%`,
                label: 'Taux de réussite QC',
                color: theme.palette.success.main,
                emoji: '🎯',
                gradient: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)'
              },
            ].map((stat, index) => (
              <Grid item xs={12} sm={6} md={2} key={index}>
                <Card 
                  sx={{ 
                    height: '100%',
                    borderRadius: 4,
                    background: `linear-gradient(135deg, ${alpha(stat.color, 0.1)} 0%, ${alpha(stat.color, 0.05)} 100%)`,
                    borderLeft: `6px solid ${stat.color}`,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': { 
                      boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                      transform: 'translateY(-4px)',
                      background: `linear-gradient(135deg, ${alpha(stat.color, 0.15)} 0%, ${alpha(stat.color, 0.08)} 100%)`,
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '50px',
                      height: '50px',
                      background: `linear-gradient(135deg, ${alpha(stat.color, 0.2)}, transparent)`,
                      borderRadius: '0 0 0 50px',
                    }
                  }}
                >
                  <CardContent sx={{ p: 3, textAlign: 'center', position: 'relative' }}>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        mb: 2,
                        position: 'relative'
                      }}
                    >
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          background: stat.gradient,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: `0 4px 16px ${alpha(stat.color, 0.3)}`,
                          position: 'relative',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            inset: 2,
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(10px)',
                          }
                        }}
                      >
                        <Typography 
                          sx={{ 
                            fontSize: '1.5rem',
                            position: 'relative',
                            zIndex: 1
                          }}
                        >
                          {stat.emoji}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Typography 
                      variant="h3" 
                      sx={{ 
                        fontWeight: 800,
                        background: stat.gradient,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
                        mb: 1,
                        lineHeight: 1.2
                      }}
                    >
                      {stat.value}
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.text.secondary,
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Enhanced Instrument Status */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card 
                sx={{ 
                  borderRadius: 4,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`,
                  borderLeft: `6px solid ${theme.palette.success.main}`,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': { 
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)',
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.15)} 0%, ${alpha(theme.palette.success.light, 0.08)} 100%)`,
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '60px',
                    height: '60px',
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.2)}, transparent)`,
                    borderRadius: '0 0 0 60px',
                  }
                }}
              >
                <CardContent sx={{ p: 3, position: 'relative' }}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 4px 16px ${alpha(theme.palette.success.main, 0.3)}`,
                      }}
                    >
                      <Typography sx={{ fontSize: '1.5rem' }}>📊</Typography>
                    </Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700,
                        background: 'linear-gradient(45deg, #4caf50, #66bb6a)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Statut Instrument ICP
                    </Typography>
                  </Box>
                  
                  <Alert 
                    severity="success" 
                    sx={{ 
                      mb: 2,
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`,
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                      '& .MuiAlert-icon': {
                        fontSize: '1.5rem'
                      }
                    }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>
                      🟢 Instrument ICP opérationnel - Uptime: {stats.instrument_uptime}%
                    </Typography>
                  </Alert>
                  
                  <Box sx={{ mt: 2, p: 2, borderRadius: 2, background: alpha(theme.palette.grey[100], 0.5) }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                      ⚡ Plasma stable • RF Power: 1.2 kW • Température: 23°C • Humidité: 45%
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      🔧 Dernière maintenance: Il y a 3 jours • Prochaine calibration: Dans 2 jours
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  borderRadius: 4,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.light, 0.05)} 100%)`,
                  borderLeft: `6px solid ${theme.palette.info.main}`,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': { 
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)',
                    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.15)} 0%, ${alpha(theme.palette.info.light, 0.08)} 100%)`,
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '50px',
                    height: '50px',
                    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.2)}, transparent)`,
                    borderRadius: '0 0 0 50px',
                  }
                }}
              >
                <CardContent sx={{ p: 3, position: 'relative' }}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 4px 16px ${alpha(theme.palette.info.main, 0.3)}`,
                      }}
                    >
                      <Typography sx={{ fontSize: '1.2rem' }}>⚡</Typography>
                    </Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700,
                        background: 'linear-gradient(45deg, #2196f3, #42a5f5)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Performance Équipe
                    </Typography>
                  </Box>
                  
                  {Object.entries(performanceMetrics.analyst_efficiency).length > 0 ? (
                    Object.entries(performanceMetrics.analyst_efficiency).map(([analyst, count]) => (
                      <Box 
                        key={analyst} 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          mb: 1,
                          p: 1.5,
                          borderRadius: 2,
                          background: alpha(theme.palette.info.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            background: alpha(theme.palette.info.main, 0.1),
                            transform: 'translateX(4px)'
                          }
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>👨‍🔬 {analyst}</Typography>
                        <Typography variant="body2" fontWeight={700} color="info.main">
                          {count} échantillons
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Box 
                      sx={{ 
                        p: 2, 
                        textAlign: 'center',
                        borderRadius: 2,
                        background: alpha(theme.palette.grey[400], 0.1),
                        border: `1px dashed ${alpha(theme.palette.grey[400], 0.3)}`
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        📊 Aucune donnée de performance disponible
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ mt: 2, p: 2, borderRadius: 2, background: alpha(theme.palette.grey[100], 0.5) }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      ⏱️ Délai moyen: <span style={{ color: theme.palette.info.main, fontWeight: 700 }}>
                        {performanceMetrics.average_turnaround_time}h
                      </span>
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Performance Équipe Tab */}
      {currentTab === 1 && (
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            <People sx={{ mr: 1, verticalAlign: 'middle' }} />
            Performance de l'Équipe ICP
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 4 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    📈 Productivité Analystes
                  </Typography>
                  
                  {Object.entries(performanceMetrics.analyst_efficiency).length > 0 ? (
                    Object.entries(performanceMetrics.analyst_efficiency).map(([analyst, efficiency]) => (
                      <Box key={analyst} sx={{ mb: 2 }}>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body1">{analyst}</Typography>
                          <Typography variant="body1" fontWeight={600}>{efficiency} éch/jour</Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={(efficiency / 20) * 100} 
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Aucune donnée de performance disponible
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 4 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    🎯 Métriques Qualité
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Taux de réussite QC</Typography>
                    <Typography variant="h5" color="success.main" fontWeight={700}>
                      {performanceMetrics.quality_pass_rate.toFixed(1)}%
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Uptime Instrument</Typography>
                    <Typography variant="h5" color="info.main" fontWeight={700}>
                      {performanceMetrics.instrument_uptime}%
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">Échantillons traités</Typography>
                    <Typography variant="h5" color="primary.main" fontWeight={700}>
                      {performanceMetrics.total_samples_processed}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Validation Échantillons Tab */}
      {currentTab === 2 && (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6">
              <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
              Validation des Échantillons ICP
              {totalSamples > 0 && (
                <Chip 
                  label={`${totalSamples} total • ${batchCount} lots`} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ ml: 2 }}
                />
              )}
            </Typography>
            
            {/* 🔍 SEARCH BAR */}
            <TextField
              size="small"
              placeholder="Rechercher par ID ou N° Lot..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ 
                width: { xs: '100%', sm: 300 },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {Array.isArray(samples) && samples.length > 0 ? (
            <Box>
              {(() => {
                const { batches, individualSamples } = groupSamplesByBatch(samples);
                const hasBatches = Object.keys(batches).length > 0;
                const hasIndividual = individualSamples.length > 0;

                return (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>N° Échantillon / Lot</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Client</TableCell>
                          <TableCell>Priorité</TableCell>
                          <TableCell>Analyste</TableCell>
                          <TableCell>Date Analyse</TableCell>
                          <TableCell>Statut</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Batch rows */}
                        {Object.entries(batches).map(([batchNumber, batchSamples]) => {
                          const isExpanded = expandedBatches.has(batchNumber);
                          
                          return (
                            <React.Fragment key={batchNumber}>
                              {/* Batch Header Row - Enhanced Professional Styling */}
                              <TableRow 
                                sx={{ 
                                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.secondary.main, 0.15)} 100%)`,
                                  cursor: 'pointer',
                                  borderLeft: `6px solid ${theme.palette.primary.main}`,
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                  transition: 'all 0.3s ease',
                                  '&:hover': { 
                                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.25)} 0%, ${alpha(theme.palette.secondary.main, 0.25)} 100%)`,
                                    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                                    transform: 'translateY(-1px)'
                                  }
                                }}
                                onClick={() => toggleBatchExpansion(batchNumber)}
                              >
                                <TableCell colSpan={8} sx={{ py: 2 }}>
                                  <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box display="flex" alignItems="center" gap={2}>
                                      <IconButton 
                                        size="small" 
                                        sx={{ 
                                          p: 1,
                                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.2) },
                                          borderRadius: 2
                                        }}
                                      >
                                        {isExpanded ? <ExpandLess color="primary" /> : <ExpandMoreIcon color="primary" />}
                                      </IconButton>
                                      
                                      <Chip 
                                        label="🔬 LOT ICP VALIDATION" 
                                        sx={{ 
                                          fontWeight: 'bold',
                                          fontSize: '0.9rem',
                                          px: 2,
                                          py: 1,
                                          background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                          color: 'white',
                                          boxShadow: '0 3px 12px rgba(25, 118, 210, 0.4)',
                                          '&:hover': {
                                            boxShadow: '0 6px 20px rgba(25, 118, 210, 0.6)',
                                          }
                                        }}
                                      />
                                      
                                      {/* 🔍 BGM STATUS INDICATOR - VISUAL ONLY */}
                                      {(() => {
                                        const bgmStatus = getBGMValidationStatus(batchSamples);
                                        if (bgmStatus.isBGM && bgmStatus.awaitingAU) {
                                          return (
                                            <Chip 
                                              label="⏳ EN ATTENTE DE VALIDATION AU" 
                                              sx={{ 
                                                fontWeight: 'bold',
                                                fontSize: '0.8rem',
                                                px: 1.5,
                                                py: 0.5,
                                                background: `linear-gradient(45deg, #ff9800, #ffc107)`,
                                                color: 'white',
                                                boxShadow: '0 2px 8px rgba(255, 152, 0, 0.4)',
                                                animation: 'pulse 2s infinite',
                                                '@keyframes pulse': {
                                                  '0%': { opacity: 1 },
                                                  '50%': { opacity: 0.7 },
                                                  '100%': { opacity: 1 }
                                                }
                                              }}
                                            />
                                          );
                                        } else if (bgmStatus.isBGM && bgmStatus.auCompleted) {
                                          return (
                                            <Chip 
                                              label="✅ BGM COMPLET (ICP + AU)" 
                                              sx={{ 
                                                fontWeight: 'bold',
                                                fontSize: '0.8rem',
                                                px: 1.5,
                                                py: 0.5,
                                                background: `linear-gradient(45deg, #4caf50, #8bc34a)`,
                                                color: 'white',
                                                boxShadow: '0 2px 8px rgba(76, 175, 80, 0.4)',
                                              }}
                                            />
                                          );
                                        } else if (bgmStatus.isBGM) {
                                          return (
                                            <Chip 
                                              label="🔬 ÉCHANTILLON BGM" 
                                              sx={{ 
                                                fontWeight: 'bold',
                                                fontSize: '0.8rem',
                                                px: 1.5,
                                                py: 0.5,
                                                background: `linear-gradient(45deg, #9c27b0, #e91e63)`,
                                                color: 'white',
                                                boxShadow: '0 2px 8px rgba(156, 39, 176, 0.4)',
                                              }}
                                            />
                                          );
                                        }
                                        return null;
                                      })()}
                                      
                                      <Typography 
                                        variant="h5" 
                                        sx={{ 
                                          fontWeight: 800,
                                          background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                          backgroundClip: 'text',
                                          WebkitBackgroundClip: 'text',
                                          WebkitTextFillColor: 'transparent',
                                          textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
                                        }}
                                      >
                                        {batchNumber}
                                      </Typography>
                                      
                                      <Chip 
                                        label={`📊 ${batchSamples.length} échantillons`}
                                        sx={{ 
                                          backgroundColor: alpha(theme.palette.info.main, 0.15),
                                          color: theme.palette.info.main,
                                          fontWeight: 600,
                                          border: `2px solid ${alpha(theme.palette.info.main, 0.3)}`,
                                          fontSize: '0.85rem'
                                        }}
                                      />
                                      
                                      <Chip 
                                        label={`⚡ ${batchSamples[0]?.metadonnees?.icp_analyst_name || 'ICP Analyst'}`}
                                        sx={{ 
                                          backgroundColor: alpha(theme.palette.success.main, 0.15),
                                          color: theme.palette.success.main,
                                          fontWeight: 600,
                                          border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
                                          fontSize: '0.85rem'
                                        }}
                                      />
                                    </Box>
                                    
                                    <Button
                                      variant="contained"
                                      size="large"
                                      startIcon={<CheckCircle />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleValidateBatch(batchSamples);
                                      }}
                                      sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: '1rem',
                                        px: 3,
                                        py: 1.5,
                                        borderRadius: 3,
                                        background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
                                        boxShadow: '0 4px 16px rgba(76, 175, 80, 0.4)',
                                        textTransform: 'none',
                                        '&:hover': { 
                                          background: `linear-gradient(45deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                                          boxShadow: '0 6px 24px rgba(76, 175, 80, 0.6)',
                                          transform: 'translateY(-2px)'
                                        },
                                        transition: 'all 0.3s ease'
                                      }}
                                    >
                                      🚀 Valider le Lot
                                    </Button>
                                  </Box>
                                </TableCell>
                              </TableRow>

                              {/* Batch Sample Rows - Enhanced Styling */}
                              {isExpanded && batchSamples.map((sample, index) => (
                                <TableRow 
                                  key={sample.id} 
                                  sx={{ 
                                    background: index % 2 === 0 
                                      ? `linear-gradient(90deg, ${alpha(theme.palette.grey[50], 0.8)} 0%, ${alpha(theme.palette.grey[100], 0.6)} 100%)`
                                      : `linear-gradient(90deg, ${alpha(theme.palette.info.light, 0.4)} 0%, ${alpha(theme.palette.info.light, 0.2)} 100%)`,
                                    borderLeft: `4px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
                                      borderLeft: `4px solid ${theme.palette.primary.main}`,
                                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                    }
                                  }}
                                >
                                  <TableCell sx={{ pl: 6 }}>
                                    <Box display="flex" alignItems="center" gap={1.5}>
                                      <Box 
                                        sx={{ 
                                          width: 8, 
                                          height: 8, 
                                          borderRadius: '50%', 
                                          backgroundColor: theme.palette.primary.main,
                                          boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.5)}`
                                        }} 
                                      />
                                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Position {getBatchPosition(sample)}:
                                      </Typography>
                                      <Typography 
                                        sx={{ 
                                          fontWeight: 700, 
                                          color: theme.palette.primary.main,
                                          fontSize: '0.95rem'
                                        }}
                                      >
                                        {sample.sample_id}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={sample.type_echantillon} 
                                      size="small" 
                                      sx={{ 
                                        backgroundColor: alpha(theme.palette.grey[600], 0.1),
                                        color: theme.palette.grey[700],
                                        fontWeight: 500
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                      {sample.client?.company_name}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={sample.priorite} 
                                      size="small" 
                                      color={getPriorityColor(sample.priorite)}
                                      sx={{ 
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        boxShadow: 1
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Box 
                                        sx={{ 
                                          width: 6, 
                                          height: 6, 
                                          borderRadius: '50%', 
                                          backgroundColor: theme.palette.success.main 
                                        }} 
                                      />
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {sample.analyst_name || sample.metadonnees?.icp_analyst_name || 'ICP Analyst'}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                      {new Date(sample.date_modification).toLocaleDateString('fr-FR')}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={sample.statut} 
                                      size="small" 
                                      sx={{ 
                                        backgroundColor: alpha(theme.palette.info.main, 0.15),
                                        color: theme.palette.info.main,
                                        fontWeight: 600,
                                        border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      startIcon={<Visibility />}
                                      onClick={() => handleValidateSample(sample)}
                                      sx={{ 
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderColor: theme.palette.primary.main,
                                        '&:hover': {
                                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                          borderColor: theme.palette.primary.dark,
                                          boxShadow: 2
                                        }
                                      }}
                                    >
                                      Voir
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </React.Fragment>
                          );
                        })}

                        {/* Individual samples - Enhanced Styling */}
                        {individualSamples.map((sample, index) => (
                          <TableRow 
                            key={sample.id}
                            sx={{
                              background: `linear-gradient(90deg, ${alpha(theme.palette.grey[50], 0.9)} 0%, ${alpha(theme.palette.grey[100], 0.7)} 100%)`,
                              borderLeft: `4px solid ${alpha(theme.palette.warning.main, 0.6)}`,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                background: `linear-gradient(90deg, ${alpha(theme.palette.warning.main, 0.08)} 0%, ${alpha(theme.palette.warning.light, 0.12)} 100%)`,
                                borderLeft: `4px solid ${theme.palette.warning.main}`,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                              }
                            }}
                          >
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1.5}>
                                <Box 
                                  sx={{ 
                                    width: 8, 
                                    height: 8, 
                                    borderRadius: '50%', 
                                    backgroundColor: theme.palette.warning.main,
                                    boxShadow: `0 0 8px ${alpha(theme.palette.warning.main, 0.5)}`
                                  }} 
                                />
                                <Typography 
                                  sx={{ 
                                    fontWeight: 700, 
                                    color: theme.palette.warning.dark,
                                    fontSize: '0.95rem'
                                  }}
                                >
                                  {sample.sample_id}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={sample.type_echantillon} 
                                size="small" 
                                sx={{ 
                                  backgroundColor: alpha(theme.palette.grey[600], 0.1),
                                  color: theme.palette.grey[700],
                                  fontWeight: 500
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                {sample.client?.company_name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={sample.priorite} 
                                size="small" 
                                color={getPriorityColor(sample.priorite)}
                                sx={{ 
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  boxShadow: 1
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Box 
                                  sx={{ 
                                    width: 6, 
                                    height: 6, 
                                    borderRadius: '50%', 
                                    backgroundColor: theme.palette.success.main 
                                  }} 
                                />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {sample.analyst_name || sample.metadonnees?.icp_analyst_name || 'ICP Analyst'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {new Date(sample.date_modification).toLocaleDateString('fr-FR')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={sample.statut} 
                                size="small" 
                                sx={{ 
                                  backgroundColor: alpha(theme.palette.info.main, 0.15),
                                  color: theme.palette.info.main,
                                  fontWeight: 600,
                                  border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<CheckCircle />}
                                onClick={() => handleValidateSample(sample)}
                                sx={{ 
                                  borderRadius: 2,
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                                  boxShadow: 2,
                                  '&:hover': {
                                    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                    boxShadow: 4,
                                    transform: 'translateY(-1px)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                Valider
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              })()}
              
              {/* 📄 PAGINATION CONTROLS */}
              <TablePagination
                component="div"
                count={totalSamples}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[50, 100, 250, 500]}
                labelRowsPerPage="Échantillons par page:"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} sur ${count} échantillons (${batchCount} lots sur cette page)`
                }
                sx={{
                  borderTop: '1px solid #e0e0e0',
                  mt: 2,
                  '& .MuiTablePagination-toolbar': {
                    backgroundColor: '#f5f5f5',
                  }
                }}
              />
            </Box>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              Aucun échantillon en attente de validation ICP
            </Alert>
          )}
        </Box>
      )}


      {/* Oxide ↔ Element report form dialog (non-BGM / non-ZMSM only) */}
      <Dialog open={reportFormDialogOpen} onClose={() => setReportFormDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Forme de report — certificat</DialogTitle>
        <DialogContent>
          {reportFormDialogSymbol && (() => {
            const pair = getIcpConversionPair(reportFormDialogSymbol);
            if (!pair) return null;
            return (
              <Box sx={{ pt: 1 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Colonne <strong>{reportFormDialogSymbol}</strong> — choisissez la forme sur le certificat final.
                  La lecture brute en base n&apos;est pas modifiée.
                </Typography>
                <RadioGroup
                  value={reportFormDialogMode}
                  onChange={(e) => setReportFormDialogMode(e.target.value as 'oxide' | 'element')}
                >
                  <MuiFormControlLabel
                    value="oxide"
                    control={<Radio size="small" />}
                    label={`Oxyde (${pair.oxide}) — valeur standard`}
                  />
                  <MuiFormControlLabel
                    value="element"
                    control={<Radio size="small" />}
                    label={`Élément (${pair.element}) = ${pair.oxide} ÷ ${pair.factor}`}
                  />
                </RadioGroup>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportFormDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={applyReportFormChoice}>Confirmer</Button>
        </DialogActions>
      </Dialog>

      {/* Validation Dialog - Enhanced like SAA */}
      <Dialog 
        open={validationDialog} 
        onClose={() => {
          setValidationDialog(false);
          setFirstBISSampleId(null);
          setSecondBISSampleId(null);
          clearAllEdits(); // 🆕 Clear edits on dialog close
        }} 
        maxWidth="lg" 
        fullWidth
        fullScreen
      >
        <DialogTitle sx={{ 
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <VerifiedUser color="primary" sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                🔬 Validation ICP - {(selectedSample as any)?.isBatchValidation ? 'Lot' : 'Échantillon'} 
                {(selectedSample as any)?.isBatchValidation ? (selectedSample ? getBatchNumber(selectedSample) : '') : selectedSample?.sample_id}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {(selectedSample as any)?.isBatchValidation 
                  ? `${(selectedSample as any)?.batchSamples?.length || 0} échantillons dans le lot`
                  : 'Validation individuelle'
                } • Laboratoire ICP • Superviseur: {user?.firstName} {user?.lastName}
              </Typography>
              
              {/* 🔍 BGM STATUS INFO IN DIALOG - VISUAL ONLY */}
              {(selectedSample as any)?.isBatchValidation && (() => {
                const bgmStatus = getBGMValidationStatus((selectedSample as any)?.batchSamples || []);
                if (bgmStatus.isBGM) {
                  return (
                    <Typography variant="body2" sx={{ 
                      mt: 1, 
                      p: 1, 
                      borderRadius: 1,
                      backgroundColor: bgmStatus.awaitingAU ? alpha('#ff9800', 0.1) : alpha('#4caf50', 0.1),
                      color: bgmStatus.awaitingAU ? '#e65100' : '#2e7d32',
                      fontWeight: 'bold'
                    }}>
                      🔬 <strong>Échantillon Multi-Lab:</strong> {
                        bgmStatus.awaitingAU 
                          ? '⏳ Validation ICP terminée - En attente de validation AU pour génération de certificat'
                          : bgmStatus.auCompleted 
                            ? '✅ Validations ICP et AU terminées - Prêt pour certificat'
                            : '🔄 En cours de validation ICP'
                      }
                    </Typography>
                  );
                }
                return null;
              })()}
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
          {selectedSample && (
            <Box sx={{ mt: 1 }}>
              {(selectedSample as any)?.isBatchValidation ? (
                /* Batch Validation View - Enhanced with Complete Information */
                <Box>
                  {/* Comprehensive Batch Information Section */}
                  <Card sx={{ mb: 2, backgroundColor: '#e8f5e8', border: '2px solid #4caf50' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#2e7d32', mb: 2 }}>
                        📊 Récapitulatif Complet du Lot ICP {selectedSample ? getBatchNumber(selectedSample) : 'N/A'}
                      </Typography>
                      
                      <Grid container spacing={2}>
                        {/* Batch Summary */}
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Échantillons</Typography>
                            <Typography variant="h6" color="primary" sx={{ fontSize: '1.2rem' }}>
                              {(selectedSample as any)?.batchSamples?.length || 0}
                            </Typography>
                          </Card>
                        </Grid>
                        
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Analyste ICP</Typography>
                            <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                              {(selectedSample as any)?.batchSamples?.[0]?.metadonnees?.icp_analyst_name || 'N/A'}
                            </Typography>
                          </Card>
                        </Grid>
                        
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Éléments Analysés</Typography>
                            <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                              {(() => {
                                const sample = (selectedSample as any)?.batchSamples?.[0];
                                // 🔧 FIX: Use getSampleElements with includeAuForBGM for validation modal
                                const bs = (selectedSample as any)?.batchSamples || [];
                                if (lotFinalElements.length > 0 && !isBGMBatch(bs) && !isZMSMBatch(bs)) {
                                  return lotFinalElements.length;
                                }
                                const elements = sample ? getSampleElements(sample, true) : [];
                                return elements.length || 0;
                              })()}
                            </Typography>
                          </Card>
                        </Grid>
                        
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Date Analyse</Typography>
                            <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                              {(() => {
                                const analysisDate = (selectedSample as any)?.batchSamples?.[0]?.metadonnees?.icp_analysis_completed_at ||
                                                    (selectedSample as any)?.batchSamples?.[0]?.date_fin_analyse;
                                return analysisDate ? new Date(analysisDate).toLocaleDateString('fr-FR') : 'N/A';
                              })()}
                            </Typography>
                          </Card>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                  
                  {/* LOT-LEVEL CERTIFICATE ELEMENT SELECTION (non-BGM / non-ZMSM) */}
                  {!isBGMBatch((selectedSample as any)?.batchSamples || []) &&
                   !isZMSMBatch((selectedSample as any)?.batchSamples || []) &&
                   validationDecision === 'APPROVE' &&
                   lotInitialElements.length > 0 && (
                    <Card sx={{ mb: 2, backgroundColor: alpha('#fff3e0', 0.4), border: '2px solid #ff9800' }}>
                      <CardContent sx={{ p: 2 }}>
                        <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 'bold', color: '#e65100', mb: 1 }}>
                          🎯 Éléments pour certificat final
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                          Sélectionnés: <strong>{lotFinalElements.length}</strong> / {lotInitialElements.length}
                          {' '}— Les éléments désélectionnés restent archivés dans les données ICP.
                        </Typography>
                        {batchHasHeterogeneousElements((selectedSample as any)?.batchSamples || []) && (
                          <Alert severity="warning" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
                            Ce lot contient des listes d'éléments différentes à la réception.
                            La validation appliquera la même liste finale à tous les échantillons.
                          </Alert>
                        )}
                        {lotElementQaqcStale && (
                          <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
                            Les éléments ont changé — rechargez le template QA/QC avant validation.
                          </Alert>
                        )}
                        <Box display="flex" gap={1} flexWrap="wrap" mb={1.5}>
                          {lotInitialElements.map((element: string) => {
                            const selected = lotFinalElements.includes(element);
                            return (
                              <Chip
                                key={`lot-el-${element}`}
                                label={element}
                                size="small"
                                clickable
                                color={selected ? 'primary' : 'default'}
                                variant={selected ? 'filled' : 'outlined'}
                                onClick={() => toggleLotCertificateElement(element)}
                                sx={{ fontSize: '0.72rem', opacity: selected ? 1 : 0.55 }}
                              />
                            );
                          })}
                        </Box>
                        <Box display="flex" gap={1}>
                          <Button size="small" variant="outlined" onClick={() => {
                            setLotFinalElements([...lotInitialElements]);
                            invalidateLotQaqcOnElementChange();
                          }} sx={{ fontSize: '0.72rem' }}>
                            Tout sélectionner
                          </Button>
                          <Button size="small" variant="outlined" onClick={resetLotCertificateElements} sx={{ fontSize: '0.72rem' }}>
                            Réinitialiser
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  )}

                  {/* COMPREHENSIVE ANALYSIS + PREPARATION TABLE */}
                  <Card sx={{ mb: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#1976d2', mb: 2 }}>
                        📊 Tableau Complet: Préparation Chimique + Résultats d'Analyse ICP
                      </Typography>
                      
                      {/* Elements analyzed overview */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', fontWeight: 'bold', mb: 1 }}>
                          Éléments analysés:
                        </Typography>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {(() => {
                            const sample = (selectedSample as any)?.batchSamples?.[0];
                            const isBGM = sample?.metadonnees?.bgm_method_selected === true || sample?.metadonnees?.multi_lab_workflow === true;
                            // 🔧 FIX: Use getSampleElements with includeAuForBGM for validation modal
                            const elements = sample
                              ? (lotFinalElements.length > 0 ? getBatchCertificateElements((selectedSample as any)?.batchSamples || []) : getSampleElements(sample, true))
                              : [];
                            return elements.map((element: string) => (
                              <Chip 
                                key={element} 
                                label={element} 
                                size="small"
                                color={element === 'Au' && isBGM ? 'warning' : 'primary'} 
                                variant="outlined"
                                title={element === 'Au' && isBGM ? '⚖️ Valeur du laboratoire AU (pour comparaison)' : undefined}
                                sx={{ fontSize: '0.7rem', height: '24px' }}
                              />
                            ));
                          })()}
                        </Box>
                      </Box>
                      
                      {/* COMPREHENSIVE MATRIX TABLE - PREPARATION + ANALYSIS */}
                      <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              {/* Sample and Preparation Data Columns */}
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: 120 }}>Échantillon</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: 200 }}>Client / Référence</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e8', minWidth: 100 }}>Poids Initial (g)</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e8', minWidth: 120 }}>Méthode Digestion</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e8', minWidth: 100 }}>Volume Final (mL)</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e8', minWidth: 100 }}>Facteur Dilution</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e8', minWidth: 120 }}>Technicien</TableCell>
                              
                              {/* ALL ELEMENTS COLUMNS WITH METHOD-UNIT HEADER */}
                              {/* 🔧 FIX: Use ALL elements from batch (union) with AU for BGM samples */}
                              {(() => {
                                const batchSamples = (selectedSample as any)?.batchSamples || [];
                                // 🆕 Include AU for BGM samples in validation modal
                                const sample = batchSamples[0];
                                const isBGM = sample?.metadonnees?.bgm_method_selected === true || (sample as any)?.client?.company_name === 'BGM';
                                const elements = isBGM ? getAllBatchElements(batchSamples, true) : getBatchCertificateElements(batchSamples);
                                // 🔧 FIX (ZMSM): Mirror backend certificates.service rule
                                //   useBGMLimits = bgm_method_selected || client.company_name === 'ZMSM'
                                // so the supervisor dashboard shows the same detection limits as the
                                // generated certificate. Multi-lab (non-BGM) lots also use BGM-style
                                // limits because that's how AU-from-AU-lab is merged with ICP elements.
                                // Safety: BGM lots already use loadDefaultQAQCTemplate (hardcoded header)
                                // — this only affects the per-cell DL lookup, not the QC template.
                                const canEditReportForm = !isBGM && !isZMSMBatch(batchSamples);
                                return elements.map((element: string) => {
                                  const limit = getMethodDetectionLimit(resolveElementMethodCode(sample, element), element);
                                  const unitLabel = validationTraceColumnPercentMap[element] ? '%' : (limit?.unit || 'ppm');
                                  const isAuFromAULab = element === 'Au' && isBGM;
                                  const reportEntry = lotReportForm[element] || Object.values(lotReportForm).find(e => e.reportAs === element);
                                  const isElementReport = reportEntry?.mode === 'element';
                                  const convertible = canEditReportForm && isReportFormConvertible(element);
                                  return (
                                    <TableCell
                                      key={`${element}-ppm`}
                                      onClick={convertible ? () => openReportFormDialog(element) : undefined}
                                      sx={{ 
                                      minWidth: 80, 
                                      fontWeight: 'bold', 
                                      backgroundColor: isAuFromAULab ? '#ff9800' : (isElementReport ? '#ffe0b2' : '#ffeb3b'), 
                                      textAlign: 'center',
                                      borderLeft: isAuFromAULab ? '3px solid #e65100' : '2px solid #fbc02d',
                                      cursor: convertible ? 'pointer' : 'default',
                                    }}>
                                      {element} ({unitLabel})
                                      {isElementReport && (
                                        <Typography component="span" sx={{ fontSize: '0.55rem', display: 'block', color: '#e65100' }}>
                                          élément ÷{reportEntry?.factor}
                                        </Typography>
                                      )}
                                      {convertible && (
                                        <Typography component="span" sx={{ fontSize: '0.55rem', display: 'block', color: '#666' }}>
                                          clic → O/E
                                        </Typography>
                                      )}
                                      {isAuFromAULab && <Typography component="span" sx={{ fontSize: '0.6rem', display: 'block', color: '#fff' }}>⚖️ AU Lab</Typography>}
                                    </TableCell>
                                  );
                                });
                              })()}
                              
                              {/* Removed percentage headers for ICP supervisor */}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {/* 🔧 FIX: Sort samples by sample_id in ascending order (first sample at top) */}
                            {(selectedSample as any)?.batchSamples
                              ?.slice() // Create a copy to avoid mutating original
                              ?.sort((a: any, b: any) => {
                                // Extract numeric suffix from sample_id (e.g., "AFL-0072-01" -> 1)
                                const getNum = (id: string) => {
                                  const parts = id?.split('-') || [];
                                  return parseInt(parts[parts.length - 1]) || 0;
                                };
                                return getNum(a.sample_id) - getNum(b.sample_id);
                              })
                              ?.map((sample: any, sampleIndex: number) => {
                              const prepData = sample.metadonnees?.icp_preparation || sample.metadonnees?.icp_preparation_data || {};
                              
                              // 🔧 CRITICAL FIX: For ICP Supervisor, prioritize ICP-specific data sources for ICP elements
                              // But for Au, always get from analysis_results (comes from AU lab)
                              // Priority: icp_analysis_form -> icp_analysis_data -> analysis_results (fallback)
                              const icpAnalysisSources = [
                                sample.metadonnees?.icp_analysis_form?.analysis_readings,
                                sample.metadonnees?.icp_analysis_data?.analysis_readings,
                                sample.metadonnees?.analysis_results
                              ];
                              
                              let icpReadings = [];
                              for (const source of icpAnalysisSources) {
                                if (source && Array.isArray(source) && source.length > 0) {
                                  icpReadings = source;
                                  break;
                                }
                              }
                              
                              // For BGM samples, get Au from au_analysis_form or analysis_results
                              const analysisResults = sample.metadonnees?.analysis_results || [];
                              const auAnalysisReadings = sample.metadonnees?.au_analysis_form?.analysis_readings || [];
                              
                              // Try to get Au from analysis_results first
                              let auData = analysisResults.find((r: any) => r.element === 'Au');
                              
                              // If Au is 0 or missing in analysis_results, get from au_analysis_form
                              if ((!auData || auData.concentration_ppm === 0) && auAnalysisReadings.length > 0) {
                                const auFormData = auAnalysisReadings.find((r: any) => r.element === 'Au');
                                if (auFormData) {
                                  // Convert au_analysis_form format to standard format
                                  auData = {
                                    element: 'Au',
                                    concentration_ppm: auFormData.ppm || auFormData.grams_per_tonne || 0,
                                    detection_status: 'VALID',
                                    timestamp: auFormData.timestamp
                                  };
                                }
                              }
                              
                              // Merge: ICP elements from icpReadings + Au from AU lab
                              let analysisReadings = [...icpReadings];
                              if (auData && !icpReadings.some((r: any) => r.element === 'Au')) {
                                analysisReadings.push(auData);
                              } else if (auData) {
                                // Replace Au in icpReadings with Au from AU lab (more accurate)
                                analysisReadings = icpReadings.map((r: any) => 
                                  r.element === 'Au' ? auData : r
                                );
                              }
                              
                              // Create a map for quick element lookup
                              const elementData: Record<string, any> = {};
                              analysisReadings.forEach((reading: any) => {
                                elementData[reading.element] = reading;
                              });
                              
                              // 🔧 FIX: Use ALL batch elements for column consistency, with AU for BGM
                              const batchSamples = (selectedSample as any)?.batchSamples || [];
                              // 🆕 Include AU for BGM samples in validation modal
                              const isBGMRow = sample?.metadonnees?.bgm_method_selected === true || (sample as any)?.client?.company_name === 'BGM';
                              const allBatchElements = isBGMRow ? getAllBatchElements(batchSamples, true) : getBatchCertificateElements(batchSamples);
                              const sampleSelectedElements = getSampleElements(sample, true);
                              
                              return (
                                <TableRow key={sampleIndex} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                                  {/* Sample ID */}
                                  <TableCell sx={{ fontWeight: 'bold', color: '#1976d2', fontSize: '0.8rem' }}>
                                    {sample.sample_id}
                                  </TableCell>
                                  
                                  {/* Client / Reference */}
                                  <TableCell sx={{ fontSize: '0.75rem' }}>
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                        {sample.client?.company_name || 'N/A'}
                                      </Typography>
                                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                        Réf: {sample.external_id || 'N/A'}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  
                                  {/* Full Preparation Data */}
                                  <TableCell sx={{ fontSize: '0.75rem' }}>
                                    <Chip 
                                      label={prepData.initialWeight != null ? Number(prepData.initialWeight).toFixed(3) : prepData.initial_weight != null ? Number(prepData.initial_weight).toFixed(3) : 'N/A'} 
                                      size="small"
                                      color={prepData.initialWeight || prepData.initial_weight ? 'success' : 'error'}
                                      sx={{ fontSize: '0.65rem' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem' }}>
                                    <Chip 
                                      label={prepData.digestionMethod || prepData.digestion_method || 'N/A'} 
                                      size="small"
                                      color={prepData.digestionMethod || prepData.digestion_method ? 'primary' : 'default'}
                                      sx={{ fontSize: '0.65rem' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                      {prepData.digestionVolume || prepData.initial_volume || prepData.finalVolume || 'N/A'} mL
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem' }}>
                                    <Chip 
                                      label={`${prepData.dilutionFactor || prepData.total_dilution_factor || 'N/A'}x`} 
                                      size="small"
                                      color="secondary"
                                      sx={{ fontSize: '0.65rem' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem' }}>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                                      {prepData.technician || prepData.technician_name || 'N/A'}
                                    </Typography>
                                  </TableCell>
                                  
                                  {/* ALL PPM VALUES FOR ALL ELEMENTS WITH GOLDEN RULE */}
                                  {/* 🔧 FIX: Iterate over ALL batch elements, grey out non-selected per sample */}
                                  {allBatchElements.map((element: string) => {
                                    const inCertList = allBatchElements.includes(element);
                                    const pairForCol = getIcpConversionPair(element);
                                    const altSymbol = pairForCol
                                      ? (element === pairForCol.element ? pairForCol.oxide : pairForCol.element)
                                      : null;
                                    const reportCol = !isBGMRow ? resolveReportFormColumn(element, lotReportForm) : null;
                                    const hasElement = inCertList && (
                                      sampleSelectedElements.includes(element) ||
                                      (altSymbol != null && sampleSelectedElements.includes(altSymbol)) ||
                                      (reportCol?.dataKey != null && sampleSelectedElements.includes(reportCol.dataKey))
                                    );
                                    const { storedPpm, dataKey, entry: reportEntry, finalPercent: reportFinalPercent } = getMatrixStoredPpmForColumn(
                                      sample.id,
                                      element,
                                      elementData,
                                      isBGMRow ? {} : lotReportForm,
                                    );
                                    const ppm = storedPpm;
                                    const reading = elementData[dataKey] || elementData[element];
                                    
                                    // 🆕 BGM FIX: Check if this is AU element from AU lab (for visual distinction)
                                    const isBGMSample = sample.metadonnees?.bgm_method_selected === true || sample.metadonnees?.multi_lab_workflow === true;
                                    const isAuFromAULab = element === 'Au' && isBGMSample;
                                    
                                    // 🔧 FIX: If element not selected for this sample, show greyed disabled cell
                                    if (!hasElement) {
                                      return (
                                        <TableCell key={`ppm-${sample.id}-${element}`} sx={{ 
                                          borderLeft: '1px solid #ffeb3b', 
                                          p: 0.5, 
                                          textAlign: 'center',
                                          backgroundColor: '#f5f5f5'
                                        }}>
                                          <Typography sx={{ color: '#bdbdbd', fontSize: '0.75rem' }}>-</Typography>
                                        </TableCell>
                                      );
                                    }
                                    
                                    // Get method code for method-specific detection limits
                                    // 🔧 FIX (ZMSM): Mirror backend certificates.service — ZMSM client
                                    // and BGM/multi-lab batches must use BGM-MULTI-LAB limits
                                    // (Cu/Fe/Pb/Zn 0,01 %, Ag 1 g/t, Au 0,05 g/t) instead of the
                                    // 4-acid fallback. Safe for 5-element ZMSM + BGM + 4-acid methods.
                                    const icpMethodCode = resolveElementMethodCode(sample, element);

                                    const elementLimit = getMethodDetectionLimit(icpMethodCode, element);
                                    const columnForcePercent = !isBGMRow && !isZMSMBatch(batchSamples) && !!validationTraceColumnPercentMap[element];
                                    const { formatted, reportAsPercent, editDisplayValue } = formatMatrixCellValue(
                                      icpMethodCode,
                                      element,
                                      storedPpm,
                                      isBGMRow ? null : reportEntry,
                                      columnForcePercent,
                                      reportFinalPercent,
                                    );
                                    
                                    // Check detection limits (traces: raw ppm; majors: LOD in % is handled inside formatter)
                                    const validation = methodValidations.find(v => v.element === element);
                                    const belowLOD = formatted?.startsWith('<');
                                    const belowLOQ =
                                      !reportAsPercent &&
                                      validation &&
                                      ppm >= (validation.lod_ppm || 0) &&
                                      ppm < (validation.loq_ppm || 0);
                                    const origStored = Number((elementData[dataKey] || elementData[element])?.concentration_ppm) || 0;
                                    const origDisplayValue = reportEntry?.mode === 'element'
                                      ? icpStoredPpmToElementPercent(origStored, reportEntry.factor)
                                      : (elementLimit ? icpStoredPpmToDisplayValue(elementLimit, origStored, columnForcePercent) : origStored);

                                    const isEdited = editedValues[sample.id]?.[dataKey] !== undefined || editedValues[sample.id]?.[element] !== undefined;

                                    const cellKey = `${sample.id}-${element}`;
                                    const isCellEditing = editingCell === cellKey;

                                    return (
                                      <TableCell key={`ppm-${cellKey}`} sx={{
                                        borderLeft: isAuFromAULab ? '3px solid #e65100' : '1px solid #ffeb3b',
                                        p: 0.5,
                                        textAlign: 'center',
                                        backgroundColor: isAuFromAULab
                                          ? (isEdited ? '#ffe0b2' : '#fff3e0')
                                          : (isEdited ? '#fffacd' : (belowLOD ? '#ffebee' : belowLOQ ? '#fff3e0' : 'transparent')),
                                        cursor: 'pointer',
                                        minWidth: 70,
                                        position: 'relative',
                                      }}
                                      onDoubleClick={() => {
                                        setEditingCell(cellKey);
                                        setEditingCellDraft(String(editDisplayValue));
                                      }}
                                      >
                                        {isCellEditing ? (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                            <input
                                              type="number"
                                              value={editingCellDraft}
                                              autoFocus
                                              step={element === 'Au' ? 0.01 : reportAsPercent ? 0.01 : 0.1}
                                              min={0}
                                              style={{
                                                width: 72,
                                                textAlign: 'center',
                                                padding: '3px',
                                                fontSize: '0.75rem',
                                                border: `2px solid ${editingCellDraft !== '' && isNaN(parseFloat(editingCellDraft)) ? '#d32f2f' : '#1976d2'}`,
                                                borderRadius: 3,
                                              }}
                                              onChange={(e) => setEditingCellDraft(e.target.value)}
                                              onBlur={() => {
                                                const newVal = parseFloat(editingCellDraft.replace(',', '.'));
                                                if (!isNaN(newVal) && newVal >= 0) {
                                                  // User always types in the DISPLAY unit (% for majors, ppm for traces).
                                                  // Convert back to the internal ppm-scale storage:
                                                  //   reportAsPercent=true  → user typed %, store as % × 10000
                                                  //   reportAsPercent=false → user typed ppm, store as-is
                                                  let newPpm = reportAsPercent ? newVal * 10000 : newVal;
                                                  if (reportEntry?.mode === 'element') {
                                                    newPpm = newVal * reportEntry.factor * 10000;
                                                  }
                                                  handleValueEdit(sample.id, dataKey, newPpm, Number((elementData[dataKey] || elementData[element])?.concentration_ppm) || 0);
                                                }
                                                setEditingCell(null);
                                                setEditingCellDraft('');
                                              }}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                                if (e.key === 'Escape') {
                                                  setEditingCell(null);
                                                  setEditingCellDraft('');
                                                }
                                              }}
                                            />
                                            <Tooltip title="Annuler (Échap)" placement="top" arrow>
                                              <span
                                                style={{ cursor: 'pointer', fontSize: '0.75rem', color: '#999', lineHeight: 1 }}
                                                onMouseDown={(e) => {
                                                  e.preventDefault(); // prevent blur firing first
                                                  setEditingCell(null);
                                                  setEditingCellDraft('');
                                                }}
                                              >✕</span>
                                            </Tooltip>
                                          </Box>
                                        ) : (
                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                                            <Typography
                                              component="span"
                                              sx={{
                                                fontSize: '0.75rem',
                                                fontWeight: isEdited ? 'bold' : 'normal',
                                                color: isEdited ? '#d32f2f' : (ppm > 0 ? '#1976d2' : '#999'),
                                              }}
                                              title={
                                                isEdited
                                                  ? `Original: ${origDisplayValue.toFixed(2)} → ${editDisplayValue.toFixed(2)} (${reportAsPercent ? '%' : 'ppm'}) — double-clic pour modifier`
                                                  : 'Double-clic pour modifier'
                                              }
                                            >
                                              {formatted ||
                                                (reportAsPercent ? editDisplayValue.toFixed(2) : Number(storedPpm).toFixed(2))}
                                              {isEdited && ' ✏️'}
                                            </Typography>
                                            {isEdited && (
                                              <Tooltip title={`Rétablir original (${origDisplayValue.toFixed(2)})`} placement="top" arrow>
                                                <span
                                                  style={{ cursor: 'pointer', fontSize: '0.65rem', color: '#1976d2', lineHeight: 1 }}
                                                  onClick={(e) => { e.stopPropagation(); revertEditedValue(sample.id, dataKey); }}
                                                >↺</span>
                                              </Tooltip>
                                            )}
                                          </Box>
                                        )}
                                      </TableCell>
                                    );
                                  })}
                                  
                                  {/* Removed duplicate percentage columns for ICP supervisor - ppm only */}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      
                      {/* Supervisor Import: Fusion overlay or Confirmation re-run */}
                      {(() => {
                        const bs = (selectedSample as any)?.batchSamples || [];
                        if (bs.length === 0 || isBGMBatch(bs)) return null; // multi-lab lots now show 4-acides section (BGM still excluded)
                        // Pre-compute file-list grouping here (outside the JSX) so we don't
                        // need a fragile nested IIFE inside the JSX tree.
                        const hasMatchInfo = supervisorImportFiles.some(f => typeof f.matchCount === 'number');
                        const matchingFiles = hasMatchInfo
                          ? supervisorImportFiles.filter(f => (f.matchCount || 0) > 0)
                          : [];
                        const otherFiles = hasMatchInfo
                          ? supervisorImportFiles.filter(f => !((f.matchCount || 0) > 0))
                          : supervisorImportFiles;
                        return (
                          <Box sx={{ mt: 2, p: 1.5, border: '1px dashed #90caf9', borderRadius: 1, backgroundColor: '#f5f9ff' }}>
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1565c0' }}>
                                Import fichier ICP (Fusion ou Confirmation)
                              </Typography>
                              <FormControl size="small" sx={{ minWidth: 160 }}>
                                <Select
                                  value={supervisorImportMode}
                                  onChange={(e) => setSupervisorImportMode(e.target.value as 'fusion' | 'confirmation')}
                                  sx={{ fontSize: '0.75rem', height: 32 }}
                                >
                                  <MenuItem value="fusion">Fusion (overlay majeurs)</MenuItem>
                                  <MenuItem value="confirmation">Confirmation (re-analyse)</MenuItem>
                                </Select>
                              </FormControl>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={handleSupervisorImportScan}
                                disabled={supervisorImportLoading}
                                sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                              >
                                {supervisorImportLoading ? <CircularProgress size={14} sx={{ mr: 0.5 }} /> : null}
                                Scanner le dossier
                              </Button>
                            </Box>

                            {supervisorImportOpen && (
                              <Box sx={{ mt: 1 }}>
                                {hasMatchInfo && matchingFiles.length === 0 && (
                                  <Alert severity="warning" sx={{ mb: 1, fontSize: '0.72rem', py: 0.25 }}>
                                    Aucun fichier du dossier ne contient les échantillons de ce lot.
                                    Vérifiez que le fichier ICP a bien été déposé dans <code>data/icp-imports</code>.
                                  </Alert>
                                )}
                                {hasMatchInfo && matchingFiles.length > 0 && (
                                  <Alert severity="success" sx={{ mb: 1, fontSize: '0.72rem', py: 0.25 }}>
                                    <strong>{matchingFiles.length}</strong> fichier(s) contiennent des échantillons de ce lot — placés en tête de liste.
                                  </Alert>
                                )}
                                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                  <FormControl size="small" sx={{ minWidth: 320 }}>
                                    <Select
                                      value={supervisorImportSelectedFile}
                                      onChange={(e) => { setSupervisorImportSelectedFile(e.target.value); setSupervisorImportPreview(null); }}
                                      displayEmpty
                                      sx={{ fontSize: '0.75rem', height: 32 }}
                                    >
                                      <MenuItem value="" disabled>Choisir un fichier...</MenuItem>
                                      {matchingFiles.map((f) => (
                                        <MenuItem key={f.name} value={f.name} sx={{ fontSize: '0.75rem', bgcolor: 'rgba(46, 125, 50, 0.08)' }}>
                                          ✅ {f.name} {f.analysisType ? `(${f.analysisType})` : ''} — {f.matchCount} échantillon(s)
                                        </MenuItem>
                                      ))}
                                      {hasMatchInfo && matchingFiles.length > 0 && otherFiles.length > 0 && (
                                        <MenuItem disabled sx={{ fontSize: '0.7rem', opacity: 0.6 }}>
                                          ─── Autres fichiers ({otherFiles.length}) ───
                                        </MenuItem>
                                      )}
                                      {otherFiles.map((f) => (
                                        <MenuItem key={f.name} value={f.name} sx={{ fontSize: '0.75rem' }}>
                                          {f.name} {f.analysisType ? `(${f.analysisType})` : ''}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handleSupervisorImportParse}
                                    disabled={!supervisorImportSelectedFile || supervisorImportLoading}
                                    sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                                  >
                                    Analyser
                                  </Button>
                                  <Button
                                    size="small"
                                    onClick={() => { setSupervisorImportOpen(false); setSupervisorImportPreview(null); }}
                                    sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                                  >
                                    Fermer
                                  </Button>
                                </Box>

                                {supervisorImportPreview && supervisorImportPreview.length > 0 && (
                                  <Box sx={{ mt: 1 }}>
                                    <Alert severity="info" sx={{ fontSize: '0.72rem', py: 0.25 }}>
                                      <strong>{supervisorImportPreview.length}</strong> mesures trouvées pour{' '}
                                      <strong>
                                        {new Set(supervisorImportPreview.map((r: any) => r.sample_reference)).size}
                                      </strong>{' '}
                                      échantillons du lot.{' '}
                                      {supervisorImportMode === 'fusion'
                                        ? 'Mode fusion : les majeurs seront remplacés, les traces conservées.'
                                        : 'Mode confirmation : seules les valeurs présentes dans le fichier seront remplacées.'}
                                    </Alert>
                                    <Button
                                      variant="contained"
                                      color="warning"
                                      size="small"
                                      onClick={handleSupervisorImportApply}
                                      sx={{ mt: 0.5, fontSize: '0.7rem', textTransform: 'none' }}
                                    >
                                      Appliquer les {supervisorImportPreview.length} valeurs
                                    </Button>
                                  </Box>
                                )}
                                {supervisorImportPreview && supervisorImportPreview.length === 0 && (
                                  <Alert severity="warning" sx={{ mt: 1, fontSize: '0.72rem', py: 0.25 }}>
                                    Aucun échantillon correspondant trouvé dans ce fichier pour ce lot.
                                  </Alert>
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      })()}

                      {/* Treatment Calculations: Somme Majeurs+Traces, PAF, Grand Total */}
                      {(() => {
                        const batchSamples = (selectedSample as any)?.batchSamples || [];
                        if (batchSamples.length === 0) return null;
                        if (isBGMBatch(batchSamples)) return null; // multi-lab lots now show 4-acides section (BGM still excluded)

                        // 🔧 Treatment methods that trigger the sums section.
                        // Match as substring because methods are often stored with prefixes
                        // like "FP-ICP PRE.MO/ANA/007" or "ICP PRE.MO/ANA/038".
                        const ICP_TREATMENT_METHOD_KEYS = [
                          'PRE.MO/ANA/038', 'PRE.MO/ANA/037', 'PRE.MO/ANA/041',
                          'PRE.MO/ANA/007', 'PRE.MO/ANA/039',
                        ];
                        const batchHasTreatmentMethod = batchSamples.some((s: any) => {
                          const methods: string[] = s.metadonnees?.analyses_demandees || s.analyses_demandees || [];
                          // Method-string substring match
                          if (methods.some((m: string) => typeof m === 'string' && ICP_TREATMENT_METHOD_KEYS.some(k => m.includes(k)))) return true;
                          // Fallback: digestion method or analysis_type set in icp_analysis_form
                          const dig = s.metadonnees?.icp_analysis_form?.digestion_method;
                          if (dig === 'FOUR_ACID' || dig === 'FUSION_Na2O2') return true;
                          const at = s.metadonnees?.icp_analysis_form?.analysis_type;
                          if (at === '4-acides' || at === 'fusion') return true;
                          // Fallback: any reading has a 4-acides/fusion analysisType marker
                          if (icpGetMetadonneesReadings(s).some((r: any) => r.analysisType === '4-acides' || r.analysisType === 'fusion')) return true;
                          return false;
                        });
                        if (!batchHasTreatmentMethod) return null;

                        const batchTypes = new Set<string>();
                        batchSamples.forEach((s: any) => {
                          const t = s.metadonnees?.icp_analysis_form?.analysis_type;
                          if (t && t !== 'unknown') batchTypes.add(t);
                          icpGetMetadonneesReadings(s).forEach((r: any) => {
                            if (r.analysisType && r.analysisType !== 'unknown') batchTypes.add(r.analysisType);
                          });
                        });

                        const hasFusionOverlay = batchSamples.some((s: any) =>
                          fusionImportedReadings[s.id] && Object.keys(fusionImportedReadings[s.id]).length > 0,
                        );
                        const batchHasSiInEdits = batchSamples.some((s: any) =>
                          editedValues[s.id]?.['Si'] !== undefined && editedValues[s.id]['Si'] > 0,
                        );
                        if ((hasFusionOverlay || batchHasSiInEdits) && !batchTypes.has('fusion')) batchTypes.add('fusion');
                        const isMixedType = batchTypes.size > 1 || hasFusionOverlay;
                        const displayType = isMixedType
                          ? '4-Acides + Fusion'
                          : batchTypes.has('fusion') ? 'Fusion'
                          : batchTypes.has('4-acides') ? '4-Acides' : null;
                        const digestionMethod = batchSamples[0]?.metadonnees?.icp_analysis_form?.digestion_method;
                        const hasAnyReadings = batchSamples.some((s: any) => icpGetMetadonneesReadings(s).length > 0);
                        const batchHasSi = batchHasSiInEdits || batchSamples.some((s: any) =>
                          icpGetMetadonneesReadings(s).some((r: any) => r.element === 'Si'),
                        );
                        const expectNear100Pct = batchTypes.has('fusion') || batchHasSi;
                        const sortSamples = (arr: any[]) =>
                          arr.slice().sort((a: any, b: any) => {
                            const n = (id: string) => parseInt((id?.split('-') || []).pop() || '0') || 0;
                            return n(a.sample_id) - n(b.sample_id);
                          });
                        
                        return (
                          <Box sx={{ mt: 2 }}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                Traitement: Somme Oxydes, PAF et Total
                              </Typography>
                              {displayType && (
                                <Chip label={displayType} size="small" color={isMixedType ? 'info' : batchTypes.has('fusion') ? 'secondary' : 'warning'} />
                              )}
                              {digestionMethod && <Chip label={digestionMethod} size="small" variant="outlined" />}
                              {!hasAnyReadings && <Chip label="Aucune donnée d'analyse trouvée" size="small" color="error" />}
                            </Box>

                            {/* ─── LOT-LEVEL SOURCE RULE EDITOR ───
                                Visible only when there's at least one mixed sample
                                (both 4-Acides AND Fusion data). Lets the supervisor
                                say "all 4-Acides except SiO₂ from Fusion" with one
                                click for the whole lot, instead of toggling each
                                element on each sample. */}
                            {hasAnyReadings && isMixedType && (() => {
                              const mixedSamples = sortSamples(batchSamples).filter((s: any) =>
                                fusionImportedReadings[s.id] &&
                                Object.keys(fusionImportedReadings[s.id]).length > 0 &&
                                icpGetMetadonneesReadings(s).length > 0,
                              );
                              if (mixedSamples.length === 0) return null;

                              // Union of elements where BOTH sources have a value
                              // (so flipping the source actually does something)
                              const eligibleElements = new Set<string>();
                              for (const s of mixedSamples) {
                                const fmap = fusionImportedReadings[s.id] || {};
                                const acidEls = new Set<string>(
                                  icpGetMetadonneesReadings(s).map((r: any) => r.element).filter(Boolean),
                                );
                                for (const el of Object.keys(fmap)) {
                                  if (el.startsWith('__')) continue;
                                  if (acidEls.has(el)) eligibleElements.add(el);
                                }
                              }
                              const sortedElements = Array.from(eligibleElements).sort((a: string, b: string) => {
                                const aIsMaj = ICP_TREATMENT_MAJOR_ELEMENTS.has(a) || a === 'S';
                                const bIsMaj = ICP_TREATMENT_MAJOR_ELEMENTS.has(b) || b === 'S';
                                if (aIsMaj !== bIsMaj) return aIsMaj ? -1 : 1;
                                return a.localeCompare(b);
                              });

                              const altSource: 'acid' | 'fusion' =
                                lotSourceRule.defaultSource === 'acid' ? 'fusion' : 'acid';
                              const altLabel = altSource === 'fusion' ? 'Fusion' : '4-Acides';

                              return (
                                <Paper
                                  variant="outlined"
                                  sx={{
                                    p: 1.5,
                                    mb: 1.5,
                                    backgroundColor: '#f8fafc',
                                    borderColor: '#cfd8dc',
                                  }}
                                >
                                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1565c0' }}>
                                      ⚙️ Règle de sélection (lot entier)
                                    </Typography>
                                    <Chip
                                      label={`${mixedSamples.length} échantillon(s) mixte(s)`}
                                      size="small"
                                      variant="outlined"
                                    />
                                    {lotRuleApplied && (
                                      <Chip label="Appliquée" size="small" color="success" />
                                    )}
                                  </Box>

                                  <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5} mb={1}>
                                    <Typography variant="body2" sx={{ minWidth: 110 }}>
                                      Par défaut, prendre :
                                    </Typography>
                                    <ToggleButtonGroup
                                      size="small"
                                      exclusive
                                      value={lotSourceRule.defaultSource}
                                      onChange={(_, v: 'acid' | 'fusion' | null) => {
                                        if (!v) return;
                                        setLotSourceRule(prev => ({ ...prev, defaultSource: v, exceptions: {} }));
                                        setLotRuleApplied(false);
                                      }}
                                    >
                                      <ToggleButton value="acid" sx={{ textTransform: 'none', px: 1.5 }}>
                                        4-Acides
                                      </ToggleButton>
                                      <ToggleButton value="fusion" sx={{ textTransform: 'none', px: 1.5 }}>
                                        Fusion
                                      </ToggleButton>
                                    </ToggleButtonGroup>
                                  </Box>

                                  <Box mb={1}>
                                    <Typography variant="caption" sx={{ color: '#546e7a' }}>
                                      Exceptions — éléments à prendre depuis <strong>{altLabel}</strong> à la place :
                                    </Typography>
                                    <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                      {sortedElements.length === 0 && (
                                        <Typography variant="caption" sx={{ color: '#90a4ae', fontStyle: 'italic' }}>
                                          Aucun élément n'a de valeur dans les deux sources.
                                        </Typography>
                                      )}
                                      {sortedElements.map(el => {
                                        const isException = lotSourceRule.exceptions[el] === altSource;
                                        return (
                                          <Chip
                                            key={el}
                                            label={el}
                                            size="small"
                                            color={isException ? (altSource === 'fusion' ? 'secondary' : 'warning') : 'default'}
                                            variant={isException ? 'filled' : 'outlined'}
                                            onClick={() => {
                                              setLotSourceRule(prev => {
                                                const nextExc = { ...prev.exceptions };
                                                if (nextExc[el]) delete nextExc[el];
                                                else nextExc[el] = altSource;
                                                return { ...prev, exceptions: nextExc };
                                              });
                                              setLotRuleApplied(false);
                                            }}
                                            sx={{ cursor: 'pointer' }}
                                          />
                                        );
                                      })}
                                    </Box>
                                  </Box>

                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="primary"
                                      startIcon={<CheckCircle fontSize="small" />}
                                      onClick={() => applyLotSourceRule(lotSourceRule)}
                                    >
                                      Appliquer au lot
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={resetLotSourceRule}
                                      disabled={
                                        lotSourceRule.defaultSource === 'acid' &&
                                        Object.keys(lotSourceRule.exceptions).length === 0
                                      }
                                    >
                                      Réinitialiser
                                    </Button>
                                    <Typography variant="caption" sx={{ color: '#78909c', ml: 1 }}>
                                      Astuce : vous pouvez toujours ajuster un échantillon individuel ensuite via ▾.
                                    </Typography>
                                  </Box>
                                </Paper>
                              );
                            })()}

                            {/* ─── VIEW-MODE TOGGLE (Sommes ↔ Matrice par élément) — mixed lots only ─── */}
                            {hasAnyReadings && isMixedType && (
                              <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={0.5} flexWrap="wrap">
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1565c0' }}>
                                    Comparaison 4-Acides ↔ Fusion
                                  </Typography>
                                  <Chip label="4-Acides + Fusion" size="small" color="info" />
                                  {(() => {
                                    const fusionCount = sortSamples(batchSamples).filter((s: any) => fusionImportedReadings[s.id] && Object.keys(fusionImportedReadings[s.id]).length > 0).length;
                                    return <Chip label={`${fusionCount}/${batchSamples.length} avec fusion`} size="small" variant="outlined" />;
                                  })()}
                                </Box>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Vue :</Typography>
                                  <ToggleButtonGroup
                                    size="small"
                                    exclusive
                                    value={icpComparativeView}
                                    onChange={(_e, v) => { if (v) setIcpComparativeView(v); }}
                                  >
                                    <ToggleButton value="matrice" sx={{ px: 1.2, py: 0.3, fontSize: '0.7rem' }}>
                                      Matrice par élément
                                    </ToggleButton>
                                    <ToggleButton value="sommes" sx={{ px: 1.2, py: 0.3, fontSize: '0.7rem' }}>
                                      Sommes par échantillon
                                    </ToggleButton>
                                  </ToggleButtonGroup>
                                </Box>
                              </Box>
                            )}

                            {/* ─── NEW: PER-ELEMENT MATRIX VIEW (4-Acides ↔ Fusion side-by-side, all samples at once) ─── */}
                            {hasAnyReadings && isMixedType && icpComparativeView === 'matrice' && (() => {
                              const samplesArr = sortSamples(batchSamples);

                              // Union of elements with any data (4A reading or Fusion overlay)
                              const elementSet = new Set<string>();
                              samplesArr.forEach((s: any) => {
                                icpGetMetadonneesReadings(s).forEach((r: any) => {
                                  if (r.element && !r.element.startsWith('__')) elementSet.add(r.element);
                                });
                                Object.keys(fusionImportedReadings[s.id] || {}).forEach(el => {
                                  if (!el.startsWith('__')) elementSet.add(el);
                                });
                              });
                              const sortedElements = Array.from(elementSet).sort((a, b) => {
                                const aIsMaj = ICP_TREATMENT_MAJOR_ELEMENTS.has(a) || a === 'S';
                                const bIsMaj = ICP_TREATMENT_MAJOR_ELEMENTS.has(b) || b === 'S';
                                if (aIsMaj !== bIsMaj) return aIsMaj ? -1 : 1;
                                return a.localeCompare(b);
                              });
                              const elementsToShow = sortedElements.filter(el => {
                                const isMaj = ICP_TREATMENT_MAJOR_ELEMENTS.has(el) || el === 'S';
                                if (matrixFilter === 'majeurs') return isMaj;
                                if (matrixFilter === 'traces') return !isMaj;
                                return true;
                              });
                              const majorCount = sortedElements.filter(el => ICP_TREATMENT_MAJOR_ELEMENTS.has(el) || el === 'S').length;
                              const traceCount = sortedElements.length - majorCount;

                              // Pull a representative method code & detection limit for an element from any sample
                              const findAcidMethodCode = (el: string): string => {
                                for (const s of samplesArr) {
                                  const r = icpGetMetadonneesReadings(s).find((r: any) => r.element === el && r.analysisType !== 'fusion');
                                  if (r?.method) return r.method;
                                  const rAny = icpGetMetadonneesReadings(s).find((r: any) => r.element === el);
                                  if (rAny?.method && rAny.analysisType !== 'fusion') return rAny.method;
                                }
                                return ICP_TREATMENT_MAJOR_ELEMENTS.has(el) || el === 'S' ? 'PRE.MO/ANA/038' : 'PRE.MO/ANA/038';
                              };
                              const findFusionMethodCode = (_el: string): string => 'PRE.MO/ANA/007';

                              const cellLabelLimit = (el: string, methodCode: string): string => {
                                const lim = getMethodDetectionLimit(methodCode, el);
                                if (!lim) return '—';
                                return `${lim.limit} ${lim.unit}`;
                              };

                              // Format a cell value for display, honouring editedValues when present.
                              //   majors → oxide percentage (matches existing sums logic)
                              //   traces → ppm
                              // Returns { display, isOverridden } so cells can show an edit indicator.
                              const fmtCellValue = (
                                sample: any,
                                el: string,
                                ppm: number | undefined,
                                isMaj: boolean,
                                sourceKey: 'acid' | 'fusion',
                              ): { display: string; isOverridden: boolean } => {
                                if (ppm === undefined || ppm === null || isNaN(ppm)) return { display: '—', isOverridden: false };
                                const isLegacy = icpDetectLegacyMajorSmallPpm(icpGetMetadonneesReadings(sample));
                                // Check if this source's raw ppm was manually overridden in editedValues:
                                // editedValues stores the final resolved ppm for the chosen element.
                                // We compare against it only when this source IS the chosen one.
                                const eff = getEffectiveSource(sample.id, el);
                                const isChosenSource = (sourceKey === 'acid' && eff === 'acid') ||
                                                       (sourceKey === 'fusion' && (eff === 'fusion' || eff === 'manual'));
                                const overridePpm = isChosenSource ? editedValues[sample.id]?.[el] : undefined;
                                const resolvedPpm = overridePpm !== undefined ? overridePpm : ppm;
                                const isOverridden = overridePpm !== undefined && overridePpm !== ppm;
                                let display: string;
                                if (isMaj) {
                                  const oxidePct = icpDisplayMajorAsOxidePercent(resolvedPpm, isLegacy);
                                  display = oxidePct.toFixed(2);
                                } else {
                                  display = resolvedPpm < 100 ? resolvedPpm.toFixed(2) : resolvedPpm.toFixed(1);
                                }
                                return { display, isOverridden };
                              };

                              // Discrepancy detection: when both sources exist and differ significantly.
                              const isDiscrepant = (acidV: number | undefined, fusionV: number | undefined, isMaj: boolean): boolean => {
                                if (acidV === undefined || fusionV === undefined) return false;
                                if (isMaj) {
                                  // Compare oxide-percents (use raw ppm with same isLegacy assumption)
                                  const a = acidV / 10000;
                                  const f = fusionV / 10000;
                                  if (Math.max(a, f) < 0.05) return false; // tiny values → ignore
                                  return Math.abs(a - f) > 0.5 && (Math.abs(a - f) / Math.max(a, f, 0.001)) > 0.20;
                                }
                                if (Math.max(acidV, fusionV) < 5) return false;
                                return (Math.abs(acidV - fusionV) / Math.max(acidV, fusionV, 1)) > 0.30;
                              };

                              return (
                                <>
                                  <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Filtrer :</Typography>
                                    <ToggleButtonGroup
                                      size="small"
                                      exclusive
                                      value={matrixFilter}
                                      onChange={(_e, v) => { if (v) setMatrixFilter(v); }}
                                    >
                                      <ToggleButton value="majeurs" sx={{ px: 1, py: 0.2, fontSize: '0.65rem' }}>
                                        Majeurs ({majorCount})
                                      </ToggleButton>
                                      <ToggleButton value="traces" sx={{ px: 1, py: 0.2, fontSize: '0.65rem' }}>
                                        Traces ({traceCount})
                                      </ToggleButton>
                                      <ToggleButton value="tous" sx={{ px: 1, py: 0.2, fontSize: '0.65rem' }}>
                                        Tous ({sortedElements.length})
                                      </ToggleButton>
                                    </ToggleButtonGroup>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>
                                      1 clic = choisir la source · Double-clic sur la cellule choisie (verte) = modifier la valeur
                                    </Typography>
                                  </Box>

                                  <TableContainer component={Paper} sx={{ maxHeight: 600, overflowX: 'auto' }}>
                                    <Table
                                      size="small"
                                      stickyHeader
                                      sx={{
                                        borderCollapse: 'separate',
                                        '& .MuiTableCell-root': { fontSize: '0.7rem', py: 0.4, px: 0.6, borderRight: '1px solid #eceff1' },
                                      }}
                                    >
                                      <TableHead>
                                        {/* HEADER ROW 1: pink "4 acide" / yellow "Fusion" sub-headers */}
                                        <TableRow>
                                          <TableCell colSpan={2} sx={{ position: 'sticky', left: 0, top: 0, zIndex: 4, backgroundColor: '#cfd8dc', borderBottom: '1px solid #b0bec5' }} />
                                          {elementsToShow.map(el => (
                                            <React.Fragment key={`hdr1-${el}`}>
                                              <TableCell sx={{ backgroundColor: '#f8bbd0', textAlign: 'center', fontWeight: 'bold', color: '#880e4f', fontSize: '0.65rem', py: 0.2 }}>4 acide</TableCell>
                                              <TableCell sx={{ backgroundColor: '#fff59d', textAlign: 'center', fontWeight: 'bold', color: '#827717', fontSize: '0.65rem', py: 0.2, borderRight: '2px solid #90a4ae' }}>Fusion</TableCell>
                                            </React.Fragment>
                                          ))}
                                        </TableRow>
                                        {/* HEADER ROW 2: METHOD label + method code per sub-column */}
                                        <TableRow>
                                          <TableCell colSpan={2} sx={{ position: 'sticky', left: 0, zIndex: 3, backgroundColor: '#1976d2', color: 'white', fontWeight: 'bold', textAlign: 'center', fontSize: '0.7rem' }}>METHOD</TableCell>
                                          {elementsToShow.map(el => (
                                            <React.Fragment key={`hdr2-${el}`}>
                                              <TableCell sx={{ backgroundColor: '#fce4ec', textAlign: 'center', fontSize: '0.62rem', color: '#880e4f' }}>{findAcidMethodCode(el)}</TableCell>
                                              <TableCell sx={{ backgroundColor: '#fffde7', textAlign: 'center', fontSize: '0.62rem', color: '#827717', borderRight: '2px solid #90a4ae' }}>{findFusionMethodCode(el)}</TableCell>
                                            </React.Fragment>
                                          ))}
                                        </TableRow>
                                        {/* HEADER ROW 3: LIMITE DETECTION + LOD per sub-column */}
                                        <TableRow>
                                          <TableCell colSpan={2} sx={{ position: 'sticky', left: 0, zIndex: 3, backgroundColor: '#42a5f5', color: 'white', fontWeight: 'bold', textAlign: 'center', fontSize: '0.65rem' }}>LIMITE DETECTION</TableCell>
                                          {elementsToShow.map(el => (
                                            <React.Fragment key={`hdr3-${el}`}>
                                              <TableCell sx={{ backgroundColor: '#fce4ec', textAlign: 'center', fontSize: '0.62rem', color: '#880e4f' }}>{cellLabelLimit(el, findAcidMethodCode(el))}</TableCell>
                                              <TableCell sx={{ backgroundColor: '#fffde7', textAlign: 'center', fontSize: '0.62rem', color: '#827717', borderRight: '2px solid #90a4ae' }}>{cellLabelLimit(el, findFusionMethodCode(el))}</TableCell>
                                            </React.Fragment>
                                          ))}
                                        </TableRow>
                                        {/* HEADER ROW 4: ref columns + element name (colSpan=2) */}
                                        <TableRow>
                                          <TableCell sx={{ position: 'sticky', left: 0, zIndex: 3, backgroundColor: '#1976d2', color: 'white', fontWeight: 'bold', minWidth: 150 }}>Customers-Ref</TableCell>
                                          <TableCell sx={{ position: 'sticky', left: 150, zIndex: 3, backgroundColor: '#1976d2', color: 'white', fontWeight: 'bold', minWidth: 120 }}>Lab-Ref</TableCell>
                                          {elementsToShow.map(el => {
                                            const isMaj = ICP_TREATMENT_MAJOR_ELEMENTS.has(el) || el === 'S';
                                            const label = isMaj ? (ICP_ELEMENT_OXIDE_LABEL[el] || el) : el;
                                            const unit = isMaj ? '%' : 'ppm';
                                            return (
                                              <TableCell key={`hdr4-${el}`} colSpan={2} sx={{
                                                backgroundColor: isMaj ? '#bbdefb' : '#e1f5fe',
                                                textAlign: 'center',
                                                fontWeight: 'bold',
                                                color: '#0d47a1',
                                                fontSize: '0.72rem',
                                                borderRight: '2px solid #90a4ae',
                                              }}>
                                                {label} *({unit})
                                              </TableCell>
                                            );
                                          })}
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {samplesArr.map((sample: any) => {
                                          const customerRef = sample.external_id || '—';
                                          const labRef = sample.sample_id;
                                          const fusionMap = fusionImportedReadings[sample.id] || {};
                                          const hasFusion = Object.keys(fusionMap).length > 0;

                                          return (
                                            <TableRow key={sample.id} sx={{ '&:hover td': { backgroundColor: 'rgba(25,118,210,0.04)' } }}>
                                              <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, backgroundColor: '#fafafa', fontWeight: 600, color: '#0d47a1' }}>
                                                {customerRef}
                                              </TableCell>
                                              <TableCell sx={{ position: 'sticky', left: 150, zIndex: 2, backgroundColor: '#fafafa', color: '#1976d2', fontWeight: 500 }}>
                                                {labRef}
                                              </TableCell>
                                              {elementsToShow.map(el => {
                                                const isMaj = ICP_TREATMENT_MAJOR_ELEMENTS.has(el) || el === 'S';
                                                const acidV = getAcidPpm(sample, el);
                                                const fusionV = fusionMap[el];
                                                const eff = getEffectiveSource(sample.id, el);
                                                const acidChosen = eff === 'acid';
                                                const fusionChosen = eff === 'fusion' || eff === 'manual';
                                                const discrepant = isDiscrepant(acidV, fusionV, isMaj);

                                                const acidFmt = fmtCellValue(sample, el, acidV, isMaj, 'acid');
                                                const fusionFmt = fmtCellValue(sample, el, fusionV, isMaj, 'fusion');

                                                const baseAcidBg = '#fce4ec';
                                                const baseFusionBg = '#fffde7';
                                                const acidBg = acidChosen ? '#a5d6a7' : (acidV === undefined ? '#f5f5f5' : baseAcidBg);
                                                const fusionBg = fusionChosen ? '#a5d6a7' : (fusionV === undefined ? '#f5f5f5' : baseFusionBg);

                                                const acidMatrixKey = `${sample.id}__${el}__acid`;
                                                const fusionMatrixKey = `${sample.id}__${el}__fusion`;
                                                const isEditingAcid = matrixEditingCell === acidMatrixKey;
                                                const isEditingFusion = matrixEditingCell === fusionMatrixKey;

                                                // Commit a matrix inline edit
                                                const commitMatrixEdit = (rawSource: 'acid' | 'fusion', rawPpm: number, originalPpm: number) => {
                                                  if (isNaN(rawPpm) || rawPpm < 0) { setMatrixEditingCell(null); setMatrixEditDraft(''); return; }
                                                  // Reverse oxide % → stored ppm for majors
                                                  const isLegacy = icpDetectLegacyMajorSmallPpm(icpGetMetadonneesReadings(sample));
                                                  let storedPpm = rawPpm;
                                                  if (isMaj) storedPpm = isLegacy ? rawPpm : rawPpm * 10000;
                                                  // Write override into editedValues and mark source as manual
                                                  handleValueEdit(sample.id, el, storedPpm, originalPpm);
                                                  setValueSourcePref(prev => ({ ...prev, [sample.id]: { ...(prev[sample.id] || {}), [el]: 'manual' as const } }));
                                                  // Store in manualOverrides too so per-element picker shows it
                                                  setManualOverrides(prev => ({ ...prev, [sample.id]: { ...(prev[sample.id] || {}), [el]: storedPpm } }));
                                                  setMatrixEditingCell(null);
                                                  setMatrixEditDraft('');
                                                };

                                                return (
                                                  <React.Fragment key={`${sample.id}-${el}`}>
                                                    {/* ── ACID cell ── */}
                                                    <Tooltip
                                                      title={
                                                        acidV === undefined
                                                          ? 'Aucune donnée 4-Acides pour cet élément'
                                                          : isEditingAcid
                                                            ? ''
                                                            : acidChosen
                                                              ? '✓ Source choisie : 4-Acides — double-clic pour modifier la valeur'
                                                              : 'Cliquer pour utiliser la valeur 4-Acides comme source'
                                                      }
                                                      placement="top"
                                                      arrow
                                                      disableHoverListener={isEditingAcid}
                                                    >
                                                      <TableCell
                                                        onClick={() => { if (!isEditingAcid && acidV !== undefined) setSourceFor(sample, el, 'acid'); }}
                                                        onDoubleClick={() => {
                                                          if (acidV !== undefined && acidChosen) {
                                                            setMatrixEditingCell(acidMatrixKey);
                                                            setMatrixEditDraft(acidFmt.display !== '—' ? acidFmt.display : '');
                                                          }
                                                        }}
                                                        sx={{
                                                          textAlign: 'right',
                                                          fontFamily: 'monospace',
                                                          backgroundColor: acidBg,
                                                          cursor: acidV === undefined ? 'default' : 'pointer',
                                                          color: acidV === undefined ? 'text.disabled' : (acidChosen ? '#1b5e20' : '#880e4f'),
                                                          fontWeight: acidChosen ? 'bold' : 'normal',
                                                          border: acidChosen ? '2px solid #1b5e20' : undefined,
                                                          transition: 'all 0.12s',
                                                          minWidth: 68,
                                                        }}
                                                      >
                                                        {isEditingAcid ? (
                                                          <input
                                                            type="number"
                                                            value={matrixEditDraft}
                                                            autoFocus
                                                            step={isMaj ? 0.01 : 0.1}
                                                            min={0}
                                                            style={{ width: 60, textAlign: 'right', padding: '2px', fontSize: '0.68rem', border: '2px solid #1976d2', borderRadius: 3 }}
                                                            onChange={(e) => setMatrixEditDraft(e.target.value)}
                                                            onBlur={() => commitMatrixEdit('acid', parseFloat(matrixEditDraft.replace(',', '.')), acidV ?? 0)}
                                                            onKeyDown={(e) => {
                                                              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                                              if (e.key === 'Escape') { setMatrixEditingCell(null); setMatrixEditDraft(''); }
                                                            }}
                                                            onClick={(e) => e.stopPropagation()}
                                                          />
                                                        ) : (
                                                          <>
                                                            {acidChosen && <span style={{ marginRight: 3 }}>✓</span>}
                                                            {acidFmt.display}
                                                            {acidFmt.isOverridden && <span style={{ marginLeft: 2, fontSize: '0.6rem' }}>✏️</span>}
                                                          </>
                                                        )}
                                                      </TableCell>
                                                    </Tooltip>
                                                    {/* ── FUSION cell ── */}
                                                    <Tooltip
                                                      title={
                                                        fusionV === undefined
                                                          ? (hasFusion ? 'Cet élément n\'est pas dans le fichier fusion' : 'Aucun fichier fusion importé pour cet échantillon')
                                                          : isEditingFusion
                                                            ? ''
                                                            : fusionChosen
                                                              ? '✓ Source choisie : Fusion — double-clic pour modifier la valeur'
                                                              : 'Cliquer pour utiliser la valeur Fusion comme source'
                                                      }
                                                      placement="top"
                                                      arrow
                                                      disableHoverListener={isEditingFusion}
                                                    >
                                                      <TableCell
                                                        onClick={() => { if (!isEditingFusion && fusionV !== undefined) setSourceFor(sample, el, 'fusion'); }}
                                                        onDoubleClick={() => {
                                                          if (fusionV !== undefined && fusionChosen) {
                                                            setMatrixEditingCell(fusionMatrixKey);
                                                            setMatrixEditDraft(fusionFmt.display !== '—' ? fusionFmt.display : '');
                                                          }
                                                        }}
                                                        sx={{
                                                          textAlign: 'right',
                                                          fontFamily: 'monospace',
                                                          backgroundColor: fusionBg,
                                                          cursor: fusionV === undefined ? 'default' : 'pointer',
                                                          color: fusionV === undefined ? 'text.disabled' : (fusionChosen ? '#1b5e20' : '#827717'),
                                                          fontWeight: fusionChosen ? 'bold' : 'normal',
                                                          border: fusionChosen ? '2px solid #1b5e20' : undefined,
                                                          borderRight: '2px solid #90a4ae',
                                                          transition: 'all 0.12s',
                                                          position: 'relative',
                                                          minWidth: 68,
                                                        }}
                                                      >
                                                        {isEditingFusion ? (
                                                          <input
                                                            type="number"
                                                            value={matrixEditDraft}
                                                            autoFocus
                                                            step={isMaj ? 0.01 : 0.1}
                                                            min={0}
                                                            style={{ width: 60, textAlign: 'right', padding: '2px', fontSize: '0.68rem', border: '2px solid #9c27b0', borderRadius: 3 }}
                                                            onChange={(e) => setMatrixEditDraft(e.target.value)}
                                                            onBlur={() => commitMatrixEdit('fusion', parseFloat(matrixEditDraft.replace(',', '.')), fusionV ?? 0)}
                                                            onKeyDown={(e) => {
                                                              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                                              if (e.key === 'Escape') { setMatrixEditingCell(null); setMatrixEditDraft(''); }
                                                            }}
                                                            onClick={(e) => e.stopPropagation()}
                                                          />
                                                        ) : (
                                                          <>
                                                            {fusionChosen && <span style={{ marginRight: 3 }}>✓</span>}
                                                            {fusionFmt.display}
                                                            {fusionFmt.isOverridden && <span style={{ marginLeft: 2, fontSize: '0.6rem' }}>✏️</span>}
                                                          </>
                                                        )}
                                                        {!isEditingFusion && discrepant && (
                                                          <Tooltip title="Écart > 20% entre 4-Acides et Fusion — vérifier" placement="top" arrow>
                                                            <span style={{ position: 'absolute', top: 1, right: 2, fontSize: '0.65rem', color: '#d32f2f' }}>⚠</span>
                                                          </Tooltip>
                                                        )}
                                                      </TableCell>
                                                    </Tooltip>
                                                  </React.Fragment>
                                                );
                                              })}
                                            </TableRow>
                                          );
                                        })}

                                        {/* FOOTER: column distribution (how many samples chose 4A vs Fusion vs no-data per element) */}
                                        <TableRow sx={{ borderTop: '2px solid #1976d2' }}>
                                          <TableCell colSpan={2} sx={{ position: 'sticky', left: 0, zIndex: 2, backgroundColor: '#e3f2fd', fontWeight: 'bold', color: '#1565c0' }}>
                                            Source choisie
                                          </TableCell>
                                          {elementsToShow.map(el => {
                                            let acidPicks = 0;
                                            let fusionPicks = 0;
                                            let manualPicks = 0;
                                            samplesArr.forEach((s: any) => {
                                              const eff = getEffectiveSource(s.id, el);
                                              if (eff === 'acid') acidPicks++;
                                              else if (eff === 'fusion') fusionPicks++;
                                              else if (eff === 'manual') manualPicks++;
                                            });
                                            return (
                                              <TableCell key={`foot-${el}`} colSpan={2} sx={{ backgroundColor: '#e3f2fd', textAlign: 'center', fontSize: '0.65rem', borderRight: '2px solid #90a4ae' }}>
                                                <Box display="flex" justifyContent="center" gap={0.4}>
                                                  {acidPicks > 0 && <Chip label={`4A: ${acidPicks}`} size="small" sx={{ backgroundColor: '#fce4ec', color: '#880e4f', height: 16, fontSize: '0.58rem' }} />}
                                                  {fusionPicks > 0 && <Chip label={`F: ${fusionPicks}`} size="small" sx={{ backgroundColor: '#fff9c4', color: '#827717', height: 16, fontSize: '0.58rem' }} />}
                                                  {manualPicks > 0 && <Chip label={`M: ${manualPicks}`} size="small" sx={{ backgroundColor: '#e1f5fe', color: '#01579b', height: 16, fontSize: '0.58rem' }} />}
                                                </Box>
                                              </TableCell>
                                            );
                                          })}
                                        </TableRow>
                                      </TableBody>
                                    </Table>
                                  </TableContainer>

                                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mt: 0.5, mb: 1, fontSize: '0.7rem' }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Légende :</Typography>
                                    <Box display="flex" alignItems="center" gap={0.4}>
                                      <Box sx={{ width: 14, height: 14, backgroundColor: '#fce4ec', border: '1px solid #ddd' }} />
                                      <Typography variant="caption">4-Acides disponible</Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={0.4}>
                                      <Box sx={{ width: 14, height: 14, backgroundColor: '#fffde7', border: '1px solid #ddd' }} />
                                      <Typography variant="caption">Fusion disponible</Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={0.4}>
                                      <Box sx={{ width: 14, height: 14, backgroundColor: '#a5d6a7', border: '2px solid #1b5e20' }} />
                                      <Typography variant="caption">Source retenue (✓)</Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={0.4}>
                                      <Box sx={{ width: 14, height: 14, backgroundColor: '#f5f5f5', border: '1px solid #ddd' }} />
                                      <Typography variant="caption">Pas de donnée</Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={0.4}>
                                      <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>⚠</span>
                                      <Typography variant="caption">Écart 4A↔Fusion {'>'} 20%</Typography>
                                    </Box>
                                  </Box>
                                </>
                              );
                            })()}

                            {/* ─── UNIFIED COMPARATIVE TABLE: 4-Acides | Fusion | Source choice | Final ─── */}
                            {hasAnyReadings && (!isMixedType || icpComparativeView === 'sommes') && (
                            <>
                            <TableContainer component={Paper} sx={{ maxHeight: 460, overflowX: 'auto' }}>
                              <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { fontSize: '0.72rem', py: 0.5, px: 0.7 } }}>
                                <TableHead>
                                  {isMixedType ? (
                                    <>
                                      <TableRow>
                                        <TableCell rowSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: 110, verticalAlign: 'bottom' }}>Echantillon</TableCell>
                                        <TableCell colSpan={3} sx={{ fontWeight: 'bold', backgroundColor: '#fff3e0', textAlign: 'center', color: '#ed6c02', borderRight: '2px solid #ffcc80' }}>4-Acides (analyste)</TableCell>
                                        <TableCell colSpan={3} sx={{ fontWeight: 'bold', backgroundColor: '#f3e5f5', textAlign: 'center', color: '#9c27b0', borderRight: '2px solid #ce93d8' }}>Fusion (superviseur)</TableCell>
                                        <TableCell rowSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#e1f5fe', textAlign: 'center', verticalAlign: 'bottom', minWidth: 130 }}>
                                          <Tooltip title="Choix par échantillon: 4A = utilise 4-Acides seul. F = remplace par fusion. Mixte = choisir élément par élément (clic sur ▾).">
                                            <span>Source<br/>retenue</span>
                                          </Tooltip>
                                        </TableCell>
                                        <TableCell colSpan={3} sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e9', textAlign: 'center', color: '#2e7d32', borderLeft: '2px solid #a5d6a7' }}>Final (envoyé au certificat)</TableCell>
                                        <TableCell rowSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#fff3e0', textAlign: 'center', verticalAlign: 'bottom' }}>
                                          <Tooltip title="Par défaut : ((CaO/56)+(MgO/40))×44">
                                            <span>PAF<br/>(%)</span>
                                          </Tooltip>
                                        </TableCell>
                                        <TableCell rowSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#fce4ec', textAlign: 'center', verticalAlign: 'bottom' }}>Grand<br/>Total</TableCell>
                                      </TableRow>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fff8e1', textAlign: 'right', color: '#ed6c02' }}>Σ Maj</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fff8e1', textAlign: 'right', color: '#ed6c02' }}>Σ Tr</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fff8e1', textAlign: 'right', color: '#ed6c02', borderRight: '2px solid #ffcc80' }}>Σ Tot</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f3e5f5', textAlign: 'right', color: '#9c27b0' }}>Σ Maj</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f3e5f5', textAlign: 'right', color: '#9c27b0' }}>Σ Tr</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f3e5f5', textAlign: 'right', color: '#9c27b0', borderRight: '2px solid #ce93d8' }}>Σ Tot</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e9', textAlign: 'right', color: '#2e7d32', borderLeft: '2px solid #a5d6a7' }}>Σ Maj</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e9', textAlign: 'right', color: '#2e7d32' }}>Σ Tr</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e9', textAlign: 'right', color: '#2e7d32' }}>Σ Tot</TableCell>
                                      </TableRow>
                                    </>
                                  ) : (
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: 120 }}>Echantillon</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd', textAlign: 'center' }}>Somme Oxydes Majeurs (%)</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd', textAlign: 'center' }}>Somme Traces (%)</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e9', textAlign: 'center' }}>Somme Totale (%)</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fff3e0', textAlign: 'center' }}>
                                        <Tooltip title="Par défaut : ((CaO/56)+(MgO/40))×44 (fichier Excel TA ICP). Videz le champ pour rétablir la formule.">
                                          <span>PAF / LOI (%)</span>
                                        </Tooltip>
                                      </TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fce4ec', textAlign: 'center' }}>Grand Total (%)</TableCell>
                                    </TableRow>
                                  )}
                                </TableHead>
                                <TableBody>
                                  {sortSamples(batchSamples).map((sample: any) => {
                                      const readings = icpGetMetadonneesReadings(sample);
                                      const isLegacy = icpDetectLegacyMajorSmallPpm(readings);
                                      // Final sums (uses editedValues which already reflect per-sample/per-element source choice)
                                      const sums = icpComputeSampleSums(readings, editedValues[sample.id], isLegacy);
                                      // Always-computed reference sums for comparison columns
                                      const acidSums = icpComputeSampleSums(readings, undefined, isLegacy);
                                      const fusionMap = fusionImportedReadings[sample.id] || {};
                                      const hasFusionForSample = Object.keys(fusionMap).length > 0;
                                      const fusionSums = hasFusionForSample
                                        ? icpComputeSampleSums(readings, fusionMap, isLegacy)
                                        : null;

                                      const { caO_pct, mgO_pct } = icpCaMgOxidePercent(readings, sample.id, editedValues);
                                      const pafFromFormula = icpPafCarbonateEstimatePercent(caO_pct, mgO_pct);
                                      const pafManual = editedValues[sample.id]?.['__PAF__'];
                                      const pafVal = pafManual !== undefined ? pafManual : pafFromFormula;
                                      const grandTotal = sums.sommeTotale + pafVal;
                                      const totalColor = expectNear100Pct
                                        ? grandTotal >= 95 && grandTotal <= 105
                                          ? '#2e7d32'
                                          : grandTotal >= 80 && grandTotal <= 110
                                            ? '#ed6c02'
                                            : '#d32f2f'
                                        : '#1565c0';

                                      const isPickerExpanded = expandedSourcePicker.has(sample.id);
                                      const manualMap = manualOverrides[sample.id] || {};
                                      const editedMap = editedValues[sample.id] || {};
                                      // Union of elements that have any data from any source (for the per-element drill-down)
                                      const elementSet = new Set<string>();
                                      readings.forEach((r: any) => { if (r.element) elementSet.add(r.element); });
                                      Object.keys(fusionMap).forEach(el => { if (!el.startsWith('__')) elementSet.add(el); });
                                      Object.keys(manualMap).forEach(el => { if (!el.startsWith('__')) elementSet.add(el); });
                                      Object.keys(editedMap).forEach(el => { if (!el.startsWith('__')) elementSet.add(el); });
                                      const pickerElements = Array.from(elementSet).sort((a: string, b: string) => {
                                        const aIsMaj = ICP_TREATMENT_MAJOR_ELEMENTS.has(a) || a === 'S';
                                        const bIsMaj = ICP_TREATMENT_MAJOR_ELEMENTS.has(b) || b === 'S';
                                        if (aIsMaj !== bIsMaj) return aIsMaj ? -1 : 1;
                                        return a.localeCompare(b);
                                      });
                                      const sampleSrc = getEffectiveSampleSource(sample.id);

                                      return (
                                        <React.Fragment key={sample.id}>
                                        <TableRow>
                                          <TableCell sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              <Tooltip title={isPickerExpanded ? 'Masquer le sélecteur par élément' : 'Voir/modifier les valeurs élément par élément'}>
                                                <IconButton size="small" onClick={() => toggleSourcePicker(sample.id)} sx={{ p: 0.25 }}>
                                                  {isPickerExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                                </IconButton>
                                              </Tooltip>
                                              <span>{sample.sample_id}</span>
                                              {readings.length === 0 && <Typography component="span" sx={{ color: '#d32f2f', fontSize: '0.6rem', ml: 0.5 }}>(0 lectures)</Typography>}
                                            </Box>
                                          </TableCell>

                                          {isMixedType ? (
                                            <>
                                              {/* 4-Acides comparison cells */}
                                              <TableCell sx={{ textAlign: 'right', color: '#ed6c02', backgroundColor: sampleSrc === 'acid' ? '#fff8e1' : 'transparent', fontWeight: sampleSrc === 'acid' ? 'bold' : 'normal' }}>{acidSums.sommeMajeurs.toFixed(2)}</TableCell>
                                              <TableCell sx={{ textAlign: 'right', color: '#ed6c02', backgroundColor: sampleSrc === 'acid' ? '#fff8e1' : 'transparent' }}>{acidSums.sommeTraces.toFixed(3)}</TableCell>
                                              <TableCell sx={{ textAlign: 'right', color: '#ed6c02', fontWeight: 'bold', backgroundColor: sampleSrc === 'acid' ? '#fff8e1' : 'transparent', borderRight: '2px solid #ffcc80' }}>{acidSums.sommeTotale.toFixed(2)}</TableCell>

                                              {/* Fusion comparison cells */}
                                              {fusionSums ? (
                                                <>
                                                  <TableCell sx={{ textAlign: 'right', color: '#9c27b0', backgroundColor: sampleSrc === 'fusion' ? '#f3e5f5' : 'transparent', fontWeight: sampleSrc === 'fusion' ? 'bold' : 'normal' }}>{fusionSums.sommeMajeurs.toFixed(2)}</TableCell>
                                                  <TableCell sx={{ textAlign: 'right', color: '#9c27b0', backgroundColor: sampleSrc === 'fusion' ? '#f3e5f5' : 'transparent' }}>{fusionSums.sommeTraces.toFixed(3)}</TableCell>
                                                  <TableCell sx={{ textAlign: 'right', color: '#9c27b0', fontWeight: 'bold', backgroundColor: sampleSrc === 'fusion' ? '#f3e5f5' : 'transparent', borderRight: '2px solid #ce93d8' }}>{fusionSums.sommeTotale.toFixed(2)}</TableCell>
                                                </>
                                              ) : (
                                                <TableCell colSpan={3} sx={{ textAlign: 'center', color: 'text.disabled', fontStyle: 'italic', borderRight: '2px solid #ce93d8' }}>pas encore importé</TableCell>
                                              )}

                                              {/* Source toggle */}
                                              <TableCell sx={{ textAlign: 'center', backgroundColor: '#e1f5fe' }}>
                                                <ToggleButtonGroup
                                                  size="small"
                                                  exclusive
                                                  value={sampleSrc}
                                                  onChange={(_e, v) => { if (v) setSampleSource(sample, v); }}
                                                  sx={{ '& .MuiToggleButton-root': { px: 0.6, py: 0.1, fontSize: '0.62rem', minWidth: 30, lineHeight: 1.2 } }}
                                                >
                                                  <ToggleButton value="acid" sx={{ color: '#ed6c02', '&.Mui-selected': { backgroundColor: '#fff3e0', color: '#ed6c02' } }}>4A</ToggleButton>
                                                  <ToggleButton value="fusion" disabled={!hasFusionForSample} sx={{ color: '#9c27b0', '&.Mui-selected': { backgroundColor: '#f3e5f5', color: '#9c27b0' } }}>F</ToggleButton>
                                                  <ToggleButton value="mixed" disabled={!hasFusionForSample} sx={{ color: '#1976d2', '&.Mui-selected': { backgroundColor: '#e3f2fd', color: '#1976d2' } }}>Mix</ToggleButton>
                                                </ToggleButtonGroup>
                                              </TableCell>
                                            </>
                                          ) : null}

                                          {/* Final sums (envoyés au certificat) */}
                                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', color: '#2e7d32', backgroundColor: '#e8f5e9', borderLeft: isMixedType ? '2px solid #a5d6a7' : 'none' }}>{sums.sommeMajeurs.toFixed(2)}</TableCell>
                                          <TableCell sx={{ textAlign: 'right', color: '#2e7d32', backgroundColor: '#e8f5e9' }}>{sums.sommeTraces.toFixed(3)}</TableCell>
                                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', color: '#2e7d32', backgroundColor: '#e8f5e9' }}>{sums.sommeTotale.toFixed(2)}</TableCell>

                                          {/* PAF — uses staged draft so we only commit on blur/Enter */}
                                          <TableCell sx={{ textAlign: 'center' }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                              <input
                                                type="number"
                                                value={
                                                  pafDrafts[sample.id] !== undefined
                                                    ? pafDrafts[sample.id]
                                                    : (pafManual !== undefined ? String(pafManual) : String(pafFromFormula.toFixed(2)))
                                                }
                                                onChange={(e) => {
                                                  setPafDrafts(prev => ({ ...prev, [sample.id]: e.target.value }));
                                                }}
                                                onBlur={(e) => {
                                                  const t = e.target.value.trim();
                                                  if (t === '') {
                                                    clearPafOverride(sample.id);
                                                    setPafDrafts(prev => { const { [sample.id]: _r, ...rest } = prev; return rest; });
                                                    return;
                                                  }
                                                  const val = parseFloat(t.replace(',', '.'));
                                                  if (!isNaN(val) && val >= 0) {
                                                    handleValueEdit(sample.id, '__PAF__', val, 0);
                                                  }
                                                  setPafDrafts(prev => { const { [sample.id]: _r, ...rest } = prev; return rest; });
                                                }}
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                                  if (e.key === 'Escape') {
                                                    setPafDrafts(prev => { const { [sample.id]: _r, ...rest } = prev; return rest; });
                                                    (e.target as HTMLInputElement).blur();
                                                  }
                                                }}
                                                placeholder="PAF %"
                                                step={0.01}
                                                min={0}
                                                title={
                                                  pafManual === undefined
                                                    ? `Auto: ((CaO ${caO_pct.toFixed(2)}%/56)+(MgO ${mgO_pct.toFixed(2)}%/40))×44`
                                                    : 'Saisie manuelle (vider pour rétablir la formule)'
                                                }
                                                style={{
                                                  width: 64,
                                                  textAlign: 'center',
                                                  padding: '2px',
                                                  fontSize: '0.7rem',
                                                  border: `1px solid ${pafManual !== undefined || pafDrafts[sample.id] !== undefined ? '#ed6c02' : '#4caf50'}`,
                                                  borderRadius: 3,
                                                }}
                                              />
                                            </Box>
                                          </TableCell>

                                          {/* Grand Total */}
                                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', color: totalColor, fontSize: '0.78rem' }}>
                                            {grandTotal.toFixed(2)}
                                          </TableCell>
                                        </TableRow>

                                        {/* 🆕 PER-ELEMENT SOURCE PICKER (collapsible) */}
                                        <TableRow>
                                          <TableCell colSpan={isMixedType ? 13 : 6} sx={{ p: 0, border: 0, backgroundColor: '#fafafa' }}>
                                            <Collapse in={isPickerExpanded} timeout="auto" unmountOnExit>
                                              <Box sx={{ p: 1.5, borderLeft: '3px solid #1976d2', m: 0.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                                    Choisir la source par élément — {sample.sample_id}
                                                  </Typography>
                                                  {hasFusionForSample
                                                    ? <Chip label="4-Acides + Fusion disponibles" size="small" color="info" sx={{ height: 18, fontSize: '0.6rem' }} />
                                                    : <Chip label="4-Acides uniquement" size="small" color="warning" sx={{ height: 18, fontSize: '0.6rem' }} />}
                                                </Box>
                                                <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.4, px: 0.8, fontSize: '0.72rem' } }}>
                                                  <TableHead>
                                                    <TableRow sx={{ backgroundColor: '#eceff1' }}>
                                                      <TableCell sx={{ fontWeight: 'bold' }}>Élément</TableCell>
                                                      <TableCell sx={{ fontWeight: 'bold', color: '#ed6c02', textAlign: 'right' }}>4-Acides (ppm)</TableCell>
                                                      <TableCell sx={{ fontWeight: 'bold', color: '#9c27b0', textAlign: 'right' }}>Fusion (ppm)</TableCell>
                                                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Manuel (ppm)</TableCell>
                                                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Source</TableCell>
                                                      <TableCell sx={{ fontWeight: 'bold', color: '#1976d2', textAlign: 'right' }}>Final retenu</TableCell>
                                                    </TableRow>
                                                  </TableHead>
                                                  <TableBody>
                                                    {pickerElements.length === 0 && (
                                                      <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }}>Aucune lecture disponible</TableCell></TableRow>
                                                    )}
                                                    {pickerElements.map((el: string) => {
                                                      const acidV = getAcidPpm(sample, el);
                                                      const fusionV = fusionMap[el];
                                                      const manualV = manualMap[el];
                                                      const editedV = editedMap[el];
                                                      const eff = getEffectiveSource(sample.id, el);
                                                      const finalVal = eff === 'fusion' ? fusionV : eff === 'manual' ? (manualV ?? editedV) : acidV;
                                                      const isMajorEl = ICP_TREATMENT_MAJOR_ELEMENTS.has(el) || el === 'S';
                                                      const fmt = (n: number | undefined) =>
                                                        n === undefined || n === null ? '—' : (n >= 100 ? n.toFixed(1) : n.toFixed(3));
                                                      return (
                                                        <TableRow key={el} sx={{ backgroundColor: isMajorEl ? '#fff8e1' : 'inherit' }}>
                                                          <TableCell sx={{ fontWeight: 'bold' }}>
                                                            {el}
                                                            {isMajorEl && <Chip label="majeur" size="small" sx={{ ml: 0.5, height: 14, fontSize: '0.55rem' }} />}
                                                          </TableCell>
                                                          <TableCell sx={{ textAlign: 'right', color: acidV !== undefined ? '#ed6c02' : 'text.disabled' }}>{fmt(acidV)}</TableCell>
                                                          <TableCell sx={{ textAlign: 'right', color: fusionV !== undefined ? '#9c27b0' : 'text.disabled' }}>{fmt(fusionV)}</TableCell>
                                                          <TableCell sx={{ textAlign: 'right' }}>
                                                            <input
                                                              type="number"
                                                              value={manualV !== undefined ? manualV : ''}
                                                              onChange={(e) => {
                                                                const t = e.target.value.trim();
                                                                if (t === '') {
                                                                  // Empty → clear the manual override and revert source
                                                                  clearManualValue(sample, el);
                                                                  return;
                                                                }
                                                                const v = parseFloat(t.replace(',', '.'));
                                                                if (!isNaN(v) && v >= 0) setManualValue(sample, el, v);
                                                              }}
                                                              placeholder="—"
                                                              step={0.001}
                                                              min={0}
                                                              title={manualV !== undefined ? 'Vider pour revenir à la source automatique' : 'Saisir une valeur manuelle (ppm)'}
                                                              style={{
                                                                width: 70,
                                                                textAlign: 'right',
                                                                padding: '2px 4px',
                                                                fontSize: '0.7rem',
                                                                border: `1px solid ${manualV !== undefined ? '#1976d2' : '#cfd8dc'}`,
                                                                borderRadius: 3,
                                                                backgroundColor: manualV !== undefined ? '#e3f2fd' : 'transparent',
                                                              }}
                                                            />
                                                          </TableCell>
                                                          <TableCell sx={{ textAlign: 'center' }}>
                                                            <ToggleButtonGroup
                                                              size="small"
                                                              exclusive
                                                              value={eff}
                                                              onChange={(_e, newSrc) => { if (newSrc) setSourceFor(sample, el, newSrc); }}
                                                              sx={{ '& .MuiToggleButton-root': { px: 0.6, py: 0.1, fontSize: '0.6rem', minWidth: 32 } }}
                                                            >
                                                              <ToggleButton value="acid" disabled={acidV === undefined} sx={{ color: '#ed6c02', '&.Mui-selected': { backgroundColor: '#fff3e0', color: '#ed6c02' } }}>
                                                                4A
                                                              </ToggleButton>
                                                              <ToggleButton value="fusion" disabled={fusionV === undefined} sx={{ color: '#9c27b0', '&.Mui-selected': { backgroundColor: '#f3e5f5', color: '#9c27b0' } }}>
                                                                F
                                                              </ToggleButton>
                                                              <ToggleButton value="manual" disabled={manualV === undefined} sx={{ color: '#1976d2', '&.Mui-selected': { backgroundColor: '#e3f2fd', color: '#1976d2' } }}>
                                                                M
                                                              </ToggleButton>
                                                            </ToggleButtonGroup>
                                                          </TableCell>
                                                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', color: '#1976d2' }}>
                                                            {fmt(finalVal)}
                                                          </TableCell>
                                                        </TableRow>
                                                      );
                                                    })}
                                                  </TableBody>
                                                </Table>
                                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontSize: '0.65rem' }}>
                                                  Le choix par élément alimente directement la valeur "Final retenu", qui est utilisée pour le calcul des sommes ci-dessus ET envoyée à la génération de certificat.
                                                </Typography>
                                              </Box>
                                            </Collapse>
                                          </TableCell>
                                        </TableRow>
                                        </React.Fragment>
                                      );
                                    })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            </>
                            )}
                            <Alert severity="info" sx={{ mt: 1, fontSize: '0.75rem' }}>
                              <strong>PRE.FO/ICP/078</strong> : somme oxydes en % + traces (ppm÷10 000→%).{' '}
                              <strong>PAF</strong> : par défaut <code>((CaO/56)+(MgO/40))×44</code> (estimation CO₂ carbonates) ; champ vide = retour au calcul auto ; bordure orange = valeur manuelle.
                              {isMixedType && (
                                <>{' '}<strong>Lot mixte</strong> : le tableau 4-Acides montre les sommes originales (sans SiO₂, ~25–55 %). Le tableau Fusion montre les sommes après remplacement des majeurs (avec SiO₂). Le tableau Final combine les deux + PAF.</>
                              )}
                              {!isMixedType && (
                                <>{' '}{expectNear100Pct
                                  ? 'Analyse quasi-complète (fusion ou Si présent) : le grand total peut être comparé à ~95–105 %.'
                                  : '4-Acides sans Si mesuré : total volontairement inférieur à 100 % (il manque surtout SiO₂).'}</>
                              )}
                            </Alert>
                          </Box>
                        );
                      })()}
                      
                      {/* Batch Summary - Same as ICP Analyst Dashboard */}
                      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={3}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Échantillons</Typography>
                            <Typography variant="h6" color="primary" sx={{ fontSize: '1rem' }}>
                              {(selectedSample as any)?.batchSamples?.length || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Éléments</Typography>
                            <Typography variant="h6" color="primary" sx={{ fontSize: '1rem' }}>
                              {(() => {
                                const sample = (selectedSample as any)?.batchSamples?.[0];
                                // 🔧 FIX: Include AU for BGM in validation modal
                                return getSampleElements(sample, true) || [];
                              })().length || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Mesures Complétées</Typography>
                            <Typography variant="h6" color="success.main" sx={{ fontSize: '1rem' }}>
                              {(() => {
                                let completedCount = 0;
                                let totalRequired = 0;
                                
                                (selectedSample as any)?.batchSamples?.forEach((sample: any) => {
                                  const analysisReadings = sample.metadonnees?.icp_analysis_data?.analysis_readings || 
                                                         sample.metadonnees?.icp_analysis_form?.analysis_readings || 
                                                         sample.metadonnees?.analysis_results || [];
                                  
                                  // 🔧 FIX: Include AU for BGM in validation modal
                                  const elementsToAnalyze = getSampleElements(sample, true);
                                  totalRequired += elementsToAnalyze.length;
                                  
                                  analysisReadings.forEach((reading: any) => {
                                    if (reading.concentration_ppm > 0) {
                                      completedCount++;
                                    }
                                  });
                                });
                                
                                return `${completedCount}/${totalRequired}`;
                              })()}
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Statut QC</Typography>
                            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem', color: '#1976d2' }}>
                              LOD/LOQ Vérifié
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              ) : (
                /* Individual Sample Validation View */
                <Box>
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Informations Échantillon ICP</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Client:</Typography>
                          <Typography variant="body1">{selectedSample.client?.company_name || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Type d'échantillon:</Typography>
                          <Typography variant="body1">{selectedSample.type_echantillon}</Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {/* 🧪 QA/QC DATA COLLECTION */}
              {(selectedSample as any)?.isBatchValidation && 
               isBGMBatch((selectedSample as any)?.batchSamples || []) && 
               validationDecision === 'APPROVE' && (
                <Card sx={{ mt: 2, backgroundColor: alpha('#2196f3', 0.05), border: `2px solid ${theme.palette.info.main}` }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#1976d2' }}>
                        🧪 Données QA/QC (Échantillons BGM)
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={loadDefaultQAQCTemplate}
                        sx={{ fontSize: '0.75rem' }}
                      >
                        📋 Charger Template Par Défaut
                      </Button>
                    </Box>
                    
                    {!showQaqcForm && (
                      <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
                        ℹ️ Les données QA/QC sont <strong>optionnelles</strong> mais recommandées pour les lots BGM.
                        Cliquez sur "Charger Template" pour ajouter les standards de référence.
                      </Alert>
                    )}
                    
                    {showQaqcForm && (
                      <Box>
                        <Alert severity="success" sx={{ mb: 2, fontSize: '0.85rem' }}>
                          ✅ Template chargé. Remplissez les valeurs manquantes (Réf Client, Valeurs).
                        </Alert>
                        
                        {/* BIS SAMPLE SELECTION - SIMPLIFIED TO 2 BIS */}
                        <Box sx={{ mb: 3, p: 2, backgroundColor: alpha('#2196f3', 0.08), border: `2px solid ${theme.palette.info.main}`, borderRadius: 1 }}>
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
                            📊 Sélectionner le 1er BIS (pour la ligne 1) :
                          </Typography>
                          
                          <Box sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
                            <Grid container spacing={1}>
                              {((selectedSample as any)?.batchSamples || []).map((sample: any, idx: number) => (
                                <Grid item xs={12} sm={6} md={4} key={sample.id}>
                                  <Paper sx={{ p: 1, backgroundColor: '#fff' }}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={sample.id === firstBISSampleId}
                                          onChange={(e) => handleFirstBISChange(sample.id, e.target.checked)}
                                          disabled={sample.id === secondBISSampleId}
                                          size="small"
                                          sx={{ 
                                            color: '#1976d2',
                                            '&.Mui-checked': { color: '#1976d2' }
                                          }}
                                        />
                                      }
                                      label={
                                        <Box>
                                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                            {sample.sample_id}
                                          </Typography>
                                          <Typography sx={{ fontSize: '0.65rem', color: '#666' }}>
                                            Position {idx + 1}/{((selectedSample as any)?.batchSamples || []).length}
                                          </Typography>
                                        </Box>
                                      }
                                    />
                                    {sample.id === firstBISSampleId && (
                                      <Chip label="1er BIS" size="small" color="primary" sx={{ ml: 1, fontSize: '0.6rem' }} />
                                    )}
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                          
                          <Divider sx={{ my: 2 }} />
                          
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#ff9800' }}>
                            📊 Sélectionner le 2ème BIS (pour la ligne 2) :
                          </Typography>
                          
                          <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                            <Grid container spacing={1}>
                              {((selectedSample as any)?.batchSamples || []).map((sample: any, idx: number) => (
                                <Grid item xs={12} sm={6} md={4} key={`second-${sample.id}`}>
                                  <Paper sx={{ p: 1, backgroundColor: '#fff' }}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={sample.id === secondBISSampleId}
                                          onChange={(e) => handleSecondBISChange(sample.id, e.target.checked)}
                                          disabled={sample.id === firstBISSampleId}
                                          size="small"
                                          sx={{ 
                                            color: '#ff9800',
                                            '&.Mui-checked': { color: '#ff9800' }
                                          }}
                                        />
                                      }
                                      label={
                                        <Box>
                                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                            {sample.sample_id}
                                          </Typography>
                                          <Typography sx={{ fontSize: '0.65rem', color: '#666' }}>
                                            Position {idx + 1}/{((selectedSample as any)?.batchSamples || []).length}
                                          </Typography>
                                        </Box>
                                      }
                                    />
                                    {sample.id === secondBISSampleId && (
                                      <Chip label="2ème BIS" size="small" color="warning" sx={{ ml: 1, fontSize: '0.6rem' }} />
                                    )}
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                          
                          <Alert severity="info" sx={{ mt: 2, fontSize: '0.7rem' }}>
                            💡 <strong>Astuce :</strong> Le 1er BIS utilise sa propre référence client. Le 2ème BIS utilise automatiquement <strong>BIS(1er BIS référence)</strong>.
                          </Alert>
                        </Box>
                        
                        {/* EDITABLE QA/QC TABLE - 3-ROW HEADER matching your image EXACTLY */}
                        <TableContainer component={Paper} sx={{ maxHeight: 500, overflow: 'auto' }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              {/* HEADER ROW 1: METHOD */}
                              <TableRow>
                                <TableCell rowSpan={3} sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555', minWidth: '150px' }}>
                                  METHOD
                                </TableCell>
                                <TableCell rowSpan={3} sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555', minWidth: '120px' }}>
                                  {/* Empty column between METHOD and data */}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                  MO/ANA/002
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                  MO/ANA/002
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                  MO/ANA/002
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                  MO/ANA/002
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                  MO/ANA/002
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                  MO/ANA/035
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                  MO/ANA/001
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                  MO/ANA/038
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                  MO/ANA/038
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                  MO/ANA/038
                                </TableCell>
                                <TableCell rowSpan={3} sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555', minWidth: '80px' }}>
                                  Actions
                                </TableCell>
                              </TableRow>
                              
                              {/* HEADER ROW 2: LIMITE DETECTION */}
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555' }}>1</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555' }}>0,01</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555' }}>0,01</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555' }}>0,01</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555' }}>0,01</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555' }}>200</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555' }}>0,05</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555' }}>1</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555' }}>1</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555' }}>1</TableCell>
                              </TableRow>
                              
                              {/* HEADER ROW 3: Customers-Ref, Lab-Ref, Element names + units */}
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555' }}>
                                  Ag<br/><span style={{ fontSize: '0.6rem' }}>(g/t)</span>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555' }}>
                                  Cu<br/><span style={{ fontSize: '0.6rem' }}>(%)</span>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555' }}>
                                  Fe<br/><span style={{ fontSize: '0.6rem' }}>(%)</span>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555' }}>
                                  Pb<br/><span style={{ fontSize: '0.6rem' }}>(%)</span>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555' }}>
                                  Zn<br/><span style={{ fontSize: '0.6rem' }}>(%)</span>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555' }}>
                                  Ag_FA<br/><span style={{ fontSize: '0.6rem' }}>(g/t)</span>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555' }}>
                                  Au_FA<br/><span style={{ fontSize: '0.6rem' }}>(g/t)</span>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555' }}>
                                  Mo_ICP<br/><span style={{ fontSize: '0.6rem' }}>(ppm)</span>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555' }}>
                                  As_ICP<br/><span style={{ fontSize: '0.6rem' }}>(ppm)</span>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555' }}>
                                  Sn_ICP<br/><span style={{ fontSize: '0.6rem' }}>(ppm)</span>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {qaqcStandards.map((standard, idx) => (
                                <TableRow key={idx} sx={{ 
                                  '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                                  border: '1px solid #ddd'
                                }}>
                                  {/* Customers-Ref (editable) */}
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      value={standard.customers_ref}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].customers_ref = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder="Ex: BMX25120250"
                                      sx={{ width: '130px' }}
                                      inputProps={{ style: { fontSize: '0.7rem' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Lab-Ref (editable) */}
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      value={standard.lab_ref}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].lab_ref = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder="Ex: R-9348-25-25"
                                      sx={{ width: '120px' }}
                                      inputProps={{ style: { fontSize: '0.7rem' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Ag - ALWAYS EDITABLE */}
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.Ag}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].Ag = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder=""
                                      sx={{ width: '70px' }}
                                      inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Cu - ALWAYS EDITABLE */}
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.Cu}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].Cu = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder=""
                                      sx={{ width: '70px' }}
                                      inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Fe - ALWAYS EDITABLE */}
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.Fe}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].Fe = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder=""
                                      sx={{ width: '70px' }}
                                      inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Pb - ALWAYS EDITABLE */}
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.Pb}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].Pb = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder=""
                                      sx={{ width: '70px' }}
                                      inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Zn - ALWAYS EDITABLE */}
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.Zn}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].Zn = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder=""
                                      sx={{ width: '70px' }}
                                      inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Ag_FA - ALWAYS EDITABLE */}
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.Ag_FA}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].Ag_FA = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder=""
                                      sx={{ width: '70px' }}
                                      inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Au_FA - ALWAYS EDITABLE */}
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.Au_FA}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].Au_FA = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder=""
                                      sx={{ width: '70px' }}
                                      inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Mo_ICP - ALWAYS EDITABLE */}
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.Mo_ICP}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].Mo_ICP = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder=""
                                      sx={{ width: '70px' }}
                                      inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* As_ICP - ALWAYS EDITABLE */}
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.As_ICP}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].As_ICP = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder=""
                                      sx={{ width: '70px' }}
                                      inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Sn_ICP - ALWAYS EDITABLE */}
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.Sn_ICP}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].Sn_ICP = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder=""
                                      sx={{ width: '70px' }}
                                      inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Actions */}
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => {
                                        setQaqcStandards(qaqcStandards.filter((_, i) => i !== idx));
                                      }}
                                      title="Supprimer cette ligne"
                                    >
                                      <Cancel fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        
                        <Box mt={1} display="flex" justifyContent="space-between" alignItems="center">
                          <Box display="flex" gap={2} alignItems="center">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                const newRow: QAQCStandard = {
                                  customers_ref: 'Standard additionnel',
                                  lab_ref: '',
                                  Ag: '', Cu: '', Fe: '', Pb: '', Zn: '',
                                  Ag_FA: '', Au_FA: '', Mo_ICP: '', As_ICP: '', Sn_ICP: '',
                                };
                                setQaqcStandards([...qaqcStandards, newRow]);
                              }}
                              sx={{ fontSize: '0.7rem' }}
                            >
                              + Ajouter une ligne
                            </Button>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              💡 Ces données seront incluses dans le certificat final
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            startIcon={<Cancel />}
                            onClick={() => {
                              setShowQaqcForm(false);
                              setQaqcStandards([]);
                            }}
                            sx={{ fontSize: '0.7rem' }}
                          >
                            Annuler QA/QC
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* 🧪 DYNAMIC QA/QC DATA COLLECTION - ZMSM LOTS (unchanged legacy form) */}
              {(selectedSample as any)?.isBatchValidation && 
               !isBGMBatch((selectedSample as any)?.batchSamples || []) && 
               isZMSMBatch((selectedSample as any)?.batchSamples || []) && 
               validationDecision === 'APPROVE' && (
                <Card sx={{ mt: 2, backgroundColor: alpha('#2196f3', 0.05), border: `2px solid ${theme.palette.info.main}` }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#1976d2' }}>
                        🧪 Données QA/QC
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={loadDynamicQAQCTemplate}
                        sx={{ fontSize: '0.75rem' }}
                      >
                        📋 Charger Template QA/QC
                      </Button>
                    </Box>

                    {!showDynamicQaqcForm && (
                      <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
                        ℹ️ Les données QA/QC sont <strong>optionnelles</strong>.
                        Cliquez sur "Charger Template QA/QC" pour ajouter les standards de référence.
                      </Alert>
                    )}

                    {showDynamicQaqcForm && dynamicQaqcElements.length > 0 && (
                      <Box>
                        <Alert severity="success" sx={{ mb: 2, fontSize: '0.85rem' }}>
                          ✅ Template chargé ({dynamicQaqcElements.length} éléments). Remplissez les valeurs.
                        </Alert>

                        <TableContainer component={Paper} sx={{ maxHeight: 500, overflow: 'auto' }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell rowSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555', minWidth: '150px' }}>
                                  Customers-Ref
                                </TableCell>
                                <TableCell rowSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555', minWidth: '120px' }}>
                                  Lab-Ref
                                </TableCell>
                                {dynamicQaqcElements.map(el => (
                                  <TableCell key={el} sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                    {ELEMENT_DETECTION_LIMITS[el] || '-'}
                                  </TableCell>
                                ))}
                                <TableCell rowSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555', minWidth: '80px' }}>
                                  Actions
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                {dynamicQaqcElements.map(el => (
                                  <TableCell key={el} sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                    {el}<br/><span style={{ fontSize: '0.6rem' }}>({ELEMENT_UNITS[el] || 'ppm'})</span>
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {dynamicQaqcStandards.map((standard, idx) => (
                                <TableRow key={idx} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' }, border: '1px solid #ddd' }}>
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      value={standard.customers_ref}
                                      onChange={(e) => {
                                        const updated = [...dynamicQaqcStandards];
                                        updated[idx] = { ...updated[idx], customers_ref: e.target.value };
                                        setDynamicQaqcStandards(updated);
                                      }}
                                      placeholder="Réf. Client"
                                      sx={{ width: '130px' }}
                                      inputProps={{ style: { fontSize: '0.7rem' } }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      value={standard.lab_ref}
                                      onChange={(e) => {
                                        const updated = [...dynamicQaqcStandards];
                                        updated[idx] = { ...updated[idx], lab_ref: e.target.value };
                                        setDynamicQaqcStandards(updated);
                                      }}
                                      placeholder="Réf. Labo"
                                      sx={{ width: '120px' }}
                                      inputProps={{ style: { fontSize: '0.7rem' } }}
                                    />
                                  </TableCell>
                                  {dynamicQaqcElements.map(el => (
                                    <TableCell key={el} sx={{ border: '1px solid #ddd' }}>
                                      <TextField
                                        size="small"
                                        value={standard[el] || ''}
                                        onChange={(e) => {
                                          const updated = [...dynamicQaqcStandards];
                                          updated[idx] = { ...updated[idx], [el]: e.target.value };
                                          setDynamicQaqcStandards(updated);
                                        }}
                                        placeholder=""
                                        sx={{ width: '70px' }}
                                        inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                      />
                                    </TableCell>
                                  ))}
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => setDynamicQaqcStandards(dynamicQaqcStandards.filter((_, i) => i !== idx))}
                                      title="Supprimer cette ligne"
                                    >
                                      <Cancel fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        <Box mt={1} display="flex" justifyContent="space-between" alignItems="center">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              const newRow: Record<string, string> = { customers_ref: '', lab_ref: '' };
                              dynamicQaqcElements.forEach(el => { newRow[el] = ''; });
                              setDynamicQaqcStandards([...dynamicQaqcStandards, newRow]);
                            }}
                            sx={{ fontSize: '0.7rem' }}
                          >
                            + Ajouter une ligne
                          </Button>
                          <Button
                            size="small"
                            startIcon={<Cancel />}
                            onClick={() => {
                              setShowDynamicQaqcForm(false);
                              setDynamicQaqcStandards([]);
                              setDynamicQaqcElements([]);
                            }}
                            sx={{ fontSize: '0.7rem' }}
                          >
                            Annuler QA/QC
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* 🧪 ROLE-BASED QA/QC DATA COLLECTION - NON-BGM / NON-ZMSM LOTS */}
              {(selectedSample as any)?.isBatchValidation &&
               !isBGMBatch((selectedSample as any)?.batchSamples || []) &&
               !isZMSMBatch((selectedSample as any)?.batchSamples || []) &&
               validationDecision === 'APPROVE' && (
                <Card sx={{ mt: 2, backgroundColor: alpha('#2196f3', 0.05), border: `2px solid ${theme.palette.info.main}` }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#1976d2' }}>
                        🧪 Données QA/QC (Contrôle qualité)
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={loadRoleBasedQAQCTemplate}
                        sx={{ fontSize: '0.75rem' }}
                      >
                        📋 Charger Template QA/QC
                      </Button>
                    </Box>

                    {!(showDynamicQaqcForm && isRoleBasedQaqc) && (
                      <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
                        ℹ️ Sélectionnez le 1er échantillon, son BIS, un BLANK MP et un BLANK CP.
                        Les valeurs se remplissent automatiquement depuis le tableau de résultats
                        (en tenant compte de vos modifications). La ligne <strong>OREAS 30a</strong> est
                        pré-remplie et reste modifiable. Cliquez sur "Charger Template QA/QC".
                      </Alert>
                    )}

                    {showDynamicQaqcForm && isRoleBasedQaqc && dynamicQaqcElements.length > 0 && (
                      <Box>
                        <Alert severity="success" sx={{ mb: 2, fontSize: '0.85rem' }}>
                          ✅ Template chargé ({dynamicQaqcElements.length} éléments). Cochez les échantillons ci-dessous.
                        </Alert>

                        {/* ROLE PICKERS */}
                        {([
                          { key: 'first', label: '1er échantillon', sel: qcRoleFirstId, on: handleRoleFirstChange, color: '#1976d2' },
                          { key: 'bis', label: 'BIS (duplicata du 1er échantillon)', sel: qcRoleBisId, on: handleRoleBisChange, color: '#ff9800' },
                          { key: 'blankMP', label: 'BLANK MP', sel: qcRoleBlankMPId, on: handleRoleBlankMPChange, color: '#4caf50' },
                          { key: 'blankCP', label: 'BLANK CP', sel: qcRoleBlankCPId, on: handleRoleBlankCPChange, color: '#9c27b0' },
                        ] as const).map(role => (
                          <Box key={role.key} sx={{ mb: 1.5, p: 1.5, backgroundColor: alpha(role.color, 0.06), border: `1px solid ${alpha(role.color, 0.4)}`, borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: role.color }}>
                              📊 Sélectionner : {role.label}
                            </Typography>
                            <Box sx={{ maxHeight: 160, overflow: 'auto' }}>
                              <Grid container spacing={1}>
                                {((selectedSample as any)?.batchSamples || []).map((sample: any, idx: number) => (
                                  <Grid item xs={12} sm={6} md={4} key={`${role.key}-${sample.id}`}>
                                    <Paper sx={{ p: 0.5, backgroundColor: '#fff' }}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={sample.id === role.sel}
                                            onChange={(e) => role.on(sample.id, e.target.checked)}
                                            size="small"
                                            sx={{ color: role.color, '&.Mui-checked': { color: role.color } }}
                                          />
                                        }
                                        label={
                                          <Box>
                                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 'bold' }}>
                                              {sample.sample_id}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.62rem', color: '#666' }}>
                                              {sample.external_id || `Position ${idx + 1}`}
                                            </Typography>
                                          </Box>
                                        }
                                      />
                                    </Paper>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          </Box>
                        ))}

                        {/* QA/QC TABLE */}
                        <TableContainer component={Paper} sx={{ maxHeight: 520, overflow: 'auto', mt: 1 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell rowSpan={3} sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555', minWidth: '52px' }}></TableCell>
                                <TableCell rowSpan={3} sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555', minWidth: '140px' }}>Customers-Ref</TableCell>
                                <TableCell rowSpan={3} sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555', minWidth: '110px' }}>Lab-Ref</TableCell>
                                <TableCell rowSpan={3} sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>Lab_Weight<br/>Kg</TableCell>
                                {dynamicQaqcElements.map(el => (
                                  <TableCell key={`m-${el}`} sx={{ fontWeight: 'bold', backgroundColor: '#cfe2f3', fontSize: '0.58rem', textAlign: 'center', border: '1px solid #555', minWidth: '64px' }}>
                                    {roleQaqcElementMeta[el]?.method || 'PRE.MO/ANA/038'}
                                  </TableCell>
                                ))}
                              </TableRow>
                              <TableRow>
                                {dynamicQaqcElements.map(el => (
                                  <TableCell key={`l-${el}`} sx={{ backgroundColor: '#e8eef7', fontSize: '0.6rem', textAlign: 'center', border: '1px solid #555' }}>
                                    {roleQaqcElementMeta[el]?.limit ?? '-'}
                                  </TableCell>
                                ))}
                              </TableRow>
                              <TableRow>
                                {dynamicQaqcElements.map(el => (
                                  <TableCell key={`h-${el}`} sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.66rem', textAlign: 'center', border: '1px solid #555', minWidth: '64px' }}>
                                    {el}<br/><span style={{ fontSize: '0.58rem' }}>({roleQaqcElementMeta[el]?.unit || 'ppm'})</span>
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {dynamicQaqcStandards.map((standard, idx) => (
                                <TableRow key={idx} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' }, border: '1px solid #ddd' }}>
                                  <TableCell sx={{ border: '1px solid #ddd', textAlign: 'center', width: '52px' }}>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => removeRoleBasedQaqcRow(idx)}
                                      title="Supprimer cette ligne"
                                    >
                                      <Cancel fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.customers_ref || ''}
                                      onChange={(e) => {
                                        const updated = [...dynamicQaqcStandards];
                                        updated[idx] = { ...updated[idx], customers_ref: e.target.value };
                                        setDynamicQaqcStandards(updated);
                                      }}
                                      sx={{ width: '130px' }}
                                      inputProps={{ style: { fontSize: '0.7rem' } }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.lab_ref || ''}
                                      onChange={(e) => {
                                        const updated = [...dynamicQaqcStandards];
                                        updated[idx] = { ...updated[idx], lab_ref: e.target.value };
                                        setDynamicQaqcStandards(updated);
                                      }}
                                      sx={{ width: '100px' }}
                                      inputProps={{ style: { fontSize: '0.7rem' } }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.lab_weight || ''}
                                      onChange={(e) => {
                                        const updated = [...dynamicQaqcStandards];
                                        updated[idx] = { ...updated[idx], lab_weight: e.target.value };
                                        setDynamicQaqcStandards(updated);
                                      }}
                                      sx={{ width: '64px' }}
                                      inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                    />
                                  </TableCell>
                                  {dynamicQaqcElements.map(el => (
                                    <TableCell key={el} sx={{ border: '1px solid #ddd' }}>
                                      <TextField
                                        size="small"
                                        value={standard[el] || ''}
                                        onChange={(e) => {
                                          const updated = [...dynamicQaqcStandards];
                                          updated[idx] = { ...updated[idx], [el]: e.target.value };
                                          setDynamicQaqcStandards(updated);
                                        }}
                                        sx={{ width: '64px' }}
                                        inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                      />
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        <Box mt={1} display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            💡 Ce tableau (en-têtes méthode / limite / Lab_Weight inclus) sera intégré au certificat final.
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<Cancel />}
                            onClick={() => {
                              setShowDynamicQaqcForm(false);
                              setIsRoleBasedQaqc(false);
                              setDynamicQaqcStandards([]);
                              setDynamicQaqcElements([]);
                              setRoleQaqcElementMeta({});
                              setQcRoleFirstId(null);
                              setQcRoleBisId(null);
                              setQcRoleBlankMPId(null);
                              setQcRoleBlankCPId(null);
                            }}
                            sx={{ fontSize: '0.7rem' }}
                          >
                            Annuler QA/QC
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Validation Decision Section */}
              <Card sx={{ mt: 2, backgroundColor: '#f0f8ff' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 'bold', color: '#1976d2' }}>
                    📝 Décision de Validation {(selectedSample as any)?.isBatchValidation ? 'du Lot' : ''}
                  </Typography>
                  
                  {/* Validation Decision Radio Buttons */}
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <RadioGroup
                      value={validationDecision}
                      onChange={(e) => setValidationDecision(e.target.value as 'APPROVE' | 'REJECT' | 'REQUEST_CORRECTIONS')}
                      row
                    >
                      <MuiFormControlLabel
                        value="APPROVE"
                        control={<Radio color="success" />} 
                        label={
                          <Box display="flex" alignItems="center" gap={1}>
                            <CheckCircle color="success" sx={{ fontSize: '1rem' }} />
                            <Typography variant="body2">
                              Approuver {(selectedSample as any)?.isBatchValidation ? 'le Lot' : "l'Échantillon"}
                            </Typography>
                          </Box>
                        }
                      />
                      <MuiFormControlLabel
                        value="REJECT"
                        control={<Radio color="error" />}
                        label={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Cancel color="error" sx={{ fontSize: '1rem' }} />
                            <Typography variant="body2">
                              Rejeter {(selectedSample as any)?.isBatchValidation ? 'le Lot' : "l'Échantillon"}
                            </Typography>
                          </Box>
                        }
                      />
                      <MuiFormControlLabel
                        value="REQUEST_CORRECTIONS"
                        control={<Radio color="warning" />}
                        label={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Warning color="warning" sx={{ fontSize: '1rem' }} />
                            <Typography variant="body2">Demander Corrections</Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                  

                  {!isBGMBatch((selectedSample as any)?.batchSamples || []) &&
                   !isZMSMBatch((selectedSample as any)?.batchSamples || []) &&
                   validationDecision === 'APPROVE' && (
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Notes certificat (optionnel — affichées en fin de rapport)"
                      value={certificateNotes}
                      onChange={(e) => setCertificateNotes(e.target.value)}
                      placeholder="Remarques du superviseur visibles sur le certificat final. Laisser vide = rien n'apparaît."
                      sx={{ mt: 2 }}
                    />
                  )}

                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Notes de validation (OBLIGATOIRES)"
                    value={validationNotes}
                    onChange={(e) => setValidationNotes(e.target.value)}
                    placeholder="Commentaires obligatoires sur la qualité des analyses ICP, conformité des résultats, actions correctives si nécessaire..."
                    required
                    error={!validationNotes.trim()}
                    helperText={!validationNotes.trim() ? "Les notes de validation sont obligatoires pour valider un échantillon" : "Notes sauvegardées automatiquement"}
                    sx={{ 
                      mt: 2,
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: !validationNotes.trim() ? '#d32f2f' : '#1976d2'
                        }
                      }
                    }}
                  />
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 2, 
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e0e0e0',
          position: 'sticky',
          bottom: 0,
          zIndex: 1000
        }}>
          <Button 
            onClick={() => setValidationDialog(false)} 
            startIcon={<Cancel />}
            sx={{ fontSize: '0.8rem' }}
          >
            Annuler
          </Button>
          
          {/* Certificat Partiel ICP — visible when ICP analysis data exists or batch validation */}
          {(selectedSample?.metadonnees?.icp_analysis_form ||
            selectedSample?.metadonnees?.icp_batch_analysis_summary ||
            (selectedSample as any)?.isBatchValidation) && (
            <Button
              variant="outlined"
              color="info"
              startIcon={generatingPartialCert ? <CircularProgress size={16} /> : <Assessment />}
              onClick={handleGeneratePartialCertificate}
              disabled={generatingPartialCert}
              sx={{ fontSize: '0.8rem' }}
            >
              {generatingPartialCert ? 'Generation en cours...' : 'Certificat Partiel ICP'}
            </Button>
          )}

          {/* Enable validation button for both individual samples and batch samples */}
          {(selectedSample?.metadonnees?.icp_analysis_form || 
            selectedSample?.metadonnees?.icp_batch_analysis_summary ||
            (selectedSample as any)?.isBatchValidation) && (
            <Button 
              variant="contained" 
              onClick={handleCompleteValidation}
              startIcon={<Save />}
              disabled={!validationNotes.trim()}
              color={validationDecision === 'APPROVE' ? 'success' : 'error'}
              sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}
              title={!validationNotes.trim() ? "Veuillez saisir des notes de validation pour activer ce bouton" : ""}
            >
              {(selectedSample as any)?.isBatchValidation 
                ? (validationDecision === 'APPROVE' ? 'Approuver le Lot' : 'Rejeter le Lot')
                : (validationDecision === 'APPROVE' ? 'Approuver l\'Échantillon' : 
                   (validationDecision === 'REJECT' ? 'Rejeter l\'Échantillon' : 'Demander Corrections'))
              }
              {!validationNotes.trim() && (
                <Typography variant="caption" sx={{ ml: 1, opacity: 0.7, fontSize: '0.7rem' }}>
                  (Notes requises)
                </Typography>
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default ICPSupervisorDashboard; 